"use strict";

const PERSONALIZATION_EMOJI_REGEX = /[\p{Extended_Pictographic}\p{Regional_Indicator}\uFE0F\u200D]/u;
const PERSONALIZATION_DEDICATION_SIGNATURE_REGEX = /XXX\s*$/;
const PERSONALIZATION_ALL_UPPERCASE_REGEX = /^\P{Ll}+$/u;

const PERSONALIZATION_PHOTO_MAX_BYTES = 10 * 1024 * 1024;
const PERSONALIZATION_PHOTO_ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
// What the photo picker asks the browser for. why image/* and not the shop's
// types: the picker's filter is only a convenience - a list of MIME types can
// leave some Android in-app browsers without a chooser (#3972, #3920) - while
// validatePhotoFile holds every pick to the shop's types after it.
const PERSONALIZATION_PHOTO_PICKER_ACCEPT = "image/*";
// The stuck picker (watchPhotoPickerTap): a picker that opens takes the
// page's focus, so the STUCK_TAPS-th tap without the page losing it in
// between, or OPEN_GRACE_MS after a tap without it, found the picker shut.
// why the grace: #3920 left after a single tap. A picker that gave the page
// its focus back came back empty when RETURN_GRACE_MS pass without its photo
// or the browser's cancel (handlePhotoPickerReturn). why that grace: on
// Android the page can take its focus back before the input reports either.
const PERSONALIZATION_PHOTO_PICKER_OPEN_GRACE_MS = 1500;
const PERSONALIZATION_PHOTO_PICKER_RETURN_GRACE_MS = 1000;
const PERSONALIZATION_PHOTO_PICKER_STUCK_TAPS = 2;
// Photo preparation (preparePhotoForUpload): bookpipe scales every photo to
// 2048 px on its long edge anyway and never prints it, so the wizard sends at
// most that, as a JPEG of quality 0.9. An accepted photo of at most
// UNTOUCHED_MAX_BYTES goes as it is - re-encoding it would win little - and so
// does one not prepared within TIMEOUT_MS. why PREPARE_MAX_BYTES: a decoded
// photo takes 4 bytes per pixel, one above it hundreds of MB - more than iOS
// and in-app WebViews allow a tab, which they kill instead of throwing. Such a
// photo is never decoded.
const PERSONALIZATION_PHOTO_PREPARE_MAX_EDGE = 2048;
const PERSONALIZATION_PHOTO_PREPARE_QUALITY = 0.9;
const PERSONALIZATION_PHOTO_UNTOUCHED_MAX_BYTES = 1.5 * 1024 * 1024;
const PERSONALIZATION_PHOTO_PREPARE_MAX_BYTES = 30 * 1024 * 1024;
const PERSONALIZATION_PHOTO_PREPARE_TIMEOUT_MS = 10000;
// The filename is only a "that's the one I picked" cue, so long stock-photo
// names get a middle ellipsis (extension kept) instead of blowing the dropzone
// layout apart. CSS still ellipsizes as a backstop on narrow viewports.
const PERSONALIZATION_PHOTO_FILENAME_MAX_CHARS = 32;
// A typical preview run finishes in ~30s, so poll fast while a result is
// likely imminent and back off afterwards; the total budget matches the old
// 60-attempts x 4s cap. The fast cadence must stay well under the app-proxy
// status limiter (60/min, potentially IP-shared behind the Shopify proxy).
const PERSONALIZATION_PREVIEW_POLL_INTERVAL_FAST_MS = 3000;
const PERSONALIZATION_PREVIEW_POLL_INTERVAL_MS = 4000;
const PERSONALIZATION_PREVIEW_POLL_FAST_WINDOW_MS = 30000;
const PERSONALIZATION_PREVIEW_POLL_MAX_TOTAL_MS = 240000;
// Per-poll-request bound: without it a single hung GET would suspend the
// loop forever and the total cap above could never fire.
const PERSONALIZATION_PREVIEW_POLL_REQUEST_TIMEOUT_MS = 15000;
const PERSONALIZATION_PREVIEW_POLL_MAX_CONSECUTIVE_FAILURES = 3;
const PERSONALIZATION_PREVIEW_REQUEST_TIMEOUT_MS = 180000;
// How long to wait out ANOTHER preview run already in flight for this
// session (the trigger POST answered 429 code=run_in_flight — e.g. the
// customer edited a field and re-entered step 3 while the previous preview
// was still rendering). Must exceed the server's 180s overdue settle: every
// status poll during the wait also triggers that settle, so an orphaned run
// is guaranteed a terminal verdict inside this window.
const PERSONALIZATION_PREVIEW_INFLIGHT_WAIT_MAX_MS = 200000;
// Bot-challenge round-trips (issue, verify, config refresh). Short on purpose:
// they are pure-crypto calls that run BETWEEN the preview watchdog's attempts,
// so nothing else bounds them.
const PERSONALIZATION_CHALLENGE_REQUEST_TIMEOUT_MS = 20000;
// The session create and the variables PATCH: without a bound, a stalled
// backend held them open for good - and the photo, the preview and the add to
// cart waiting behind them. Each attempt gets its own (a replay a fresh one).
const PERSONALIZATION_SESSION_REQUEST_TIMEOUT_MS = 20000;
// Audiobook name sample: one bounded round-trip per request, so a stalled
// backend leaves the button disabled instead of hanging on an open fetch.
// The POST is retried once after RETRY_MS when the server reports "no_name"
// (the session sync may still be landing); a queued render is then polled
// every POLL_INTERVAL_MS until POLL_MAX_MS — 20 polls per minute, well inside
// the endpoint's own 60/min budget.
const PERSONALIZATION_NAME_SAMPLE_REQUEST_TIMEOUT_MS = 15000;
const PERSONALIZATION_NAME_SAMPLE_RETRY_MS = 2000;
const PERSONALIZATION_NAME_SAMPLE_POLL_INTERVAL_MS = 3000;
const PERSONALIZATION_NAME_SAMPLE_POLL_MAX_MS = 90000;
// Photo upload (sendPhotoUploadOnce): an uplink through which no byte of the
// photo went out for STALL_MS has stalled, and the request is called off and
// sent once more (sendPhotoUpload). Once the whole photo is out, bookpipe's
// check (moderation + vision, ≈ 2 s) gets VERDICT_MS to answer. why so long:
// it is only the backstop - the app proxy answers a hung backend sooner with
// an error of its own, and a photo sent again meets bookpipe's cached verdict
// instead of a second check.
const PERSONALIZATION_PHOTO_UPLOAD_STALL_MS = 30000;
const PERSONALIZATION_PHOTO_UPLOAD_VERDICT_MS = 60000;
// A stall timer that fires more than FROZEN_MS behind its time had the page
// frozen meanwhile (a background tab's timers are only clamped to ≥ 1 s).
const PERSONALIZATION_PHOTO_UPLOAD_FROZEN_MS = 10000;
// Loading UX: the bar follows an eased curve through these (elapsed ms →
// percent) keyframes — deliberately not a countdown, since run times vary and
// an expired timer reads as "broken". It parks near 95% and only the real
// completion signal finishes it. Rotating messages advance on a fixed cadence
// and a reassurance message takes over once the wait is clearly above average.
const PERSONALIZATION_PREVIEW_PROGRESS_KEYFRAMES = [
    [0, 0],
    [1500, 9],
    [6000, 28],
    [15000, 52],
    [30000, 74],
    [45000, 84],
    [60000, 90],
    [120000, 95],
];
const PERSONALIZATION_PREVIEW_PROGRESS_TICK_MS = 250;
const PERSONALIZATION_PREVIEW_MESSAGE_INTERVAL_MS = 5000;
const PERSONALIZATION_PREVIEW_LONG_WAIT_MS = 45000;
const PERSONALIZATION_CONFIG_CACHE_TTL_MS = 10 * 60 * 1000;

// Reserved bookpipe option keys the step-1 renderer never draws an input for:
// the certificate hash is generated in the background, the dedication has its
// own bespoke step-2 UI (fee product, yes/no choice).
const PERSONALIZATION_HASH_KEY = "hash";
const PERSONALIZATION_DEDICATION_KEY = "dedication";
// why: the main character's name is painted onto the cover in a fixed box with no
// auto-shrink, so the wizard capped it at 12 characters long before the field config
// existed. The config's own max (bookpipe option validation_rules.max) wins when set;
// this default only closes the gap when a product forgets to configure one.
const PERSONALIZATION_MAIN_CHARACTER_DEFAULT_MAX = 12;

// The wizard's steps, identified by the data-step-key the Liquid stamps on every
// tab and form. The step NUMBER depends on the product (a cover step only exists
// when both cover variants are configured, a Hörbuch step only for a book that
// also sells its Hörbuch), so nothing in here compares numbers.
const PERSONALIZATION_STEP_KEYS = {
    CHARACTER: "character",
    COVER: "cover",
    DEDICATION: "dedication",
    AUDIOBOOK: "audiobook",
    PREVIEW: "preview",
};

// Cart lines and drafts created before the config-driven renderer used
// theme-specific input names; map them so edit-from-cart keeps working.
const PERSONALIZATION_LEGACY_ENTRY_ALIASES = {
    main_character: "__main-character-name",
};

// The child's name goes onto the wizard's lines as the one property the checkout lists, under the
// storefront's word for "for" (plan i18n-8-maerkte): the layout hands it to the scripts in window._t
// (sections.personalization.line_item_for). The German word is the name every line carried before -
// and the one a page without the strings keeps.
const PERSONALIZATION_LEGACY_RECIPIENT_PROPERTY = "für";

// ------------------------------------------------------------------
// GTM / GA4 funnel tracking
// ------------------------------------------------------------------
// theme.liquid loads Google Tag Manager (GTM-PDW2VWHW) with Consent Mode v2
// defaults on "denied" until Cookiebot updates them. The wizard therefore only
// PUSHES named events onto window.dataLayer — whether (and with which storage)
// they reach GA4 is entirely the container's decision. No cookies, no reads.
//
// Every push carries the same parameter object, namespaced under
// `personalization` and pre-filled with nulls for the keys an event does not
// use: GTM merges pushes into one data-layer state, so a partially filled
// object would leave the PREVIOUS event's values readable by later tags.
//
// The funnel (GA4 → Explore → Funnel exploration; one step per event name,
// each event needs a "Custom Event" trigger + GA4 event tag in GTM):
//   personalization_open                wizard opened (also edit-from-cart)
//   personalization_step_view           a step became visible (step_name says which)
//   personalization_photo_picker_hint   the photo picker stayed shut: its way out shows (reason)
//   personalization_photo_picker_open_external its "Im Browser öffnen" pressed
//   personalization_photo_picker_link_copied its "Link kopieren" pressed (reason: how it copied)
//   personalization_photo_selected      a photo passed the client-side checks
//   personalization_photo_verified      the server-side photo check accepted it
//   personalization_photo_rejected      the photo was refused (see reason)
//   personalization_cover_selected      Soft- or Hardcover picked in the wizard (cover_type)
//   personalization_step_completed      step left forwards, validation passed
//   personalization_validation_error    forward navigation was blocked
//   personalization_preview_requested   a preview run was triggered
//   personalization_preview_ready       preview finished (duration_ms, pages)
//   personalization_preview_failed      preview failed (reason, duration_ms)
//   personalization_preview_retry       "Erneut versuchen" pressed
//   personalization_audio_sample_played the Hörbuch name sample started playing
//   personalization_add_to_cart         personalized book reached the cart
//   personalization_add_to_cart_blocked the add was refused (reason)
//   personalization_abandoned           wizard closed without adding to cart
//
// personalization_add_to_cart_blocked reasons: "step_validation" (a step failed
// validation), "sku_rotation" (the session was bound to the other cover),
// "preview_not_ready", "field_errors" (the server refused an input) and
// "cart_error".
//
// The three photo_picker events come from Facebook's and Instagram's in-app
// browsers on Android alone, where a tap at times opens no picker, and carry
// photo_key. The hint's reason names the rule that found the picker shut -
// "second_tap", "no_open" or "returned_empty" - each counted once per page
// view; link_copied's names how the link went: "clipboard", "exec_command" or
// "manual" (shown selected, to copy by hand).
//
// personalization_photo_rejected with reason "client_validation" (the browser
// refused the photo) names the picked file's type (file_type: its MIME type,
// "unknown" where the browser names none) and size range (file_size: "<1MB",
// "1-5MB", "5-10MB", "10-30MB", ">30MB") - never its name.
//
// The three preview events carry `trigger`: "prefetch" (run started in the
// background when the customer leaves the first step), "step3" (started on
// entering the preview step — a public analytics value, so the slug keeps the
// number that step used to have) or "retry" (the retry button). ready/failed
// report the trigger of the run they settle, so a prefetched run that finishes while
// the customer is still writing the dedication stays attributed to the
// prefetch. duration_ms keeps measuring trigger → terminal state — for
// prefetched runs that is longer than the wait the customer actually saw.
// personalization_audio_sample_played reuses the same key with the value
// "name_sample" instead of adding a parameter of its own.
//
// `mode` separates a fresh personalization ("new") from an edit-from-cart
// re-save ("edit") — funnels should filter on mode = new.
//
// `locale` names the shop's language (request.locale.iso_code: "de", "fr", …),
// so the funnel can be read per market; null on a page that names none.
//
// NEVER put customer input in here: no child name, no dedication text, no
// filenames, no translated error copy. Reasons are short stable slugs.
//
// A SECOND consumer hangs off the same push: Microsoft Clarity, whose events
// carry no parameters at all. See PERSONALIZATION_CLARITY_EVENT_NAMES below —
// the step has to be baked into the Clarity event name, and the parameters go
// in as session-scoped custom tags.
//
// The map is keyed by STEP KEY, not by step number: the Cover step (products with
// both cover variants) and the Hörbuch step (books that also sell their Hörbuch)
// only exist for some products, and each shifts the number of the steps after it.
// The key is the stable identity of a step.
const PERSONALIZATION_ANALYTICS_STEP_NAMES = {
    character: "hauptfigur",
    cover: "cover",
    dedication: "widmung",
    audiobook: "hoerbuch",
    preview: "vorschau",
};

const PERSONALIZATION_ANALYTICS_EMPTY_PAYLOAD = {
    step: null,
    step_name: null,
    product_id: null,
    variant_id: null,
    sku: null,
    cover_type: null,
    mode: null,
    personalization_id: null,
    photo_key: null,
    reason: null,
    duration_ms: null,
    page_count: null,
    with_dedication: null,
    trigger: null,
    locale: null,
    file_type: null,
    file_size: null,
};

// ------------------------------------------------------------------
// Microsoft Clarity (Projekt y7axgsyodm, via Shopify-App-Block)
// ------------------------------------------------------------------
// Clarity kennt KEINE Event-Parameter: `clarity("event", name)` nimmt nur
// einen Namen. Die dataLayer-Events 1:1 durchzureichen würde deshalb genau
// das GA4-Problem reproduzieren — alle Schritte hießen
// personalization_step_view und wären im Funnel nicht unterscheidbar. Der
// Schritt muss also in den NAMEN.
//
// Diese Namen sind eine öffentliche Schnittstelle: sie erscheinen 1:1 in
// Clarity → Settings → Smart events und als Funnel-Schritt. Umbenennen
// bricht bestehende Funnels, und ein einmal gesendeter Name lässt sich in
// der Clarity-UI nicht mehr löschen (API-Events sind nur per Code
// entfernbar) — deshalb wird ausschließlich aus dieser Tabelle gesendet,
// nie ein zusammengebauter Name aus fremden Daten.
//
// Funnel-Reihenfolge (Clarity → Settings → Funnels → New funnel):
//   pers_open → pers_step1_view → pers_step2_view → pers_step3_view
//   → pers_preview_ready → pers_add_to_cart
// Die NUMMER im Namen hängt am Produkt: Hauptfigur (1) und Widmung (2) stehen
// immer vorn, dahinter folgen der Cover-Schritt (Bücher mit beiden
// Cover-Varianten) und der Hörbuch-Schritt (Bücher, die ihr Hörbuch mit
// verkaufen); die Vorschau ist immer der letzte (3, 4 oder 5).
// Die Nummer bleibt trotzdem im Namen — die Funnels sind bis heute nicht
// angelegt, und der Schritt-NAME steht in GA4 sowie im pers_step-Tag.
// Die übrigen Namen sind Diagnose-Events: keine Funnel-Schritte, aber
// Filter — und von dort ein Klick in die Session-Aufzeichnung.
//
// Bewusst NICHT an Clarity gemeldet: validation_error, photo_selected,
// preview_requested, preview_retry und audio_sample_played. Clarity deckelt
// Custom Smart Events bei 20; dieser Satz kann höchstens 19 Namen erzeugen
// (neun feste plus view/done für höchstens fünf Schritte) und bleibt knapp
// darunter — ein sechster Schritt ginge nicht mehr. Die fünf fehlenden
// stehen in GA4 und sind in einer Aufzeichnung ohnehin sichtbar.
//
// Ebenso wenig die drei photo_picker-Events: der Weg durch die Fotoauswahl in
// Facebooks und Instagrams In-App-Browsern auf Android steht in zwei Tags der
// Session. pers_inapp nennt die App (facebook / instagram) — schon beim Öffnen
// des Wizards, als Nenner —, pers_picker sammelt die Stationen des Wegs: tap,
// opened, cancel, file, file_empty, stuck, external und copied, jede höchstens
// einmal pro Seitenaufruf (tagPhotoPickerWay).
//
// Vom letzten Schritt (Vorschau) kommt nur _view an: aus ihm führt kein
// Vorwärts-Weg, also wird er nie „abgeschlossen". pers_step3_done und
// pers_step4_done kommen von Cover- bzw. Hörbuch-Schritt, je nachdem, welche
// das Produkt hat.
const PERSONALIZATION_CLARITY_EVENT_NAMES = {
    personalization_open: () => "pers_open",
    personalization_step_view: (p) => (p.step_name ? `pers_step${p.step}_view` : null),
    personalization_step_completed: (p) => (p.step_name ? `pers_step${p.step}_done` : null),
    personalization_cover_selected: () => "pers_cover_selected",
    personalization_photo_verified: () => "pers_photo_ok",
    personalization_photo_rejected: () => "pers_photo_rejected",
    personalization_preview_ready: () => "pers_preview_ready",
    personalization_preview_failed: () => "pers_preview_failed",
    personalization_add_to_cart: () => "pers_add_to_cart",
    personalization_add_to_cart_blocked: () => "pers_atc_blocked",
    personalization_abandoned: () => "pers_abandoned",
};

// Clarity-Tags sind immer Strings — eine Zahl wäre im Filter unbrauchbar,
// also Buckets. Die Grenzen sind an der Wizard-Copy orientiert („~30
// Sekunden"): alles über 30s ist eine gebrochene Erwartung.
function personalizationPreviewDurationBucket(durationMs) {
    if (!Number.isFinite(durationMs) || durationMs < 0) return null;
    if (durationMs < 10000) return "0-10s";
    if (durationMs < 30000) return "10-30s";
    if (durationMs < 60000) return "30-60s";
    return "60s+";
}

// Die Größe eines gewählten Fotos als Spanne (file_size, Clarity-Tag
// pers_file_size), geschnitten wie die Regeln des Wizards: das 10-MB-Limit
// und die 30 MB, über denen der Browser ein Foto gar nicht erst öffnet.
function personalizationPhotoSizeBucket(bytes) {
    const megabyte = 1024 * 1024;
    if (bytes < megabyte) return "<1MB";
    if (bytes <= 5 * megabyte) return "1-5MB";
    if (bytes <= PERSONALIZATION_PHOTO_MAX_BYTES) return "5-10MB";
    if (bytes <= PERSONALIZATION_PHOTO_PREPARE_MAX_BYTES) return "10-30MB";
    return ">30MB";
}

function pushPersonalizationClarity(eventName, payload) {
    // Der Shopify-App-Block definiert window.clarity sofort als Queue-Stub,
    // Aufrufe sind also auch vor dem Laden des Tag-Scripts sicher. Fehlt der
    // Block (App deinstalliert), passiert hier nichts.
    const clarity = window.clarity;
    if (typeof clarity !== "function") return;

    const resolveName = PERSONALIZATION_CLARITY_EVENT_NAMES[eventName];
    if (!resolveName) return;
    const clarityEvent = resolveName(payload);
    if (!clarityEvent) return;

    // Tags ZUERST: sie sind session-scoped, und ein Event, das vor seinem
    // Tag ankommt, ist in der Filter-Ansicht nicht zugeordnet. Mehrfaches
    // set() auf denselben Key sammelt die Werte zu einer Liste — bei
    // pers_reason ist das gewollt (alle Gründe einer Session).
    //
    // personalization_id bleibt bewusst draußen: sie würde eine
    // Session-Aufzeichnung mit einer identifizierbaren Bestellung
    // verknüpfen. Das ist eine Datenschutz-Entscheidung, keine technische.
    if (payload.cover_type) clarity("set", "pers_cover", payload.cover_type);
    if (payload.mode) clarity("set", "pers_mode", payload.mode);
    if (payload.reason) clarity("set", "pers_reason", payload.reason);
    if (payload.file_type) clarity("set", "pers_file_type", payload.file_type);
    if (payload.file_size) clarity("set", "pers_file_size", payload.file_size);
    if (payload.trigger) clarity("set", "pers_preview_trigger", payload.trigger);
    if (payload.with_dedication) clarity("set", "pers_dedication", payload.with_dedication);
    if (payload.step_name) clarity("set", "pers_step", String(payload.step));
    const durationBucket = personalizationPreviewDurationBucket(payload.duration_ms);
    if (durationBucket) clarity("set", "pers_preview_duration", durationBucket);
    // Beim Öffnen in Facebooks oder Instagrams In-App-Browser auf Android nennt
    // pers_inapp die App: der Nenner für den Weg durch die Fotoauswahl, auch
    // für Sessions, die das Foto nie antippen.
    const inApp = eventName === "personalization_open" ? personalizationAndroidInAppBrowser(window.navigator?.userAgent ?? "") : null;
    if (inApp) clarity("set", "pers_inapp", inApp);

    clarity("event", clarityEvent);

    if (eventName === "personalization_open") {
        // Clarity behält max. 100.000 Aufzeichnungen pro Tag und sampelt
        // darüber hinaus. upgrade() priorisiert genau die Sessions, deren
        // Drop-off man sehen will.
        clarity("upgrade", "personalization_wizard");
    }
}

// Ein Session-Tag ohne Event, für Momente, die in Clarity kein Event sind
// (etwa personalization_photo_selected): pushPersonalizationClarity setzt Tags
// nur zusammen mit einem Event.
function setPersonalizationClarityTag(name, value) {
    try {
        if (typeof window.clarity === "function") window.clarity("set", name, value);
    } catch (e) {
        // Tracking must never break the wizard.
    }
}

function pushPersonalizationEvent(eventName, params = {}) {
    const payload = { ...PERSONALIZATION_ANALYTICS_EMPTY_PAYLOAD, ...params };
    try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: eventName,
            personalization: payload,
        });
    } catch (e) {
        // Tracking must never break the wizard.
    }
    // Eigener try-Block: ein Clarity-Fehler darf den dataLayer-Push nicht
    // mitnehmen (und umgekehrt).
    try {
        pushPersonalizationClarity(eventName, payload);
    } catch (e) {
        // dito.
    }
}

/**
 * An error whose message is already a customer-facing, translated text (from
 * the locale files or from the bookpipe app proxy, which localizes its
 * storefront responses). Only these are ever rendered verbatim.
 *
 * Everything else \u2014 browser-generated failures above all \u2014 must NOT reach the
 * UI: their messages are untranslatable English strings baked into the engine
 * ("Load failed" in Safari, "Failed to fetch" in Chrome, "NetworkError when
 * attempting to fetch a resource." in Firefox, "The operation timed out." for
 * an AbortSignal.timeout). They are logged to the console instead and shown as
 * a translated message picked by PersonalizationModal.customerMessage().
 */
class PersonalizationLocalizedError extends Error {
    constructor(message) {
        super(message);
        this.name = "PersonalizationLocalizedError";
    }
}

// A fetch that never produced an HTTP response: offline, DNS/TLS failure,
// connection dropped mid-flight (Safari is fond of this one on flaky mobile
// networks), or a request the browser itself killed. fetch() rejects with a
// plain TypeError in every engine \u2014 the message text differs per browser, so
// the type plus "not one of ours" is the only portable signal.
function isPersonalizationNetworkError(error) {
    return error instanceof TypeError || error?.name === "TypeError";
}

// AbortSignal.timeout() rejects with a TimeoutError DOMException, controller
// .abort() with an AbortError \u2014 both mean "took too long", neither carries a
// translated message.
function isPersonalizationTimeoutError(error) {
    return error?.name === "AbortError" || error?.name === "TimeoutError";
}

// Facebook's in-app browser on Android - Messenger's too, which is Facebook's
// own (FB_IAB/Orca-Android) - or Instagram's: "facebook" or "instagram", null
// for every other browser. why Instagram first: its user agent can carry
// Facebook's in-app markers as well.
function personalizationAndroidInAppBrowser(userAgent) {
    if (!/Android/i.test(userAgent)) return null;
    if (/Instagram/.test(userAgent)) return "instagram";
    if (/FBAN|FBAV|FB_IAB|IABMV/.test(userAgent)) return "facebook";
    return null;
}

// The address of the product page at `location` that the photo picker's way
// out takes to the browser: its path, and its variant where it has one. why
// nothing else: the query can carry an ad's click ids and utm_* and the
// wizard's own (the cart's line_uuid, openPersonalize, edit) - none of it
// belongs to the page opened anew, nor in the address bar, the logs and the
// analytics there.
function personalizationPhotoPickerPageUrl(location) {
    const variant = new URLSearchParams(location.search).get("variant");
    const query = variant ? `?${new URLSearchParams({ variant })}` : "";
    return `${location.origin}${location.pathname}${query}`;
}

// The Android intent link that opens `pageUrl` in Chrome. Where Chrome is
// missing, or the app lets no intent through, the browser falls back to
// `pageUrl` itself - encoded whole, so that its own ? and = stay inside the
// fallback.
function personalizationChromeIntentUrl(pageUrl) {
    const { host, pathname, search } = new URL(pageUrl);
    return `intent://${host}${pathname}${search}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(pageUrl)};end`;
}

function removePersonalizationEmojis(value) {
    return String(value || "").replace(/[\p{Extended_Pictographic}\p{Regional_Indicator}\uFE0F\u200D]/gu, "");
}

function sanitizePersonalizationFieldValue(field) {
    if (!field) return { changed: false, value: "" };

    const currentValue = String(field.value || "");
    const selectionStart = field.selectionStart || 0;
    const sanitizedValue = removePersonalizationEmojis(currentValue);

    if (sanitizedValue === currentValue) {
        return { changed: false, value: sanitizedValue };
    }

    const sanitizedBeforeCursor = removePersonalizationEmojis(currentValue.slice(0, selectionStart));
    field.value = sanitizedValue;

    if (typeof field.setSelectionRange === "function") {
        const nextCursorPosition = sanitizedBeforeCursor.length;
        field.setSelectionRange(nextCursorPosition, nextCursorPosition);
    }

    return { changed: true, value: sanitizedValue };
}

window.PersonalizationFieldSanitizer = {
    removeEmojis: removePersonalizationEmojis,
    sanitizeFieldValue: sanitizePersonalizationFieldValue,
};

/**
 * localStorage draft schema version. Bump ONLY when the stored draft shape
 * changes incompatibly (renamed field keys, changed semantics, a flow rollout
 * that must not resume old drafts): the version is part of the key, so old
 * drafts are simply never read, and the sweep below deletes them. This is NOT
 * a deploy version — bumping discards every customer's unfinished draft.
 */
const PERSONALIZATION_STORAGE_VERSION = 1;
const PERSONALIZATION_STORAGE_KEY_PREFIX = "personalization:";

function personalizationStorageKey(productId) {
    return `${PERSONALIZATION_STORAGE_KEY_PREFIX}v${PERSONALIZATION_STORAGE_VERSION}:${productId}`;
}

// The step-2 dedication autofill (inline snippet script) reads the draft too —
// expose the key builder so every reader agrees on the version and a bump
// cannot leave an inline script reading a dead key.
window.PersonalizationDraftStorage = {
    version: PERSONALIZATION_STORAGE_VERSION,
    key: personalizationStorageKey,
};

// Drafts written under any other schema version (including the unversioned
// pre-v1 `personalization:<productId>` keys) are dead: delete them rather
// than merely skip them — saveState() merges into whatever the key already
// holds, so a stale object left in place would leak back into a fresh draft
// on the first keystroke.
try {
    const personalizationCurrentKeyPrefix = `${PERSONALIZATION_STORAGE_KEY_PREFIX}v${PERSONALIZATION_STORAGE_VERSION}:`;
    for (let i = localStorage.length - 1; i >= 0; i--) {
        const storedKey = localStorage.key(i);
        if (
            storedKey &&
            storedKey.startsWith(PERSONALIZATION_STORAGE_KEY_PREFIX) &&
            !storedKey.startsWith(personalizationCurrentKeyPrefix)
        ) {
            localStorage.removeItem(storedKey);
        }
    }
} catch (e) {
    // Storage unavailable — nothing to sweep.
}

/**
 * IndexedDB helper for the uploaded child photo. Files cannot go into
 * localStorage, but we still want the photo to survive reloads and the
 * edit-from-cart flow within the same browser.
 */
const PersonalizationPhotoStore = {
    dbName: "mutmachbuch-personalization",
    storeName: "photos",

    open(recreated = false) {
        return new Promise((resolve, reject) => {
            if (!window.indexedDB) {
                reject(new Error("IndexedDB unavailable"));
                return;
            }
            const request = window.indexedDB.open(this.dbName, 1);
            request.onupgradeneeded = () => {
                if (!request.result.objectStoreNames.contains(this.storeName)) {
                    request.result.createObjectStore(this.storeName);
                }
            };
            request.onsuccess = () => {
                const db = request.result;
                // A database at our version but without our store (an
                // upgrade cut short by an in-app browser, a stale copy from
                // an older schema): every transaction on it throws "object
                // store not found" (Sentry MUTMACHBUCH-SHOPIFY-2H). Drop it
                // once and open again — the upgrade handler recreates the
                // store; the photos in it were unreachable anyway.
                if (!db.objectStoreNames.contains(this.storeName)) {
                    db.close();
                    if (recreated) {
                        reject(new Error("IndexedDB store missing after recreate"));
                        return;
                    }
                    const drop = window.indexedDB.deleteDatabase(this.dbName);
                    drop.onsuccess = () => this.open(true).then(resolve, reject);
                    drop.onerror = () => reject(drop.error);
                    drop.onblocked = () => reject(new Error("IndexedDB recreate blocked"));
                    return;
                }
                resolve(db);
            };
            request.onerror = () => reject(request.error);
            request.onblocked = () => reject(new Error("IndexedDB open blocked"));
        });
    },

    async set(key, record) {
        try {
            const db = await this.open();
            await new Promise((resolve, reject) => {
                const tx = db.transaction(this.storeName, "readwrite");
                tx.objectStore(this.storeName).put({ ...record, savedAt: Date.now() }, key);
                tx.oncomplete = resolve;
                tx.onerror = () => reject(tx.error);
            });
            db.close();
        } catch (e) {
            // Photo persistence is best-effort only.
        }
    },

    // Photos must outlive add-to-cart so edit-from-cart can regenerate the
    // preview, but they should not accumulate forever on the device.
    async pruneOlderThan(maxAgeMs) {
        try {
            const cutoff = Date.now() - maxAgeMs;
            const db = await this.open();
            await new Promise((resolve, reject) => {
                const tx = db.transaction(this.storeName, "readwrite");
                const cursorRequest = tx.objectStore(this.storeName).openCursor();
                cursorRequest.onsuccess = () => {
                    const cursor = cursorRequest.result;
                    if (!cursor) return;
                    const savedAt = cursor.value?.savedAt || 0;
                    if (savedAt < cutoff) {
                        cursor.delete();
                    }
                    cursor.continue();
                };
                tx.oncomplete = resolve;
                tx.onerror = () => reject(tx.error);
            });
            db.close();
        } catch (e) {
            // noop
        }
    },

    async get(key) {
        try {
            const db = await this.open();
            const record = await new Promise((resolve, reject) => {
                const tx = db.transaction(this.storeName, "readonly");
                const request = tx.objectStore(this.storeName).get(key);
                request.onsuccess = () => resolve(request.result || null);
                request.onerror = () => reject(request.error);
            });
            db.close();
            return record;
        } catch (e) {
            return null;
        }
    },

    async delete(key) {
        try {
            const db = await this.open();
            await new Promise((resolve, reject) => {
                const tx = db.transaction(this.storeName, "readwrite");
                tx.objectStore(this.storeName).delete(key);
                tx.oncomplete = resolve;
                tx.onerror = () => reject(tx.error);
            });
            db.close();
        } catch (e) {
            // noop
        }
    },

    async deleteByPrefix(prefix) {
        try {
            const db = await this.open();
            await new Promise((resolve, reject) => {
                const tx = db.transaction(this.storeName, "readwrite");
                const store = tx.objectStore(this.storeName);
                const request = store.getAllKeys();
                request.onsuccess = () => {
                    (request.result || []).forEach((key) => {
                        if (String(key).indexOf(prefix) === 0) {
                            store.delete(key);
                        }
                    });
                };
                tx.oncomplete = resolve;
                tx.onerror = () => reject(tx.error);
            });
            db.close();
        } catch (e) {
            // noop
        }
    },
};

/**
 * Personalization Modal Web Component
 * Wizard: Hauptfigur (name + photo) → Widmung → Cover (only when the product has both
 * cover variants) → Hörbuch (only for a book that sells its set — a "Ja" buys the set
 * instead of the book) → Vorschau (bookpipe preview). The cover and the Hörbuch are chosen
 * inside the wizard, so the step NUMBERS depend on the product — Liquid stamps every tab
 * and form with a data-step-key, and this class resolves ids through it (stepIdForKey).
 * The page's format (data-format) shapes the same product's flow in Liquid as well: the
 * Hörbuch itself asks for name and dedication only, the set has no Hörbuch step because it
 * always brings the Hörbuch along (forced in the cart payload, handleAddToCart).
 * Handles the bookpipe personalization (multipart photo upload), preview states
 * and cart submission.
 */
class PersonalizationModal extends HTMLElement {
    constructor() {
        super();
        this.boundHandleAddToCart = this.handleAddToCart.bind(this);
        this.boundHandleStepClick = this.handleStepClick.bind(this);
        this.boundHandleInput = this.handleInput.bind(this);
        this.boundHandleFocus = this.handleFocus.bind(this);
        this.boundHandlePointerDown = this.handlePointerDown.bind(this);
        this.boundHandleDocClick = this.handleDocClick.bind(this);
        this.boundHandleTriggerOpen = this.handleTriggerOpen.bind(this);
        this.boundHandlePhotoInputChange = this.handlePhotoInputChange.bind(this);
        this.boundHandlePhotoInputCancel = this.handlePhotoInputCancel.bind(this);
        this.boundHandlePhotoAreaClick = this.handlePhotoAreaClick.bind(this);
        this.boundHandleDragOver = this.handleDragOver.bind(this);
        this.boundHandleDragLeave = this.handleDragLeave.bind(this);
        this.boundHandleDrop = this.handleDrop.bind(this);
        this.boundHandlePreviewRetry = this.handlePreviewRetry.bind(this);
        this.boundHandlePreviewPrev = this.handlePreviewPrev.bind(this);
        this.boundHandlePreviewNext = this.handlePreviewNext.bind(this);
        this.boundHandlePreviewTrackScroll = this.handlePreviewTrackScroll.bind(this);
        this.boundHandlePageShow = this.handlePageShow.bind(this);
        this.boundHandleRedirectKeydown = this.handleRedirectKeydown.bind(this);
        this.boundHandleVariantChange = this.handleVariantChange.bind(this);
        this.activePreviewPage = 0;
        this.previewPageCount = 0;
        this.variantId = this.getAttribute("data-variant-id");
        // why: the page's own selection (selected_or_first_available_variant, which
        // honours ?variant=) has to survive. The wizard WRITES the product form's
        // variant input from now on, so that input can no longer answer "what did the
        // page select" once a draft has mirrored a cover into it.
        this.initialVariantId = this.getAttribute("data-variant-id") || "";
        this.softcoverVariantId = this.getAttribute("data-softcover-variant-id");
        this.hardcoverVariantId = this.getAttribute("data-hardcover-variant-id");
        this.softcoverSku = this.getAttribute("data-softcover-sku") || "";
        this.hardcoverSku = this.getAttribute("data-hardcover-sku") || "";
        // The product's digital Hörbuch variant (blank without one): the Hörbuch is its own
        // product in bookpipe, so its render resolves config and session by this SKU, and the
        // book and the set name the Hörbuch their name sample speaks with by it.
        this.audiobookSku = this.getAttribute("data-audiobook-sku") || "";
        // The set's variants of the two covers - the product's own Kombi-Softcover and Kombi-Hardcover
        // (plan hoerbuch-set-als-variante; blank without a set): what the Hörbuch step offers
        // (setOffered) and a set line is bought as (cartVariantIdForCoverType). In the book format
        // the product form holds the book's pair, in the set format this one.
        this.setSoftcoverVariantId = this.getAttribute("data-set-softcover-variant-id") || "";
        this.setHardcoverVariantId = this.getAttribute("data-set-hardcover-variant-id") || "";
        // The in-flight cover derivation, shared by its concurrent callers
        // (ensureCoverSelection).
        this.coverSelectionPromise = null;
        this.bookpipePersonalizationUrl = this.getAttribute("data-bookpipe-personalization-url") || "/apps/bookpipe/personalizations";
        this.bookpipeProductConfigUrl = this.getAttribute("data-bookpipe-product-config-url") || "/apps/bookpipe/products";
        this.bookpipeChallengeUrl = this.getAttribute("data-bookpipe-challenge-url") || "/apps/bookpipe/challenges";
        // The shop's language (request.locale.iso_code), which every app proxy request names to
        // bookpipe (appProxyUrl). why: never window.Shopify.locale - Cookiebot can hold back
        // content_for_header, which defines it (layout/theme.liquid); the page's own lang renders
        // the same value.
        this.storefrontLocale = this.getAttribute("data-locale") || document.documentElement.lang || "";
        // Default/legacy photo option key: pre-cutover drafts stored their
        // single photo under this key, and the fallback config renders it.
        this.bookpipePhotoKey = this.getAttribute("data-bookpipe-photo-key") || "child_photo";
        // "book" (printed Mutmachbuch) or "audiobook" (the standalone digital
        // Hörbuch): the flows differ in the fallback field set and the name
        // sample. Everything else is shared.
        this.productKind = this.getAttribute("data-product-kind") || "book";
        // The format the page renders (snippets/product-format.liquid): "book", "audiobook"
        // (the Hörbuch itself) or "set" (book + Hörbuch). Liquid already shaped the steps and
        // the copy for it; the cart payload is what still depends on it.
        this.format = this.getAttribute("data-format") || "book";
        this.editMode = window.location.search.includes("edit=book") ? "book" : window.location.search.includes("edit=other") ? "other" : null;
        this.lastPointerDownTarget = null;
        this.isSubmittingCart = false;
        // True from a successful add/edit until the /cart navigation actually
        // leaves the page (or a bfcache restore rolls it back): the wizard
        // stays open under the nav cover, and a retry click may only
        // re-navigate — the items are already in the cart.
        this.cartRedirectPending = false;
        this.navFallbackTimer = null;
        // True while inputs are being programmatically restored from the
        // saved draft: hydration must never queue a session PATCH — the
        // pre-preview full sync is the reconciliation point.
        this.isHydrating = false;

        // Uploaded photos and their object URLs, keyed by the bookpipe option
        // key of the upload field (multi-photo products render one dropzone
        // per key).
        this.photoFiles = new Map();
        this.photoObjectUrls = new Map();
        // The customer's picks the browser is still preparing
        // (preparePhotoForUpload), one { file, promise } per upload field: the
        // photo picked, and its way to applyPhotoFile.
        this.photoPreparations = new Map();
        // Per-dropzone state of the photo's upload and its server-side check
        // (size + AI gate): "uploading" while its bytes go out, "checking" once
        // they are all out, "verified" after a 2xx, "failed" once the upload
        // gave up for good, "none" when the photo is present but unverified
        // (refused for a reason other than the photo, restored draft) —
        // rejected photos are cleared instead.
        this.photoCheckStates = new Map();
        // What each state says beyond its name ({ share } while uploading,
        // { challenge } once failed), so a re-render shows it again.
        this.photoCheckDetails = new Map();

        // Personalization inputs configured in bookpipe for the selected
        // variant's product; null until the app-proxy config request resolved.
        this.fieldConfig = null;
        this.fieldConfigPromise = null;
        this.fieldConfigSku = null;

        // Per-field rejection messages from the server (e.g. the AI photo
        // gate), keyed by option key.
        this.serverFieldErrors = new Map();

        this.previewState = "idle";
        this.previewSignature = null;
        this.currentPersonalization = null;
        this.previewAbortController = null;
        this.previewRequestToken = 0;

        // Loading-state progress bar + rotating messages (see the
        // PERSONALIZATION_PREVIEW_PROGRESS_* constants).
        this.previewProgressTimer = null;
        this.previewMessageTimer = null;
        this.previewMessageFadeTimer = null;
        this.previewProgressStartedAt = 0;
        this.previewMessageIndex = -1;

        // The run the current poll loop is serving ({id, signature}); lets a
        // re-entrant ensurePreview adopt it instead of orphaning it.
        this.activePreviewPoll = null;

        // why: the run the ROTATED session has to wait out ({sessionId} of the
        // session it replaced), set when a cover switch rotates while a run is
        // live. Adoption reuses a sibling session's stored output, so it only
        // sees runs that already finished: posting the new session's run into
        // that window generates — and charges — the identical image a second
        // time. Because the prefetch starts the moment the character step is left
        // and the cover step follows right after the dedication, that window is the
        // normal case for hardcover buyers, not an edge case.
        // Residual window, knowingly left open: a switch landing after the old
        // POST left the browser but before its response registered
        // activePreviewPoll is invisible here, and the show endpoint cannot
        // close it either — it reports "pending" for a draft that never ran
        // just as for a queued run, so waiting on it would stall sessions
        // without any run for the full wait budget.
        this.deferredSiblingRun = null;

        // What started the current ensurePreview pass ("prefetch" | "step3" |
        // "retry"); captured into analyticsPreviewTrigger when a run actually
        // enters "loading" so ready/failed report the trigger of THEIR run.
        this.previewRunTrigger = "step3";

        // Whether the live ensurePreview pass runs in background (prefetch)
        // mode, and — when a background pass settles in a real failure — the
        // signature it failed for. Entering step 3 with that same signature
        // REVEALS the stored error (the step-3 form renders it already)
        // instead of silently re-spending a run on byte-identical inputs;
        // the retry button stays the explicit way to try again.
        this.previewPassBackground = false;
        this.backgroundPreviewErrorSignature = null;
        // The signature the live pass is actually serving; null until the
        // pass computed and claimed it. A pass that dies BEFORE that (e.g.
        // buildPreviewSignature's cart fetch failing in edit mode) must not
        // stick its error to the PREVIOUS pass's signature — that could
        // shadow a perfectly restorable ready preview behind a bogus error.
        this.previewPassSignature = null;

        // Interactive Personalisierungs-Session (bookpipe): created when
        // the wizard opens, every input syncs immediately (photos with an
        // instant AI verdict). The preview run starts in the background on
        // the step-1→2 transition (the dedication never affects generation);
        // step 3 re-enters ensurePreview, which adopts or replays that run.
        this.sessionId = null;
        this.sessionPromise = null;
        // Bumped on every session rotation: an upload verdict that raced a
        // rotation must not be recorded against the new session.
        this.sessionEpoch = 0;
        this.sessionSyncTimers = new Map();
        this.sessionUploadedPhotos = new Map();
        // Photo uploads on their way, one entry
        // { epoch, key, fingerprint, progress, requests, startedAt, controller, promise }
        // per upload: the session epoch its current attempt goes to, the upload
        // field, the photo, the share of its bytes the current request sent so
        // far (0–1), how many requests of it went out, when it started (for the
        // failure report) and the AbortController of its current attempt (see
        // uploadPhotoToSession).
        this.pendingPhotoUploads = new Set();
        this.previewRetryNonce = 0;
        this.wizardOpened = false;

        // Audiobook name sample: the in-flight/settled run per trimmed name, and
        // the name the latest request was made for (a run whose name was
        // corrected meanwhile must not apply its audio).
        this.nameSampleRequests = new Map();
        // The same names mapped to their outcome: "pending", "failed", or the
        // signed MP3 URL the run ended with.
        this.nameSampleResults = new Map();
        this.nameSampleActiveName = null;
        // The full session sync currently in flight, { epoch, promise } — shared
        // by the preview passes of the session epoch it started in (see
        // sharedSyncAllToSession).
        this.pendingFullSync = null;

        // GTM funnel tracking (see pushPersonalizationEvent). `analyticsVisible`
        // gates the step events: activateStep also runs while the drawer is
        // closed (connectedCallback, the trigger's capture handler), and those
        // are not step VIEWS — the drawer's show event is what the customer sees.
        this.analyticsVisible = false;
        this.analyticsLastStepViewed = null;
        this.analyticsPreviewStartedAt = 0;
        this.analyticsPreviewTrigger = null;
        this.analyticsRequestedSignature = null;
        this.analyticsCartCompleted = false;
    }

    // ------------------------------------------------------------------
    // GTM funnel tracking
    // ------------------------------------------------------------------

    // The variant is read synchronously on purpose: getSelectedVariantId()
    // fetches cart.js in edit mode, and an await would let the open/step
    // events land out of order (or after the customer already moved on).
    analyticsVariantId() {
        const productFormInput = document.querySelector('product-form form input[name="id"]');
        if (productFormInput?.value) return String(productFormInput.value);
        const urlVariant = new URLSearchParams(window.location.search || "").get("variant");
        if (urlVariant) return String(urlVariant);
        return String(this.variantId || this.softcoverVariantId || "");
    }

    trackPersonalization(eventName, params = {}) {
        const variantId = this.analyticsVariantId();
        const coverType = this.resolveCoverTypeForVariant(variantId);
        const stepId = params.step === undefined ? this.getActiveStepId() : params.step;
        const step = stepId === null || stepId === undefined || stepId === "" ? null : Number(stepId);
        pushPersonalizationEvent(eventName, {
            product_id: this.productId || null,
            variant_id: variantId || null,
            sku: this.resolveSkuForCoverType(coverType) || null,
            cover_type: coverType || null,
            mode: this.editMode ? "edit" : "new",
            personalization_id: this.currentPersonalization?.personalization_id || this.sessionId || null,
            locale: this.storefrontLocale || null,
            ...params,
            step: Number.isFinite(step) ? step : null,
            step_name: PERSONALIZATION_ANALYTICS_STEP_NAMES[this.stepKeyForId(stepId)] || null,
        });
    }

    connectedCallback() {
        this.addToCartButtons = this.querySelectorAll("[data-add-personalized-to-cart]");
        this.addToCartButtons.forEach((button) => {
            button.addEventListener("click", this.boundHandleAddToCart);
        });

        // Steps and dynamic content
        this.stepHeaders = this.querySelectorAll(".personalization-step");
        this.productId = this.getAttribute("data-product-id") || this.closest("modal-drawer")?.id?.replace("personalize-modal-", "") || "unknown";

        this.stepHeaders.forEach((step) => step.addEventListener("click", this.boundHandleStepClick));

        // Navigation buttons are handled once via the document-level listener
        // (handleDocClick); a direct listener as well would run validation twice.
        document.addEventListener("click", this.boundHandleDocClick);

        // The step forms are real <form> elements; Enter inside them must not
        // trigger the browser's implicit submission (a GET navigation that
        // would put the child's name into the URL and lose all wizard state).
        this.getModalRoot()
            .querySelectorAll(".personalization-step-form, .personalization-form-boundary")
            .forEach((form) => form.addEventListener("submit", (e) => e.preventDefault()));

        const triggerSelector = `modal-trigger[target="#personalize-modal-${this.productId}"]`;
        this.triggerEl = document.querySelector(triggerSelector);
        if (this.triggerEl) {
            this.triggerEl.addEventListener("click", this.boundHandleTriggerOpen, { capture: true });
        }

        this.bindPhotoControls();
        this.bindPreviewControls();
        this.bindNameSampleControls();
        this.setupTouchScrollLock();
        this.setupSessionLifecycle();
        this.setupOpenStateClass();

        // Cart-redirect lifecycle: bfcache-restore cleanup and Escape
        // suppression while a navigation to /cart is in flight (the wizard
        // stays open under the nav cover during that window).
        window.addEventListener("pageshow", this.boundHandlePageShow);
        window.addEventListener("keydown", this.boundHandleRedirectKeydown, { capture: true });

        // The theme's variant switch is asynchronous (section fetch, then a
        // DOM patch of input[name=id]): a wizard reopened inside that window
        // reads the OLD variant and resumes the old cover's session. The
        // picker dispatches variant:change (bubbles) once the switch has
        // settled — re-run the SKU check then, so the already-open wizard
        // rotates to the newly selected cover instead of staying stale.
        document.addEventListener("variant:change", this.boundHandleVariantChange);

        const params = new URLSearchParams(window.location.search || "");
        let editState = "";
        if (params.has("edit")) {
            editState = params.get("edit");
        }
        if (editState === "book") {
            this.activateStep("1");
        } else if (editState === "other") {
            // "other" means the customer edits the dedication, not the book itself.
            this.activateStep(this.dedicationStepId() || "1");
        } else {
            this.activateStep("1");
        }

        // Load the configured personalization inputs and render step 1 from
        // them. In edit mode the openPersonalize prefill installs the edited
        // line's state and hydrates the photos itself — an eager hydration
        // here would read a stale draft's uuid.
        this.initFieldConfig({ hydratePhotos: !params.has("openPersonalize") });
        PersonalizationPhotoStore.pruneOlderThan(60 * 24 * 60 * 60 * 1000);
    }

    disconnectedCallback() {
        this.addToCartButtons.forEach((button) => {
            button.removeEventListener("click", this.boundHandleAddToCart);
        });

        this.stepHeaders?.forEach((step) => step.removeEventListener("click", this.boundHandleStepClick));
        document.removeEventListener("click", this.boundHandleDocClick);
        if (this.triggerEl) {
            this.triggerEl.removeEventListener("click", this.boundHandleTriggerOpen, true);
        }
        this.unbindPhotoControls();
        this.unbindPreviewControls();
        window.removeEventListener("pageshow", this.boundHandlePageShow);
        window.removeEventListener("keydown", this.boundHandleRedirectKeydown, { capture: true });
        document.removeEventListener("variant:change", this.boundHandleVariantChange);
        this.photoObjectUrls.forEach((url) => URL.revokeObjectURL(url));
        this.photoObjectUrls.clear();
        // The element can be torn down mid-run (theme editor section
        // re-render): kill the UI timers and invalidate the run, otherwise
        // the orphaned loop keeps writing into whichever live drawer now
        // owns the modal id (getModalRoot resolves by document id).
        this.stopPreviewProgress({ silent: true });
        this.previewRequestToken += 1;
        this.previewAbortController?.abort();
        this.activePreviewPoll = null;
        this.deferredSiblingRun = null;
        // The same for the name sample, whose poll loop runs for up to 90 s: a card
        // switch on the product page replaces the wizard without a reload, and the
        // orphaned run would show its outcome in the new wizard's drawer. Without an
        // active name the loop stops at its next check and settleNameSample drops
        // whatever still arrives.
        this.nameSampleActiveName = null;
    }

    // ------------------------------------------------------------------
    // iOS/WebKit scroll lock
    // ------------------------------------------------------------------

    // The theme's modal scroll lock only sets body{overflow:hidden}, which
    // iOS/WebKit ignores for touch scrolling: gestures on non-scrollable
    // parts of the fullscreen wizard scroll the page behind the modal.
    // While the wizard is open on touch devices, pin the body in place.
    setupTouchScrollLock() {
        const drawer = document.getElementById(`personalize-modal-${this.productId}`);
        if (!drawer || drawer.dataset.touchScrollLockBound === "true") return;
        drawer.dataset.touchScrollLockBound = "true";

        drawer.addEventListener("show", () => {
            if (!window.matchMedia("(pointer: coarse)").matches) return;
            this.lockedScrollY = window.scrollY;
            document.body.style.position = "fixed";
            document.body.style.top = `-${this.lockedScrollY}px`;
            document.body.style.left = "0";
            document.body.style.right = "0";
            document.body.style.width = "100%";
        });

        drawer.addEventListener("hide", () => {
            if (document.body.style.position !== "fixed") return;
            document.body.style.position = "";
            document.body.style.top = "";
            document.body.style.left = "";
            document.body.style.right = "";
            document.body.style.width = "";
            window.scrollTo(0, this.lockedScrollY || 0);
        });
    }

    // While the wizard is open the page carries body.personalization-open,
    // for what lives outside the drawer and has to make way for it: Trusted
    // Shops' floating badge sits above every layer of the page (z-index
    // 2147483647) and on phones covers the left end of the Weiter button.
    // The drawer reports its state through show/hide events only, no
    // attribute CSS could read. The rule that hides the badge is in
    // snippets/personalize-book.liquid.
    setupOpenStateClass() {
        const drawer = document.getElementById(`personalize-modal-${this.productId}`);
        if (!drawer || drawer.dataset.openStateClassBound === "true") return;
        drawer.dataset.openStateClassBound = "true";

        drawer.addEventListener("show", () => document.body.classList.add("personalization-open"));
        drawer.addEventListener("hide", () => document.body.classList.remove("personalization-open"));
    }

    // ------------------------------------------------------------------
    // Interactive Personalisierungs-Session (bookpipe v2)
    // ------------------------------------------------------------------

    setupSessionLifecycle() {
        const drawer = document.getElementById(`personalize-modal-${this.productId}`);
        if (!drawer || drawer.dataset.sessionLifecycleBound === "true") return;
        drawer.dataset.sessionLifecycleBound = "true";

        // The session is created when the wizard opens (FR-PERS-16) — the
        // drawer's show event covers both the trigger button and the
        // programmatic edit-from-cart open.
        drawer.addEventListener("show", async () => {
            this.wizardOpened = true;
            // First: the cover settles — the customer's own pick or an edited line's cover
            // decides the variant, else the page's stays (nothing is preselected) — and
            // with it the SKU the session is checked against and the variant the open
            // event reports.
            await this.ensureCoverSelection();
            this.ensureSessionForActiveVariant();

            // Funnel: one open event per drawer show, then the step the
            // customer actually lands on (activateStep ran before "show"
            // fires, so the initial step never re-fires through the gate).
            this.analyticsVisible = true;
            this.analyticsCartCompleted = false;
            this.analyticsLastStepViewed = null;
            this.trackPersonalization("personalization_open");
            this.trackStepView(this.getActiveStepId());
        });

        // Closing without a completed add-to-cart is the funnel's exit event.
        // The add-to-cart path never fires it: the drawer stays open under
        // the nav cover during the /cart redirect (no hide), and
        // analyticsCartCompleted guards the bfcache-restore hide.
        drawer.addEventListener("hide", () => {
            if (!this.analyticsVisible) return;
            this.analyticsVisible = false;
            if (this.analyticsCartCompleted || this.cartRedirectPending) return;
            this.trackPersonalization("personalization_abandoned");
        });
    }

    // Step views drive the funnel order (Hauptfigur → … → Vorschau, however many
    // steps the product renders). Deduped against the
    // last viewed step so re-renders (variant rotation, validation bounces
    // back to the SAME step) don't inflate the counts; a genuine back-and-
    // forward navigation is a real view again.
    trackStepView(stepId) {
        if (!this.analyticsVisible || !stepId) return;
        if (this.analyticsLastStepViewed === String(stepId)) return;
        this.analyticsLastStepViewed = String(stepId);
        this.trackPersonalization("personalization_step_view", { step: stepId });
    }

    // "true"/"false" as strings (GA4 custom dimensions are text), null when
    // the product has no dedication option or nothing is selected yet.
    dedicationSelectedForAnalytics() {
        if (!this.supportsDedication()) return null;
        const root = this.getModalRoot();
        const selected = root?.querySelector('input[name="__add-dedication"]:checked');
        return selected ? selected.value : null;
    }

    // A variant switch settled on the product page. Only an OPEN wizard
    // needs the immediate re-check (rotation + preview invalidation): while
    // it is closed, the next open runs the same check anyway, and reacting
    // here would eagerly create sessions for covers the customer is merely
    // browsing.
    handleVariantChange() {
        const drawer = document.getElementById(`personalize-modal-${this.productId}`);
        if (!drawer?.open) return;
        this.ensureSessionForActiveVariant();
    }

    resetSession() {
        this.sessionId = null;
        this.sessionPromise = null;
        this.sessionEpoch += 1;
        // An upload on its way to the replaced session can never reach this
        // one: called off, it leaves a slow uplink to the photo's upload to the
        // session that will use it.
        this.abortPhotoUploads((upload) => upload.epoch !== this.sessionEpoch);
        this.sessionUploadedPhotos.clear();
        // Every claim of a dropzone — uploading, checking, verified, failed —
        // is about the replaced session, and none may outlive it: a fresh
        // session has verified nothing yet, and the upload called off above
        // ends without a word (nothing else would end its "uploading … 37 %"
        // when no preview pass follows). The photo itself stays; the
        // pre-preview sync re-uploads and re-checks it.
        this.photoCheckStates.forEach((state, key) => {
            if (state !== "none") {
                this.setPhotoCheckState(key, "none");
            }
        });
        // A rotated session invalidates any poll target registered for the
        // old one — adopting it would watch a locked/purged session.
        this.activePreviewPoll = null;
        // Also drop the stored ids: a locked (ordered) or purged session must
        // never be resurrected from localStorage. The upload fingerprints and
        // the SKU are meaningless without their session.
        this.saveState({
            __bp_session_id: null,
            __bp_session_key: null,
            __bp_session_sku: null,
            __bp_uploaded_photos: null,
            _bookpipe_personalization_id: null,
        });
        // The name sample's unfinished runs go with the session. Last: whatever that asks again
        // must find the stored ids gone, or it would resume the very session being replaced.
        this.forgetNameSamplesOfReplacedSession();
    }

    // Sessions are bound to the SKU they were created with; when the customer
    // switched the cover variant while the wizard was closed, the stored
    // session must not be resumed for the other product. A session whose SKU
    // binding is unknown (draft persisted before this key existed) counts as
    // a mismatch — it may belong to either cover. Edit-from-cart sessions are
    // exempt: the edited line's variant is pinned through the cart and the
    // session must stay the line's own.
    async ensureSessionForActiveVariant() {
        try {
            const sku = await this.resolveActiveSku();
            const state = this.getSavedState() || {};
            const storedSku = String(state["__bp_session_sku"] || "");
            const hasSession = Boolean(this.sessionId || state["__bp_session_id"] || state["_bookpipe_personalization_id"]);
            if (sku && hasSession && !this.editMode && sku !== storedSku) {
                // Registered only after a run was POSTed for the session being
                // replaced: its output is what the new session's run adopts, so
                // remember it here — resetSession() drops the poll target below.
                const liveRun = this.activePreviewPoll;
                if (liveRun?.id) {
                    this.deferredSiblingRun = { sessionId: String(liveRun.id) };
                }
                this.resetSession();
                this.invalidatePreviewForNewSession();
            }
        } catch (e) {
            // The 409/404 replay in sessionRequest self-heals a wrong resume.
        }
        return this.ensureSession();
    }

    // After a session rotation any generated preview belongs to the old
    // session/SKU: never present it as ready-to-buy. If the customer is
    // sitting on the preview step, immediately regenerate for the new session.
    invalidatePreviewForNewSession() {
        if (this.previewState === "idle") return;
        this.previewSignature = null;
        this.currentPersonalization = null;
        this.previewRequestToken += 1;
        this.previewAbortController?.abort();
        this.setPreviewState("idle");
        const activeStepId = this.getActiveStepId();
        if (activeStepId === this.previewStepId()) {
            this.ensurePreview();
        } else if (activeStepId !== this.characterStepId()) {
            // The character step is behind the customer — restart the prefetch
            // against the rotated session. Server-side sibling adoption
            // (byte-identical photos, matching setting fingerprints) makes the
            // cover-switch run cheap; the customer finds the new cover's preview
            // ready. On the character step nothing is started: the inputs the run
            // consumes are still being edited.
            this.ensurePreview({ background: true });
        }
    }

    sessionHeaders() {
        return {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
        };
    }

    // `url` of bookpipe's app proxy with the shop's language, which bookpipe answers in (German
    // without one). why: in the query only - Shopify signs an app proxy request's query string, and
    // on bookpipe's side a body value would override the signed one.
    appProxyUrl(url) {
        if (!this.storefrontLocale) return url;
        return `${url}${url.includes("?") ? "&" : "?"}locale=${encodeURIComponent(this.storefrontLocale)}`;
    }

    // --- Bot challenge (self-hosted proof-of-work, ALTCHA wire format) ---
    // The backend advertises {enabled, provider} in the product config; while
    // enabled, the session create carries a freshly solved challenge token
    // and the cost-bearing endpoints 403 (code "challenge_required") for
    // unverified sessions, which the handlers below heal transparently.

    getChallengeConfig() {
        const challenge = this.fieldConfig?.challenge;
        return challenge && challenge.enabled ? challenge : { enabled: false, provider: "altcha" };
    }

    isChallengeRequired(response, data) {
        return response?.status === 403 && data?.code === "challenge_required";
    }

    // undefined (= unbounded) on engines without AbortSignal.timeout (older
    // Safari/Firefox), which is exactly how these requests behaved before.
    timeoutSignal(milliseconds) {
        try {
            return AbortSignal.timeout(milliseconds);
        } catch (e) {
            return undefined;
        }
    }

    // Bound every challenge round-trip: these run between the preview
    // watchdog's attempts, where no other timer is armed, so an unbounded
    // fetch against a stalled backend would park the wizard in "loading"
    // forever with no error and no retry affordance.
    challengeSignal() {
        return this.timeoutSignal(PERSONALIZATION_CHALLENGE_REQUEST_TIMEOUT_MS);
    }

    // Fetch one single-use challenge and solve it. Tokens are never cached:
    // the server burns each solution on first use.
    async mintChallengeToken() {
        const fetchFn = window.originalFetch || window.fetch;
        // Cache-busted twice over (query + no-store): a cached challenge
        // would already be burned server-side and 403 every retry. The sid
        // keys the server's issuance rate limit per customer session (the
        // browser-scoped id, like external_session_id) instead of per
        // (shared) proxy IP.
        const sid = encodeURIComponent(this.getOrCreateCustomerSessionId() || "");
        const response = await fetchFn(this.appProxyUrl(`${this.bookpipeChallengeUrl}?t=${Date.now()}&sid=${sid}`), {
            method: "GET",
            cache: "no-store",
            headers: this.sessionHeaders(),
            signal: this.challengeSignal(),
        });
        if (!response.ok) {
            throw await this.buildRequestError("Bookpipe challenge request failed", response);
        }
        const challenge = await this.parseJsonResponse(response);
        if (!challenge?.challenge || !challenge?.salt || !challenge?.signature) {
            throw new Error("Bookpipe challenge response is malformed");
        }
        const number = await this.solveChallenge(challenge);
        if (number === null) {
            throw new Error("Bookpipe challenge could not be solved");
        }
        return window.btoa(
            JSON.stringify({
                algorithm: challenge.algorithm || "SHA-256",
                challenge: challenge.challenge,
                number,
                salt: challenge.salt,
                signature: challenge.signature,
            })
        );
    }

    // Brute-force the secret number via WebCrypto (invisible to the customer,
    // subsecond on current phones). Chunked yields keep the modal responsive.
    async solveChallenge(challenge) {
        const encoder = new TextEncoder();
        const max = Number(challenge.maxnumber) > 0 ? Number(challenge.maxnumber) : 100000;
        const target = String(challenge.challenge).toLowerCase();
        for (let n = 0; n <= max; n += 1) {
            const digest = await window.crypto.subtle.digest("SHA-256", encoder.encode(`${challenge.salt}${n}`));
            const hex = Array.from(new Uint8Array(digest))
                .map((byte) => byte.toString(16).padStart(2, "0"))
                .join("");
            if (hex === target) return n;
            if (n % 2000 === 1999) {
                await this.yieldToMain();
            }
        }
        return null;
    }

    // Hands the main thread back between hash chunks without a timer. A hidden
    // tab gets one timer wake-up per second, and the solve starts as the wizard
    // opens — right when a customer may leave the page to fetch a photo (another
    // tab, another app). Behind setTimeout(0) the worst case took 47 s instead of
    // 0.6 s (Chrome 154, 29.09.2026), and the photo upload waits for the session
    // this token unlocks. Posted tasks are not throttled: scheduler.yield() where
    // the browser has it, else a MessageChannel message.
    yieldToMain() {
        if (typeof window.scheduler?.yield === "function") {
            return window.scheduler.yield();
        }
        return new Promise((resolve) => {
            const channel = new window.MessageChannel();
            channel.port1.onmessage = () => {
                channel.port1.close();
                resolve();
            };
            channel.port2.postMessage(null);
        });
    }

    // The product config is sessionStorage-cached for 10 minutes — after the
    // gate is enabled server-side, a stale config would keep saying
    // "disabled". Bust the cache and adopt the fresh challenge block without
    // re-rendering the step form.
    async refreshChallengeConfig() {
        try {
            const sku = this.fieldConfigSku || (await this.resolveActiveSku());
            if (!sku) return;
            window.sessionStorage.removeItem(this.productConfigCacheKey(sku));
            // Always a network round-trip (the cache was just dropped), and it
            // runs inside the unwatched inter-attempt window — bound it.
            const config = await this.loadFieldConfig(sku, this.challengeSignal());
            if (config && this.fieldConfig) {
                this.fieldConfig.challenge = config.challenge;
            } else if (config) {
                this.fieldConfig = config;
            }
        } catch (e) {
            // Best-effort: the challenge_required retries force-mint anyway.
        }
    }

    // Re-verify the CURRENT session after a challenge_required rejection:
    // POST a fresh solved token to the session's verify endpoint, which
    // stamps the existing server row human-verified in place — no rotation,
    // no draft-state loss. Keyed by the session's server public_id, so it
    // heals sessions resumed WITHOUT the original create idempotency key too
    // (edit-from-cart rebuilds local state from the cart line and drops it).
    async reverifySession() {
        const state = this.getSavedState() || {};
        const sessionId = this.sessionId || state["__bp_session_id"] || state["_bookpipe_personalization_id"];
        if (!sessionId) return false;
        try {
            const formData = new FormData();
            formData.append("challenge_token", await this.mintChallengeToken());

            const response = await (window.originalFetch || window.fetch)(
                this.appProxyUrl(`${this.bookpipePersonalizationUrl}/${encodeURIComponent(sessionId)}/verify`),
                {
                    method: "POST",
                    headers: this.sessionHeaders(),
                    body: formData,
                    signal: this.challengeSignal(),
                }
            );
            const data = await this.parseJsonResponse(response);
            return response.ok && Boolean(data?.human_verified);
        } catch (error) {
            console.error("PersonalizationModal: session re-verification failed", error);
            return false;
        }
    }

    // One heal per session at a time. Several cost-bearing calls can be in
    // flight together (a multi-photo product uploads in parallel), and each
    // 403 would otherwise mint, solve and verify its own challenge — N times
    // the PoW on the customer's device and N draws against the server's
    // issuance rate limit, where a 429 would fail that request's heal.
    healChallenge() {
        if (!this.challengeHealPromise) {
            this.challengeHealPromise = (async () => {
                await this.refreshChallengeConfig();
                return this.reverifySession();
            })().finally(() => {
                this.challengeHealPromise = null;
            });
        }
        return this.challengeHealPromise;
    }

    photoUploadFingerprint(file) {
        return `${file.name}:${file.size}:${file.lastModified}`;
    }

    // The multipart filename that goes over the wire. Bookpipe never reads it
    // (uploads are stored from contents + MIME), but the sandbox/prod host's
    // WAF hard-403s any multipart body whose Content-Disposition filename
    // contains header-delimiter characters (= ; ' " — verified 11.08.2026,
    // LiteSpeed block page before PHP). The wizard classified that 403 as a
    // transient hiccup, so the customer kept a photo the session never had
    // and the preview failed with "Bitte lade ein Foto hoch". Only the wire
    // name is sanitized — the dropzone keeps showing file.name.
    safeUploadFilename(file) {
        const type = String(file.type || "").toLowerCase();
        const extension = type === "image/png" ? ".png" : type === "image/webp" ? ".webp" : ".jpg";
        const stem = String(file.name || "")
            .replace(/\.[^.]*$/, "")
            .replace(/[^A-Za-z0-9._-]+/g, "_")
            .replace(/^[_.]+|[_.]+$/g, "")
            .slice(0, 64);
        return `${stem || "foto"}${extension}`;
    }

    // Fingerprints of photos the current session has verified, persisted in
    // the draft state so a reload/reopen doesn't re-run (and re-charge) the
    // AI check for an unchanged photo. Cleared with the session.
    getPersistedUploadedPhotoFingerprints() {
        const raw = this.getSavedState()?.["__bp_uploaded_photos"];
        return raw && typeof raw === "object" ? raw : {};
    }

    persistUploadedPhotoFingerprint(key, fingerprint) {
        this.saveState({
            __bp_uploaded_photos: { ...this.getPersistedUploadedPhotoFingerprints(), [key]: fingerprint },
        });
    }

    // The session lost the photo after all (a refused preview says so): its
    // fingerprint vouches for no upload any more - in memory, and in the draft,
    // which would vouch for it again after a reload.
    forgetUploadedPhoto(key) {
        this.sessionUploadedPhotos.delete(key);
        const persisted = { ...this.getPersistedUploadedPhotoFingerprints() };
        delete persisted[key];
        this.saveState({ __bp_uploaded_photos: persisted });
    }

    // True while `file` is still the photo the dropzone shows — verdicts of a
    // superseded upload (photo replaced mid-check) must not touch the UI.
    isCurrentPhotoFile(key, file) {
        const current = this.photoFiles.get(key);
        return !!current && this.photoUploadFingerprint(current) === this.photoUploadFingerprint(file);
    }

    async ensureSession() {
        if (this.sessionId) return this.sessionId;
        if (this.sessionPromise) return this.sessionPromise;
        const promise = this.createSession().finally(() => {
            // why: only its own — after a rotation while it ran, the pending
            // create is the replacing generation's, which callers must join.
            if (this.sessionPromise === promise) this.sessionPromise = null;
        });
        this.sessionPromise = promise;
        return promise;
    }

    async createSession() {
        // Per attempt, so a later unrelated failure never surfaces a stale
        // "confirm you are human" message.
        this.challengeMintFailed = false;
        // The session generation this create is for. Once a rotation replaced
        // it (two quick cover switches), the create leaves the draft alone — no
        // idempotency key of its own — and its session is never adopted: that
        // would bind the replacing generation to the other cover's session, in
        // memory and in the draft. Its callers get null and ask again, for the
        // current generation.
        const epoch = this.sessionEpoch;
        const replaced = () => epoch !== this.sessionEpoch;
        try {
            if (!this.fieldConfig && this.fieldConfigPromise) {
                await this.fieldConfigPromise;
            }

            const state = this.getSavedState() || {};
            // Resume the stored session (reload, edit-from-cart via the cart
            // line's personalization id) — the server 409s if it was ordered
            // in the meantime, which resets to a fresh draft.
            // Deliberately NOT re-stamping __bp_session_sku here: a resume
            // cannot know which SKU the stored session was created for, and
            // blessing it with the currently active SKU could bind the wizard
            // to a session created for the other cover. Legacy drafts without
            // the key rotate once — losslessly: values re-sync at preview,
            // photos re-upload via their fingerprints.
            const stored = state["__bp_session_id"] || state["_bookpipe_personalization_id"];
            if (stored) {
                this.sessionId = String(stored);
                // Photos already verified for this session (persisted across
                // reloads) don't need another upload round-trip or AI check.
                const persisted = this.getPersistedUploadedPhotoFingerprints();
                Object.keys(persisted).forEach((key) => {
                    if (typeof persisted[key] === "string" && persisted[key]) {
                        this.sessionUploadedPhotos.set(key, persisted[key]);
                    }
                });
                return this.sessionId;
            }

            const variantId = await this.getSelectedVariantId();
            if (replaced()) return null;
            const coverType = this.resolveCoverTypeForVariant(variantId);
            const shopifySku = this.resolveSkuForCoverType(coverType);
            if (!shopifySku) return null;

            const lineUuid = this.getOrCreateLineUuid();
            let idempotencyKey = state["__bp_session_key"];
            if (!idempotencyKey) {
                idempotencyKey = `session:${lineUuid}:${crypto.randomUUID()}`;
                this.saveState({ __bp_session_key: idempotencyKey });
            }

            let response;
            let data;
            let challengeRetried = false;

            while (true) {
                const formData = new FormData();
                formData.append("shopify_sku", shopifySku);
                formData.append("shopify_variant_id", String(variantId || ""));
                formData.append("external_session_id", this.getOrCreateCustomerSessionId());
                formData.append("idempotency_key", idempotencyKey);
                // On the retry the token is minted unconditionally — the
                // cached config that said "disabled" was just proven stale.
                if (this.getChallengeConfig().enabled || challengeRetried) {
                    try {
                        formData.append("challenge_token", await this.mintChallengeToken());
                    } catch (error) {
                        // A failed mint must NOT kill session creation. The
                        // cached config is up to 10 minutes stale, so the
                        // likeliest cause is that the gate was switched off
                        // server-side and /challenges now 404s — exactly the
                        // customers a kill-switch is meant to rescue. Post
                        // without a token: if the gate really is still on, the
                        // server answers challenge_required and the retry
                        // below force-mints once more.
                        console.warn("PersonalizationModal: challenge mint failed, creating without a token", error);
                        this.challengeMintFailed = true;
                        if (challengeRetried) {
                            // Already the forced retry — the server demands a
                            // token we cannot obtain. Surface it as such.
                            return null;
                        }
                    }
                }

                // A create that timed out ends like any failed one (null): the
                // next caller creates the session.
                response = await (window.originalFetch || window.fetch)(this.appProxyUrl(this.bookpipePersonalizationUrl), {
                    method: "POST",
                    headers: this.sessionHeaders(),
                    body: formData,
                    signal: this.timeoutSignal(PERSONALIZATION_SESSION_REQUEST_TIMEOUT_MS),
                });
                data = await this.parseJsonResponse(response);

                if (this.isChallengeRequired(response, data) && !challengeRetried) {
                    challengeRetried = true;
                    this.challengeMintFailed = false;
                    await this.refreshChallengeConfig();
                    continue;
                }

                break;
            }

            // Remember a persistent challenge rejection so the wizard can tell
            // the customer to reload rather than showing the generic
            // "preview could not be created".
            this.challengeMintFailed = this.challengeMintFailed || this.isChallengeRequired(response, data);

            if (response.ok && data?.personalization_id) {
                if (replaced()) return null;
                this.sessionId = String(data.personalization_id);
                this.challengeMintFailed = false;
                // A fresh session holds no uploads yet — stale fingerprints
                // from an earlier session must not suppress the re-upload.
                this.saveState({
                    __bp_session_id: this.sessionId,
                    __bp_session_sku: shopifySku,
                    __bp_uploaded_photos: null,
                });
                return this.sessionId;
            }

            // Transient failure: a later call (field sync, preview trigger)
            // probes again.
            console.error("PersonalizationModal: session creation was rejected", data?.message || response.status);
            return null;
        } catch (error) {
            console.error("PersonalizationModal: session creation failed", error);
            return null;
        }
    }

    // Runs a request against the session; a locked (already ordered) or
    // purged session is replaced by a fresh draft once and the request is
    // replayed against it. A challenge_required 403 (bot gate enabled while
    // this session was unverified) re-verifies the session once via the
    // idempotent create replay and then replays the request.
    // The request is a fetch unless its init brings a transport of its own
    // (the photo upload's, sendPhotoUpload), called like fetch.
    async sessionRequest(path, buildInit, retried = false, challengeRetried = false) {
        const epoch = this.sessionEpoch;
        const sessionId = await this.ensureSession();
        // A request whose generation was replaced while its session was being
        // resolved goes nowhere, like one that found no session: the session it
        // got is not the wizard's any more (an upload ends as called off).
        if (!sessionId || epoch !== this.sessionEpoch) return null;

        const init = buildInit();
        const send = init.transport || window.originalFetch || window.fetch;
        const response = await send(this.appProxyUrl(`${this.bookpipePersonalizationUrl}/${encodeURIComponent(sessionId)}${path}`), init);

        if ((response.status === 409 || response.status === 404) && !retried) {
            // No full re-sync after the rotation on purpose: when the failed
            // request IS the pre-preview full sync, its replay already
            // carries the complete state, and every preview run full-syncs
            // against the current session first anyway — a re-sync here would
            // need a recursion guard and could double-upload photos.
            this.resetSession();
            const fresh = await this.ensureSession();
            if (!fresh) return response;
            return this.sessionRequest(path, buildInit, true, challengeRetried);
        }

        if (response.status === 403 && !challengeRetried) {
            let code = null;
            try {
                // Peek via clone — the caller still parses the original body.
                code = (await response.clone().json())?.code;
            } catch (e) {
                // Not JSON: fall through and return the response as-is.
            }
            if (code === "challenge_required" && (await this.healChallenge())) {
                return this.sessionRequest(path, buildInit, retried, true);
            }
        }

        return response;
    }

    buildVariablesPatchInit(variables) {
        const formData = new FormData();
        // Shopify's app proxy reliably forwards POST; Laravel's method
        // spoofing turns it into the PATCH variables route.
        formData.append("_method", "PATCH");
        formData.append("variables", JSON.stringify(variables));
        return {
            method: "POST",
            headers: this.sessionHeaders(),
            body: formData,
            signal: this.timeoutSignal(PERSONALIZATION_SESSION_REQUEST_TIMEOUT_MS),
        };
    }

    // Debounced immediate sync of one text field (FR-PERS-16). Blank values
    // are reconciled by the full pre-preview sync instead — syncing them here
    // would nag "required" errors while the customer is still typing.
    queueFieldSync(key, value) {
        if (!key || !this.wizardOpened) return;
        const trimmed = String(value ?? "").trim();
        if (trimmed === "") return;
        window.clearTimeout(this.sessionSyncTimers.get(key));
        this.sessionSyncTimers.set(
            key,
            window.setTimeout(() => {
                this.sessionSyncTimers.delete(key);
                this.syncFieldsToSession({ [key]: trimmed });
            }, 600)
        );
    }

    async syncFieldsToSession(variables, options = {}) {
        try {
            const response = await this.sessionRequest("/variables", () => this.buildVariablesPatchInit(variables));
            if (!response) return { ok: true };
            const data = await this.parseJsonResponse(response);
            if (response.ok) return { ok: true, data };
            if (response.status === 422) {
                // Debounced single-field syncs stay silent (the client
                // mirrors the same rules); the pre-preview full sync surfaces
                // its errors through the preview error flow.
                return { ok: false, errorData: data };
            }
            return { ok: false, message: data?.message };
        } catch (error) {
            console.error("PersonalizationModal: variable sync failed", error);
            // Transient — the pre-preview sync retries with the full state.
            // Fail-closed callers (the add-to-cart reconcile) have NO later
            // retry: swallowing a thrown fetch there would let the order
            // print the session's stale variables.
            if (options.failClosed) {
                return { ok: false };
            }
            return { ok: true };
        }
    }

    // Upload-time photo validation (FR-KI-09/FR-WIZ-06): the photo goes to
    // bookpipe the moment it is selected; the AI verdict renders at the
    // dropzone long before any generation spend. While the photo goes out the
    // dropzone shows the share sent ("uploading"), then the "checking" state —
    // the success badge only ever renders a confirmed verdict. The upload
    // itself drives those states (sendPhotoToSession), for a sync's re-upload
    // as well; what follows here is the customer's own pick's: the funnel
    // events, and the refused photo explained at its field.
    queuePhotoUpload(key, file) {
        if (!this.wizardOpened) return;
        this.setPhotoCheckState(key, "uploading");
        return this.uploadPhotoToSession(key, file)
            .then((result) => {
                // A verdict of a replaced session (cover switch mid-check) is
                // that session's: the current one gets the photo through its
                // own upload, which owns the dropzone. Same file, so
                // isCurrentPhotoFile below cannot tell the two apart.
                if (result.epoch !== this.sessionEpoch) return result;
                // The customer may have replaced or removed the photo while
                // this check was in flight — never apply a stale verdict.
                if (!this.isCurrentPhotoFile(key, file)) return result;
                if (result.ok && result.verified) {
                    this.trackPersonalization("personalization_photo_verified", { photo_key: key });
                    // The photo reached the session after all (the dropzone's
                    // retry): a background pass's failure to send it, kept for
                    // the preview step, no longer holds there.
                    this.backgroundPreviewErrorSignature = null;
                }
                if (result.budgetExhausted || result.challengeFailed) {
                    // The photo itself is fine — the server refused for a
                    // reason that has nothing to do with it (drained vision
                    // budget, or a bot gate that would not heal) and both
                    // clear up. Keep the file, explain at the field, and
                    // withhold the verified badge (nothing was checked; the
                    // upload ended the claim).
                    this.applyFieldErrors(new Map([[key, { messages: result.messages, step: PERSONALIZATION_STEP_KEYS.CHARACTER }]]));
                } else if (!result.ok) {
                    // Any 422 verdict: the photo is not stored server-side —
                    // drop it client-side too and explain at the field.
                    // Verdicts that map to no field (content-sniffed MIME,
                    // unknown upload key) get a generic message; leaving the
                    // dropzone on "checking" forever is never an option.
                    const messages = result.messages.length
                        ? result.messages
                        : [this.getMessage("photo-check-failed", "Das Foto konnte nicht geprüft werden. Bitte versuche es mit einem anderen Foto.")];
                    // Funnel: the photo was refused and cleared — a mapped
                    // verdict is the AI gate, an unmapped 422 a generic check.
                    this.trackPersonalization("personalization_photo_rejected", {
                        photo_key: key,
                        reason: result.rejected ? "ai_check" : "check_failed",
                    });
                    this.refusePhoto(key, messages);
                }
                return result;
            })
            .catch((error) => {
                // Not the upload — that reports its own failure
                // (sendPhotoToSession) — but showing its verdict went wrong.
                console.error("PersonalizationModal: photo verdict could not be shown", error);
                if (this.isCurrentPhotoFile(key, file)) {
                    this.setPhotoCheckState(key, "none");
                }
                return { ok: true };
            });
    }

    // A refused photo - by bookpipe's check (queuePhotoUpload) or by the
    // browser (applyPhotoFile) - is dropped like a removed one and explained at
    // its field. The customer who went on meanwhile reads why on her step,
    // never by being taken off it.
    refusePhoto(key, messages) {
        this.clearPhoto(key);
        this.applyFieldErrors(new Map([[key, { messages, step: PERSONALIZATION_STEP_KEYS.CHARACTER }]]));
        const activeStepId = this.getActiveStepId();
        if (activeStepId !== this.characterStepId()) {
            this.showPhotoRejectionNotice(activeStepId, messages);
        }
    }

    // The uploads still on their way to the current session. One that goes to a
    // replaced session (cover switch mid-upload) can never reach this one.
    currentSessionPhotoUploads() {
        return [...(this.pendingPhotoUploads ?? [])].filter((upload) => upload.epoch === this.sessionEpoch);
    }

    // Calls off the uploads `which` picks: the request of each one's current
    // attempt is cancelled (its init's signal), and the upload ends there.
    abortPhotoUploads(which) {
        [...(this.pendingPhotoUploads ?? [])].filter(which).forEach((upload) => upload.controller?.abort());
    }

    // Every caller that sends a photo comes through here — the customer's pick
    // (queuePhotoUpload), the pre-preview full sync, the add-to-cart sync — and
    // joins an upload of the same photo that is still on its way instead of
    // sending it again: a parallel second upload of the same file doubles the
    // wait on a slow uplink ("Weiter" while the photo still uploads).
    uploadPhotoToSession(key, file) {
        const fingerprint = this.photoUploadFingerprint(file);
        const running = this.currentSessionPhotoUploads().find((upload) => upload.key === key && upload.fingerprint === fingerprint);
        if (running) return running.promise;
        const upload = { epoch: this.sessionEpoch, key, fingerprint, progress: 0, requests: 0, startedAt: Date.now(), promise: null };
        // why: lazily — the node tests assemble these methods without running the constructor.
        this.pendingPhotoUploads ??= new Set();
        this.pendingPhotoUploads.add(upload);
        upload.promise = this.sendPhotoToSession(upload, file)
            // The session epoch the verdict belongs to: whoever acts on it must
            // leave the verdict of a replaced session alone.
            .then((result) => ({ ...result, epoch: upload.epoch }))
            .finally(() => this.pendingPhotoUploads.delete(upload));
        return upload.promise;
    }

    // One upload of the photo and its verdict; callers go through uploadPhotoToSession.
    async sendPhotoToSession(upload, file) {
        const { key } = upload;
        // The dropzone follows every upload of the photo it shows to the session
        // the wizard uses - the customer's pick and a sync's re-upload alike
        // (the funnel counts the pick alone: queuePhotoUpload).
        const show = (state, detail) => {
            if (upload.epoch === this.sessionEpoch && this.isCurrentPhotoFile(key, file)) {
                this.setPhotoCheckState(key, state, detail);
            }
        };
        // The photo stays, without a verdict: the pre-preview full sync sends it again.
        const unverified = (flags = {}) => ({ ok: true, verified: false, rejected: false, messages: [], ...flags });
        // Gone for good — no session to send it to, the transport gave up
        // after its retry, or an answer that is no verdict came back: `phase`
        // says where ("session", "send", "verdict", "network"), `status` is the
        // answer's (null without one). `ok` stays true, like any photo without
        // a verdict: the add-to-cart sync reads the upload as before.
        // Reported once, here (createSession reports a missing session), never
        // with the file's name. why an Error: the Sentry mirror groups an Error
        // by its message but writes plain objects into the message text, and
        // every duration would be an issue of its own.
        const failed = (phase, { status = null, error = null, ...flags } = {}) => {
            if (phase !== "session") {
                const grouping = error || new Error(`PersonalizationModal: photo upload failed: ${phase}`);
                console.error("PersonalizationModal: photo upload failed", grouping, {
                    status,
                    phase,
                    ms: Date.now() - upload.startedAt,
                    bytes: file.size,
                    attempt: upload.requests,
                });
            }
            // The photo stays at the dropzone, with a retry that sends it again.
            show("failed", { challenge: Boolean(flags.challenge) });
            return unverified({ failed: true, phase, status, ...flags });
        };
        // A verdict that refuses the photo ends the dropzone's claim; whoever
        // waits for it explains why - the customer's pick at the field, the
        // preview pass through its field errors.
        const refused = (verdict) => {
            show("none");
            return verdict;
        };
        try {
            const response = await this.sessionRequest("/uploads", () => {
                // why: per attempt — a 409/404 makes sessionRequest replace the
                // session and send this upload again, to the replacing session,
                // whose verdict it then is. The replacement calls off what goes
                // to the replaced session (abortPhotoUploads), never this replay:
                // it gets a controller of its own.
                upload.epoch = this.sessionEpoch;
                upload.controller = new AbortController();
                const formData = new FormData();
                formData.append("field", key);
                formData.append("images[]", file, this.safeUploadFilename(file));
                return {
                    method: "POST",
                    headers: this.sessionHeaders(),
                    body: formData,
                    signal: upload.controller.signal,
                    transport: (url, init) =>
                        this.sendPhotoUpload(url, init, {
                            // Every request of the photo - the transport's
                            // retry, too - sends it from its first byte.
                            onSend: () => {
                                upload.requests += 1;
                                upload.progress = 0;
                                show("uploading");
                            },
                            onProgress: (share) => {
                                upload.progress = share;
                                // The whole photo is out: bookpipe checks it now.
                                show(share >= 1 ? "checking" : "uploading", { share });
                            },
                        }),
                };
            });
            if (!response) {
                // Its generation was replaced while the photo waited for its
                // session: called off with it, like an upload on its way.
                if (upload.epoch !== this.sessionEpoch) return unverified({ aborted: true });
                // No session to send the photo to: createSession reported why.
                // The bot gate that keeps refusing it is named — a reload is the
                // remedy then, not the connection.
                return failed("session", this.challengeMintFailed ? { challenge: true } : {});
            }

            const data = await this.parseJsonResponse(response);

            if (response.ok) {
                // A verdict that raced a session rotation belongs to the old
                // session: recording it would suppress the re-upload to the
                // live one, which would then generate without the photo.
                if (upload.epoch !== this.sessionEpoch) {
                    return unverified();
                }
                this.sessionUploadedPhotos.set(key, upload.fingerprint);
                this.persistUploadedPhotoFingerprint(key, upload.fingerprint);
                if (this.isCurrentPhotoFile(key, file)) {
                    this.clearFieldError(key);
                    this.setPhotoCheckState(key, "verified");
                }
                return { ok: true, verified: true, rejected: false, messages: [] };
            }

            if (response.status === 422) {
                const fieldErrors = this.extractPreviewFieldErrors(data);
                const fieldEntry = fieldErrors?.fields?.get(key);
                return refused({
                    ok: false,
                    verified: false,
                    rejected: Boolean(fieldEntry),
                    messages: fieldEntry?.messages || [],
                    errorData: data,
                });
            }

            if (response.status === 429) {
                // Exhausted vision budget (per-session or org-wide). The photo
                // was NOT stored, so treating this as a transient hiccup would
                // let the preview run against a session missing the photo and
                // fail with an error pinned to a field the customer sees as
                // filled. Surface the backend's German message at the field
                // instead — and keep the photo, since the limit resets.
                const message =
                    data?.message ||
                    this.getMessage("photo-check-failed", "Das Foto konnte nicht geprüft werden. Bitte versuche es mit einem anderen Foto.");
                return refused({
                    ok: false,
                    verified: false,
                    rejected: false,
                    budgetExhausted: true,
                    messages: [message],
                    // syncAllToSession's caller reads the singular `message`.
                    message,
                    errorData: data,
                });
            }

            if (this.isChallengeRequired(response, data)) {
                // sessionRequest already tried to heal this once and failed,
                // so the bot gate is genuinely blocking. Uploads are the first
                // gated endpoint the wizard touches, which makes step 1 where
                // the problem is DETECTED — treating it as a transient hiccup
                // hid it until the preview failed on step 3, after the
                // customer had filled in everything.
                const message = this.getMessage(
                    "challenge-failed",
                    "Deine Anfrage konnte nicht als menschlich bestätigt werden. Bitte lade die Seite neu und versuche es erneut."
                );
                return refused({
                    ok: false,
                    verified: false,
                    rejected: false,
                    challengeFailed: true,
                    messages: [message],
                    // syncAllToSession's caller reads the singular `message`.
                    message,
                    errorData: data,
                });
            }

            // An answer that is no verdict (a server error, the app proxy's
            // error page, a session gone even after its replacement): keep the
            // photo; the preview trigger re-validates through the full sync.
            return failed("verdict", { status: response.status });
        } catch (error) {
            // Called off by the wizard (its session replaced, its photo
            // removed): no failure, and nothing to report — else every cover
            // switch during an upload would land in Sentry. why the signal, not
            // the error: a failure that was still settling when the wizard
            // called the upload off is moot all the same.
            if (upload.controller?.signal.aborted) {
                return unverified({ aborted: true });
            }
            // No answer at all: a stall — before the last byte of the photo
            // went out, or while it waited for its verdict — or a dropped
            // connection.
            if (error?.name === "TimeoutError") return failed(upload.progress >= 1 ? "verdict" : "send", { error });
            return failed("network", { error });
        }
    }

    // The photo upload's transport (the init's transport in sendPhotoToSession):
    // called like fetch, settling like it, with onSend called as each request
    // goes out and onProgress getting the share of its body sent (0–1) whenever
    // bytes went out. A stalled or dropped upload goes out once more. Nothing
    // else does: not an HTTP answer, whatever its status - that is bookpipe's
    // (or the proxy's) word, and the caller handles it as before - and not an
    // upload the wizard called off (init.signal).
    async sendPhotoUpload(url, init, options = {}) {
        try {
            return await this.sendPhotoUploadOnce(url, init, options);
        } catch (error) {
            if (error?.name !== "TimeoutError" && !isPersonalizationNetworkError(error)) throw error;
            return this.sendPhotoUploadOnce(url, init, options);
        }
    }

    // One request of the photo upload, over XMLHttpRequest: fetch reports no
    // upload progress. It rejects like fetch - a TypeError when no HTTP answer
    // came back, an AbortError when init.signal called it off - and with a
    // TimeoutError once it stalled: no byte went out for
    // PERSONALIZATION_PHOTO_UPLOAD_STALL_MS, or - the whole photo sent - no
    // answer came for PERSONALIZATION_PHOTO_UPLOAD_VERDICT_MS.
    sendPhotoUploadOnce(url, init, { onSend, onProgress } = {}) {
        return new Promise((resolve, reject) => {
            const { signal } = init;
            const wizardAbort = () => new DOMException("Photo upload aborted", "AbortError");
            if (signal?.aborted) {
                reject(wizardAbort());
                return;
            }
            const xhr = new window.XMLHttpRequest();
            const dropped = (how) => new TypeError(`Photo upload failed: ${how}`);
            let stalled = false;
            const stallClock = this.photoUploadStallClock(() => {
                // why: abort() is the wizard's own call here too - the flag
                // tells the abort event that it was a stall.
                stalled = true;
                xhr.abort();
            });
            const callOff = () => xhr.abort();
            // The request is over: nothing may call it off any more.
            const end = (outcome) => {
                stallClock.stop();
                signal?.removeEventListener("abort", callOff);
                // why: inside an event handler a throw would escape the
                // promise, and with the stall clock stopped nothing would ever
                // settle the upload.
                try {
                    outcome();
                } catch (error) {
                    reject(error);
                }
            };
            xhr.upload.onprogress = (event) => {
                stallClock.restart();
                // why: a browser that cannot tell the body's length reports a
                // total of 0 - bytes went out, but no share of the photo.
                const share = event.loaded / event.total;
                if (Number.isFinite(share)) onProgress?.(Math.min(Math.max(share, 0), 1));
            };
            xhr.upload.onload = () => stallClock.awaitVerdict();
            xhr.onload = () =>
                end(() => {
                    if (xhr.status === 0) reject(dropped("the connection dropped"));
                    else resolve(this.photoUploadResponse(xhr, url));
                });
            xhr.onerror = () => end(() => reject(dropped("the connection dropped")));
            // Three kinds of abort: the stall's, the wizard's own through its
            // signal (final) and one nobody here asked for - a WebView or the
            // page lifecycle cancelling the request, which is a dropped
            // connection like any other.
            xhr.onabort = () =>
                end(() => {
                    if (stalled) reject(new DOMException("Photo upload stalled", "TimeoutError"));
                    else if (signal?.aborted) reject(wizardAbort());
                    else reject(dropped("the browser cancelled the request"));
                });
            xhr.open(init.method, url);
            Object.entries(init.headers).forEach(([name, value]) => xhr.setRequestHeader(name, value));
            signal?.addEventListener("abort", callOff);
            onSend?.();
            xhr.send(init.body);
        });
    }

    // The clock that tells a stalled photo upload: onStall runs once no sign of
    // life came for PERSONALIZATION_PHOTO_UPLOAD_STALL_MS - after awaitVerdict()
    // (the whole photo is out) for PERSONALIZATION_PHOTO_UPLOAD_VERDICT_MS.
    // Every restart() (bytes went out) and the customer coming back to the page
    // start it afresh; stop() once the request is over.
    photoUploadStallClock(onStall) {
        let wait = PERSONALIZATION_PHOTO_UPLOAD_STALL_MS;
        let timer = null;
        let dueAt = 0;
        const restart = () => {
            window.clearTimeout(timer);
            dueAt = Date.now() + wait;
            timer = window.setTimeout(() => {
                // why: a page the browser froze (iOS on every app switch,
                // Android after minutes in the background) fires its overdue
                // timers at once on its return - before the progress the upload
                // made meanwhile. A timer that far behind its time measured no
                // silence: the clock starts afresh.
                if (Date.now() - dueAt > PERSONALIZATION_PHOTO_UPLOAD_FROZEN_MS) restart();
                else onStall();
            }, wait);
        };
        const restartWhenVisible = () => {
            if (document.visibilityState === "visible") restart();
        };
        // why: only where there is a page - the node harnesses run this
        // without a document and with a window that takes no listeners.
        const watchesPage = typeof document !== "undefined" && typeof window.addEventListener === "function";
        if (watchesPage) {
            document.addEventListener("visibilitychange", restartWhenVisible);
            window.addEventListener("pageshow", restart);
        }
        restart();
        return {
            restart,
            awaitVerdict() {
                wait = PERSONALIZATION_PHOTO_UPLOAD_VERDICT_MS;
                restart();
            },
            stop() {
                window.clearTimeout(timer);
                if (watchesPage) {
                    document.removeEventListener("visibilitychange", restartWhenVisible);
                    window.removeEventListener("pageshow", restart);
                }
            },
        };
    }

    // The upload's answer as the Response fetch resolves with, so that
    // sessionRequest, parseJsonResponse and buildRequestError read it like
    // the answer to any other request.
    photoUploadResponse(xhr, url) {
        const headers = new Headers();
        xhr.getAllResponseHeaders()
            .split("\r\n")
            .forEach((line) => {
                const separator = line.indexOf(":");
                if (separator > 0) headers.append(line.slice(0, separator).trim(), line.slice(separator + 1).trim());
            });
        // A status that carries no body (204, 205, 304) gets none: a Response refuses one.
        const body = [204, 205, 304].includes(xhr.status) ? null : xhr.response;
        const response = new Response(body, { status: xhr.status, headers });
        // why: a Response built here has no URL, and parseJsonResponse names it
        // when the app proxy answered with an error page instead of JSON.
        Object.defineProperty(response, "url", { value: xhr.responseURL || url });
        return response;
    }

    // Every configured text field as the session stores it: absent optional
    // values go up as "" so the session clears them and the Standardwerte apply.
    buildTextFieldsPayload(entries) {
        const variables = this.buildBookpipeVariables(entries);
        const config = this.fieldConfig || this.buildFallbackFieldConfig();
        const payload = {};
        config.textFields.forEach((field) => {
            if (field.inputType && field.inputType !== "text" && field.inputType !== "textarea") return;
            payload[field.key] = Object.prototype.hasOwnProperty.call(variables, field.key)
                ? String(variables[field.key])
                : "";
        });
        return payload;
    }

    // The text fields alone, for the audiobook name sample: bookpipe speaks the
    // name the SESSION holds, and nothing else of the full sync concerns it — a
    // photo still uploading must neither hold the sample up nor go up twice.
    syncTextFieldsToSession() {
        return this.syncFieldsToSession(this.buildTextFieldsPayload(this.collectEntries()));
    }

    // The plain (option-less) full sync, shared by concurrent preview passes (a
    // prefetch still syncing when the preview step's pass takes over): two full
    // syncs in parallel would race each other's variables PATCH. Shared within
    // the session it started for only: after a rotation (cover switch) the
    // pending sync still serves the replaced session, and a replacing session
    // that awaited it would be previewed without ever receiving name, hash and
    // photo (422).
    sharedSyncAllToSession() {
        if (this.pendingFullSync?.epoch === this.sessionEpoch) return this.pendingFullSync.promise;
        const pending = { epoch: this.sessionEpoch, promise: null };
        pending.promise = this.syncAllToSession().finally(() => {
            if (this.pendingFullSync === pending) this.pendingFullSync = null;
        });
        this.pendingFullSync = pending;
        return pending.promise;
    }

    // Full state reconciliation before a preview run: every configured text
    // field is upserted (absent optional values are cleared so Standardwerte
    // apply), and photos that never reached this session are uploaded.
    // options.entries: a pre-collected form snapshot, so a caller that also
    // builds a payload from the form (add-to-cart) works off the SAME state
    // the session received — edits landing mid-flight hit neither or both.
    // options.failClosed: treat a thrown variables PATCH as a failure.
    // A sync whose session was replaced while it ran (cover switch, 409/404
    // replay) may have sent the name to one session and the photo to the next:
    // its result is flagged stale instead of vouching for the replacing one.
    // `ok` stays as it was, so callers that read only `ok` (the fail-closed
    // add-to-cart sync) still stop on a refusal.
    // A photo whose upload gave up for good (its `failed`) is named in
    // `undeliveredPhotos`, `ok` untouched: the preview pass does not post
    // without it, while the add-to-cart sync goes on as before - the ready
    // preview proves the session had the photo.
    async syncAllToSession(options = {}) {
        const epoch = this.sessionEpoch;
        const staleIfReplaced = (result) => (epoch === this.sessionEpoch ? result : { ...result, stale: true });
        const sessionId = await this.ensureSession();
        if (!sessionId) return null;

        this.sessionSyncTimers.forEach((timer) => window.clearTimeout(timer));
        this.sessionSyncTimers.clear();

        const entries = options.entries || this.collectEntries();
        const payload = this.buildTextFieldsPayload(entries);
        const syncResult = await this.syncFieldsToSession(payload, { failClosed: options.failClosed === true });
        if (!syncResult.ok) return staleIfReplaced(syncResult);

        // A photo still being prepared goes up as prepared (its own upload,
        // which the loop below joins).
        await this.whenPhotosPrepared();
        const undeliveredPhotos = [];
        for (const [key, file] of this.photoFiles) {
            const fingerprint = this.photoUploadFingerprint(file);
            if (this.sessionUploadedPhotos.get(key) === fingerprint) continue;
            // Same parity as setPhoto's alreadyVerified: a fingerprint the
            // session already verified (persisted across reloads) needs no
            // upload round-trip — re-sending it would burn a budget unit and,
            // pre-server-fix, wiped the session's generated outputs.
            if (this.getPersistedUploadedPhotoFingerprints()[key] === fingerprint) {
                this.sessionUploadedPhotos.set(key, fingerprint);
                continue;
            }
            const uploadResult = await this.uploadPhotoToSession(key, file);
            if (!uploadResult.ok) return staleIfReplaced(uploadResult);
            if (uploadResult.failed) undeliveredPhotos.push(key);
        }

        return staleIfReplaced(undeliveredPhotos.length > 0 ? { ok: true, undeliveredPhotos } : { ok: true });
    }

    // ------------------------------------------------------------------
    // Photo upload (step 1)
    // ------------------------------------------------------------------

    // Photo interactions are delegated on the modal root: the dropzones are
    // rendered (and re-rendered) from the field config, so direct bindings
    // would go stale on every render.
    bindPhotoControls() {
        const root = this.getModalRoot();
        this.photoDelegationRoot = root;
        root.addEventListener("change", this.boundHandlePhotoInputChange);
        // why the capture phase: the spec has the input's cancel bubble, but a
        // WebView firing it without would pass a listener of the bubble phase
        // by - and the picker the customer closed would read as one that came
        // back empty.
        root.addEventListener("cancel", this.boundHandlePhotoInputCancel, { capture: true });
        root.addEventListener("click", this.boundHandlePhotoAreaClick);
        root.addEventListener("dragover", this.boundHandleDragOver);
        root.addEventListener("dragleave", this.boundHandleDragLeave);
        root.addEventListener("drop", this.boundHandleDrop);
    }

    unbindPhotoControls() {
        // The wizard leaving the page (disconnectedCallback) ends the watch of
        // its photo picker as well, and with it the page's listeners and timers.
        this.endPhotoPickerWatch();
        const root = this.photoDelegationRoot;
        if (!root) return;
        root.removeEventListener("change", this.boundHandlePhotoInputChange);
        root.removeEventListener("cancel", this.boundHandlePhotoInputCancel, { capture: true });
        root.removeEventListener("click", this.boundHandlePhotoAreaClick);
        root.removeEventListener("dragover", this.boundHandleDragOver);
        root.removeEventListener("dragleave", this.boundHandleDragLeave);
        root.removeEventListener("drop", this.boundHandleDrop);
        this.photoDelegationRoot = null;
    }

    resolvePhotoKey(element) {
        return element?.closest?.("[data-photo-dropzone]")?.getAttribute("data-photo-key") || "";
    }

    // The key of the photo field `element` sits in - below its dropzone too,
    // as the hint's buttons do, where resolvePhotoKey finds none.
    resolvePhotoFieldKey(element) {
        return element?.closest?.("[data-field-wrapper]")?.getAttribute("data-field-key") || "";
    }

    // Whether the field holds a photo: one applied, or a pick still being
    // prepared - the dropzone shows it from the pick on.
    hasPhoto(key) {
        return this.photoFiles.has(key) || Boolean(this.photoPreparations?.has(key));
    }

    // The required photo fields without a photo: none picked, or the one picked
    // refused and dropped. A pick still being prepared is there (hasPhoto).
    missingRequiredPhotos() {
        return this.getUploadFields().filter((field) => field.required && !this.hasPhoto(field.key));
    }

    // What the photo field `field` asks for while it holds no photo.
    photoRequiredMessage(field) {
        return field.key === this.bookpipePhotoKey
            ? this.getMessage("photo-required", "Bitte lade ein Foto deines Kindes hoch.")
            : this.formatFieldMessage("photo-required-labeled", field, 'Bitte lade ein Foto hoch: „{label}“.');
    }

    // The drawer's taps on the remove and retry buttons of a filled dropzone,
    // and on the buttons of the hint below it (showPhotoPickerHint). A tap on
    // an empty dropzone is its label's - the browser opens the picker for the
    // input itself - and passes here untouched: cancelling it, or the click
    // the label then sends the input, would keep the picker shut. That click is
    // only watched, in the in-app browsers that at times open no picker for it
    // (watchPhotoPickerTap).
    handlePhotoAreaClick(event) {
        if (!(event.target instanceof Element)) return;

        const removeButton = event.target.closest("[data-photo-remove]");
        if (removeButton) {
            const key = this.resolvePhotoKey(removeButton);
            if (!key) return;
            event.preventDefault();
            event.stopPropagation();
            this.clearPhoto(key);
            return;
        }

        // The upload gave up for good: the photo the dropzone still shows goes
        // out once more.
        const retryButton = event.target.closest("[data-photo-retry]");
        if (retryButton) {
            const key = this.resolvePhotoKey(retryButton);
            const file = this.photoFiles.get(key);
            if (!file) return;
            event.preventDefault();
            event.stopPropagation();
            this.queuePhotoUpload(key, file);
            return;
        }

        // The hint's „Im Browser öffnen“, below the dropzone of its field.
        const openButton = event.target.closest("[data-photo-picker-open-external]");
        if (openButton) {
            this.openPhotoPickerInBrowser(this.resolvePhotoFieldKey(openButton));
            return;
        }

        // The hint's „Link kopieren“, below the dropzone of its field.
        const copyButton = event.target.closest("[data-photo-picker-copy]");
        if (copyButton) {
            this.copyPhotoPickerLink(this.resolvePhotoFieldKey(copyButton));
            return;
        }

        // The click the label forwards to its input - or the keyboard's Space
        // on it - which some in-app browsers answer with no picker.
        if (event.target.matches("[data-photo-input]")) {
            this.watchPhotoPickerTap(this.resolvePhotoKey(event.target));
        }
    }

    // The picker's answer, tagged whether its watch still runs or not: one that
    // comes after the return's grace shows that grace too short.
    handlePhotoInputChange(event) {
        const input = event.target;
        if (!(input instanceof Element) || !input.matches("[data-photo-input]")) return;
        const key = this.resolvePhotoKey(input);
        const file = input.files?.[0];
        if (file && key) {
            this.tagPhotoPickerWay("file");
            this.preparePickedPhoto(key, file);
        } else if (key) {
            // A change without a file: the picker came back, but empty.
            this.tagPhotoPickerWay("file_empty");
            this.photoPickerReturnedEmpty(key);
        }
        // Allow re-selecting the same file after removal.
        input.value = "";
    }

    // The browser's word that the customer closed the picker of a photo input
    // without a photo: the picker works. The watch of its field ends, and no
    // hint follows - a cancel at another field's input says nothing about the
    // picker watched. Tagged for a photo input alone - the drawer hears every
    // cancel in it, a dialog's too - and before the watch's guard: one that
    // comes after the watch ended shows that the hint came too early.
    handlePhotoInputCancel(event) {
        if (event.target instanceof Element && event.target.matches("[data-photo-input]")) this.tagPhotoPickerWay("cancel");
        if (this.photoPickerWatch?.key !== this.resolvePhotoKey(event.target)) return;
        this.endPhotoPickerWatch();
    }

    // The customer's pick goes on as the browser prepared it
    // (preparePhotoForUpload) - nothing of it is applied, stored or sent
    // before. Yet the photo is there from the pick on: the dropzone shows it,
    // on its way up at 0 %. A newer pick for the field, its remove button and
    // a fresh draft drop the preparation: one that ends after that ends unseen.
    preparePickedPhoto(key, file) {
        // why: lazily — the node tests assemble these methods without running the constructor.
        this.photoPreparations ??= new Map();
        const preparation = { file, promise: null };
        this.photoPreparations.set(key, preparation);
        this.setPhotoCheckState(key, "uploading");
        this.renderPhotoState(key);
        // What was said about an earlier photo (or a missing one) is moot now,
        // and so is the way out of a picker that opened nothing: the photo is
        // here, and no tap is watched any more.
        this.clearFieldError(key);
        this.clearErrors();
        this.endPhotoPickerWatch();
        this.hidePhotoPickerHint(key);
        preparation.promise = this.preparePhotoForUpload(file).then((result) => {
            if (this.photoPreparations.get(key) !== preparation) return;
            this.photoPreparations.delete(key);
            this.applyPhotoFile(key, result.file, { picked: file, reason: result.reason });
        });
        return preparation.promise;
    }

    // Resolves once no pick is being prepared any more - each went on
    // (applyPhotoFile) or was refused - so whoever reads the photos then reads
    // prepared ones, never the original and never "no photo" for a photo the
    // dropzone shows. A pick made meanwhile is waited for too. Each wait is
    // bounded by the preparation's own PREPARE_TIMEOUT_MS.
    async whenPhotosPrepared() {
        while (this.photoPreparations?.size) {
            await Promise.all([...this.photoPreparations.values()].map((preparation) => preparation.promise));
        }
    }

    handleDragOver(event) {
        const dropzone = event.target instanceof Element ? event.target.closest("[data-photo-dropzone]") : null;
        if (!dropzone) return;
        // Still cancelled when the dropzone is filled — the browser would
        // otherwise navigate to the dropped file — but the highlight only
        // invites a drop while the field is empty (remove first, then upload).
        event.preventDefault();
        if (this.hasPhoto(this.resolvePhotoKey(dropzone))) return;
        dropzone.classList.add("is-dragover");
    }

    handleDragLeave(event) {
        const dropzone = event.target instanceof Element ? event.target.closest("[data-photo-dropzone]") : null;
        dropzone?.classList.remove("is-dragover");
    }

    handleDrop(event) {
        const dropzone = event.target instanceof Element ? event.target.closest("[data-photo-dropzone]") : null;
        if (!dropzone) return;
        event.preventDefault();
        dropzone.classList.remove("is-dragover");
        const key = dropzone.getAttribute("data-photo-key");
        if (this.hasPhoto(key)) return;
        const file = event.dataTransfer?.files?.[0];
        if (file && key) {
            this.preparePickedPhoto(key, file);
        }
    }

    // A stage of the photo picker's way in Facebook's or Instagram's in-app
    // browser on Android, as a tag of the Clarity session - never an event:
    // Clarity takes no more names (PERSONALIZATION_CLARITY_EVENT_NAMES).
    // pers_picker collects the stages - tap, opened, cancel, file, file_empty,
    // stuck, external and copied - each at most once per page view, and the
    // first names the app in pers_inapp as well. why there too, not only at
    // the wizard's open (pushPersonalizationClarity): a Clarity that loaded
    // after the open - the consent given meanwhile - missed that tag. Every
    // other browser goes the picker's way untagged.
    tagPhotoPickerWay(stage) {
        const inApp = personalizationAndroidInAppBrowser(window.navigator?.userAgent ?? "");
        if (!inApp) return;
        // why: lazily — the node tests assemble these methods without running the constructor.
        this.photoPickerStagesTagged ??= new Set();
        if (this.photoPickerStagesTagged.has(stage)) return;
        if (this.photoPickerStagesTagged.size === 0) setPersonalizationClarityTag("pers_inapp", inApp);
        this.photoPickerStagesTagged.add(stage);
        setPersonalizationClarityTag("pers_picker", stage);
    }

    // A tap on the photo input of `key`. Facebook's and Instagram's in-app
    // browsers on Android at times open no picker for it (#3972, #3920), so
    // there the page is watched for the picker taking its focus.
    watchPhotoPickerTap(key) {
        if (!personalizationAndroidInAppBrowser(window.navigator?.userAgent ?? "")) return;
        this.tagPhotoPickerWay("tap");
        const watch = this.photoPickerWatch?.key === key ? this.photoPickerWatch : this.startPhotoPickerWatch(key);
        watch.taps += 1;
        if (watch.taps >= PERSONALIZATION_PHOTO_PICKER_STUCK_TAPS) {
            this.photoPickerStayedShut(key, "second_tap");
            return;
        }
        // One open timer per watch, so that a leave and the watch's end can call it off.
        window.clearTimeout(watch.openTimer);
        watch.openTimer = window.setTimeout(() => this.photoPickerStayedShut(key, "no_open"), PERSONALIZATION_PHOTO_PICKER_OPEN_GRACE_MS);
    }

    // One watch at a time (photoPickerWatch, none before a first tap): the
    // field tapped, its taps, whether the page left for its picker, the timers
    // of its last tap and of the page's return, and the page's listeners for
    // the page losing its focus and getting it back, which exist only while
    // it runs - endPhotoPickerWatch removes everything it added. A tap on
    // another photo field ends the one running, quietly.
    startPhotoPickerWatch(key) {
        this.endPhotoPickerWatch();
        // The page's own blur, focus and visibilitychange only. why:
        // element-level blur and focus do not bubble, but a listener in the
        // capture phase on the window sees them pass - at every tap the name
        // field blurs and the photo input takes the focus - and read as the
        // page's own, they would pass for a picker opening and closing at
        // every tap. Hence no capture, and the target checked.
        const leaveOnBlur = (event) => {
            if (event.target === window) this.handlePhotoPickerLeave();
        };
        const returnOnFocus = (event) => {
            if (event.target === window) this.handlePhotoPickerReturn();
        };
        const followVisibility = (event) => {
            if (event.target !== document) return;
            if (document.visibilityState === "hidden") this.handlePhotoPickerLeave();
            if (document.visibilityState === "visible") this.handlePhotoPickerReturn();
        };
        const listeners = [
            [window, "blur", leaveOnBlur],
            [window, "focus", returnOnFocus],
            [document, "visibilitychange", followVisibility],
        ];
        listeners.forEach(([target, type, listener]) => target.addEventListener(type, listener));
        this.photoPickerWatch = { key, taps: 0, left: false, openTimer: null, returnTimer: null, listeners };
        return this.photoPickerWatch;
    }

    endPhotoPickerWatch() {
        const watch = this.photoPickerWatch;
        if (!watch) return;
        this.photoPickerWatch = null;
        window.clearTimeout(watch.openTimer);
        window.clearTimeout(watch.returnTimer);
        watch.listeners.forEach(([target, type, listener]) => target.removeEventListener(type, listener));
    }

    // The page lost its focus, or went out of view: the picker opened. Its
    // taps count afresh, and the tap's timer is moot - as is the page's return
    // just before, when it leaves again: a prompt to let the app read the
    // photos can come and go before the picker opens.
    handlePhotoPickerLeave() {
        this.tagPhotoPickerWay("opened");
        const watch = this.photoPickerWatch;
        watch.taps = 0;
        watch.left = true;
        window.clearTimeout(watch.openTimer);
        window.clearTimeout(watch.returnTimer);
    }

    // The page took its focus back, or came back into view, after it left:
    // the picker closed, and unless its photo or the browser's cancel follows
    // within RETURN_GRACE_MS, it came back empty. A return the page never left
    // for - a stray focus, or the second of the two signals of one return - is
    // none.
    handlePhotoPickerReturn() {
        const watch = this.photoPickerWatch;
        if (!watch.left) return;
        watch.left = false;
        watch.returnTimer = window.setTimeout(() => this.photoPickerReturnedEmpty(watch.key), PERSONALIZATION_PHOTO_PICKER_RETURN_GRACE_MS);
    }

    // The picker of `key` came back without a photo: the page came back from
    // it and none followed (handlePhotoPickerReturn), or its input reported a
    // change without a file. Only the field watched shows the way out.
    photoPickerReturnedEmpty(key) {
        if (this.photoPickerWatch?.key !== key) return;
        this.photoPickerStayedShut(key, "returned_empty");
    }

    // The watched picker stayed shut, or came back empty: the watch is over,
    // and the field shows the way out.
    photoPickerStayedShut(key, reason) {
        this.endPhotoPickerWatch();
        this.showPhotoPickerHint(key, reason);
    }

    // The way out under the photo field of `key`, which every rule that found
    // its picker shut shows through here - `reason` names the rule: second_tap,
    // no_open or returned_empty.
    showPhotoPickerHint(key, reason) {
        this.getFieldWrapper(key)?.querySelector("[data-photo-picker-hint]")?.removeAttribute("hidden");
        this.trackPhotoPickerHint(key, reason);
        this.tagPhotoPickerWay("stuck");
    }

    // The hint at the photo field of `key` for GTM, once per rule (`reason`)
    // and page view, so that the rules can be compared.
    trackPhotoPickerHint(key, reason) {
        // why: lazily — the node tests assemble these methods without running the constructor.
        this.photoPickerHintReasons ??= new Set();
        if (this.photoPickerHintReasons.has(reason)) return;
        this.photoPickerHintReasons.add(reason);
        this.trackPersonalization("personalization_photo_picker_hint", { photo_key: key, reason });
    }

    // The way out gone again, once the field of `key` has its photo.
    hidePhotoPickerHint(key) {
        this.getFieldWrapper(key)?.querySelector("[data-photo-picker-hint]")?.setAttribute("hidden", "");
    }

    // „Im Browser öffnen“ under the photo field of `key`: the product page once
    // more (personalizationPhotoPickerPageUrl), in Chrome - where the picker
    // opens - through Android's intent link. Where the app lets no intent
    // through, the link falls back to the page in the app's browser, and the
    // hint's ⋮ steps and „Link kopieren“ are there again. Counted before the
    // page goes: a push after the navigation might never be read.
    openPhotoPickerInBrowser(key) {
        this.trackPersonalization("personalization_photo_picker_open_external", { photo_key: key });
        this.tagPhotoPickerWay("external");
        window.location.href = personalizationChromeIntentUrl(personalizationPhotoPickerPageUrl(window.location));
    }

    // „Link kopieren“ under the photo field of `key`: the page's address
    // (personalizationPhotoPickerPageUrl), for the customer to paste into Chrome,
    // through the Clipboard API, else through the copy command of old - and
    // once it is there, the field confirms it. Where neither copied, the
    // address shows in its read-only field, selected, to copy by hand.
    // Resolves with the way it went: "clipboard", "exec_command" or "manual" -
    // and GTM hears of it in `reason`.
    async copyPhotoPickerLink(key) {
        const url = personalizationPhotoPickerPageUrl(window.location);
        let way = "manual";
        if (await this.copyTextWithClipboardApi(url)) {
            way = "clipboard";
        } else if (this.copyTextWithExecCommand(url, this.getFieldWrapper(key))) {
            way = "exec_command";
        }
        // why `reason`, never `mode`: mode is the funnel's new or edit on every
        // event, and GTM merges the pushes into one state.
        this.trackPersonalization("personalization_photo_picker_link_copied", { photo_key: key, reason: way });
        const hint = this.getFieldWrapper(key)?.querySelector("[data-photo-picker-hint]");
        if (way !== "manual") {
            hint?.querySelector("[data-photo-picker-copied]")?.removeAttribute("hidden");
            this.tagPhotoPickerWay("copied");
            return way;
        }
        hint?.querySelector("[data-photo-picker-manual]")?.removeAttribute("hidden");
        const addressField = hint?.querySelector("[data-photo-picker-url]");
        if (addressField) {
            addressField.value = url;
            addressField.focus();
            addressField.select();
        }
        return way;
    }

    // Whether the Clipboard API took `text`: neither a browser without it -
    // which throws here - nor one that refuses it did.
    async copyTextWithClipboardApi(text) {
        try {
            await window.navigator.clipboard.writeText(text);
            return true;
        } catch (error) {
            return false;
        }
    }

    // Whether the copy command of old copied `text`, selected in a textarea in
    // `container` that goes again right after: read-only, so that no keyboard
    // opens for it, and fixed beside the screen, so that nothing shows and
    // nothing scrolls. why in the drawer, not in the body: the drawer keeps
    // the focus to itself, so a textarea outside it gives the focus straight
    // back - and Chrome's copy command then copies nothing, yet answers true.
    // A browser that denies the page the command throws: no copy either.
    copyTextWithExecCommand(text, container) {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.top = "0";
        textarea.style.left = "-9999px";
        container.append(textarea);
        try {
            textarea.select();
            return document.execCommand("copy");
        } catch (error) {
            return false;
        } finally {
            textarea.remove();
        }
    }

    // The photo types the shop takes: bookpipe's (allowed_image_mime_types),
    // else the wizard's own.
    acceptedPhotoTypes() {
        return this.fieldConfig?.limits?.acceptedTypes?.length ? this.fieldConfig.limits.acceptedTypes : PERSONALIZATION_PHOTO_ACCEPTED_TYPES;
    }

    // `reason`: why the preparation kept the file as it is (preparePhotoForUpload).
    validatePhotoFile(file, dropzone, reason = null) {
        const typeAccepted = this.acceptedPhotoTypes().includes(file.type);
        if (!typeAccepted && reason === "undecodable") {
            // A photo of another type that this browser cannot even open (HEIC
            // outside Safari): no type or size of it will do here - another
            // photo will, a screenshot of it, say.
            return [
                this.getMessage(
                    "photo-unreadable-format",
                    "Dieses Foto kann dein Browser nicht öffnen. Bitte wähle ein anderes Foto – zum Beispiel einen Screenshot davon."
                ),
            ];
        }
        const errors = [];
        if (!typeAccepted) {
            errors.push(this.getMessage("photo-type", "Bitte wähle ein Foto im Format JPG, PNG oder WEBP."));
        }
        const maxBytes = parseInt(dropzone?.getAttribute("data-max-file-size") || "", 10) || PERSONALIZATION_PHOTO_MAX_BYTES;
        if (file.size > maxBytes) {
            errors.push(this.getMessage("photo-size", "Das Foto darf maximal 10 MB groß sein."));
        }
        return errors;
    }

    // The photo as the wizard sends it: at most PREPARE_MAX_EDGE px on its long
    // edge, a JPEG turned the way it is meant to be seen, white behind
    // transparency - a phone's 2-9 MB photo took a minute and more on a mobile
    // uplink. A photo the shop does not accept but the browser can open (HEIC
    // on Safari, a file without a type) becomes a JPEG too. Resolves - never
    // rejects - with { file, prepared, reason }: the prepared JPEG ("decoded"),
    // or the original itself and why it was kept - "fast_path" (accepted and
    // small already), "too_large_to_decode" (above PREPARE_MAX_BYTES, never
    // opened), "original_smaller" (its JPEG would not be smaller),
    // "undecodable" (the browser could not make a JPEG of it) or "timeout".
    // The JPEG keeps the picked file's name and lastModified; its size, and so
    // its upload fingerprint, is its own. The type and size checks
    // (validatePhotoFile) run on the result.
    async preparePhotoForUpload(file) {
        const accepted = PERSONALIZATION_PHOTO_ACCEPTED_TYPES.includes(file.type);
        if (accepted && file.size <= PERSONALIZATION_PHOTO_UNTOUCHED_MAX_BYTES) {
            return { file, prepared: false, reason: "fast_path" };
        }
        if (file.size > PERSONALIZATION_PHOTO_PREPARE_MAX_BYTES) {
            return { file, prepared: false, reason: "too_large_to_decode" };
        }
        let timer = null;
        const timedOut = new Promise((resolve) => {
            timer = window.setTimeout(() => resolve({ file, prepared: false, reason: "timeout" }), PERSONALIZATION_PHOTO_PREPARE_TIMEOUT_MS);
        });
        try {
            return await Promise.race([this.convertPhotoToJpeg(file, accepted), timedOut]);
        } finally {
            window.clearTimeout(timer);
        }
    }

    // preparePhotoForUpload's outcome of one attempt at the JPEG. An original
    // the shop does not accept gives way to its JPEG whatever their sizes.
    async convertPhotoToJpeg(file, accepted) {
        try {
            const jpeg = await this.encodePhotoAsJpeg(file);
            if (accepted && jpeg.size >= file.size) return { file, prepared: false, reason: "original_smaller" };
            return { file: new File([jpeg], file.name, { type: "image/jpeg", lastModified: file.lastModified }), prepared: true, reason: "decoded" };
        } catch (error) {
            return { file, prepared: false, reason: "undecodable" };
        }
    }

    // The photo drawn onto white at most PREPARE_MAX_EDGE px on its long edge,
    // as a JPEG Blob; it throws when there is none. why the release in the
    // finally: iOS caps the memory all canvases of a page may hold, and a
    // decoded bitmap holds every pixel of the photo until closed (an <img> has
    // nothing to close).
    async encodePhotoAsJpeg(file) {
        const image = await this.decodePhoto(file);
        const scale = Math.min(1, PERSONALIZATION_PHOTO_PREPARE_MAX_EDGE / Math.max(image.width, image.height));
        const canvas = document.createElement("canvas");
        try {
            canvas.width = Math.round(image.width * scale);
            canvas.height = Math.round(image.height * scale);
            const context = canvas.getContext("2d");
            context.fillStyle = "#ffffff";
            context.fillRect(0, 0, canvas.width, canvas.height);
            context.drawImage(image, 0, 0, canvas.width, canvas.height);
            const jpeg = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", PERSONALIZATION_PHOTO_PREPARE_QUALITY));
            if (!jpeg) throw new Error("PersonalizationModal: the canvas gave no JPEG");
            return jpeg;
        } finally {
            canvas.width = 0;
            canvas.height = 0;
            image.close?.();
        }
    }

    // The photo decoded the way it is meant to be seen (EXIF orientation
    // applied), its width and height the photo's own.
    async decodePhoto(file) {
        try {
            return await window.createImageBitmap(file, { imageOrientation: "from-image" });
        } catch (error) {
            // why: engines that predate the "from-image" value (older WebKit,
            // frequent in Facebook's and Instagram's in-app browsers) refuse
            // the option itself, some formats open in an <img> only, and some
            // browsers have no createImageBitmap at all - their customers get
            // the smaller photo all the same.
            return this.decodePhotoThroughImage(file);
        }
    }

    // An <img> outside the page: turned by EXIF like every <img>, and its
    // width and height are the photo's own once decoded. Its object URL goes
    // as soon as the decode settled - the decoded image stays drawable.
    async decodePhotoThroughImage(file) {
        const url = URL.createObjectURL(file);
        try {
            const image = new Image();
            image.src = url;
            await image.decode();
            return image;
        } finally {
            URL.revokeObjectURL(url);
        }
    }

    static shortenPhotoFilename(name, maxChars = PERSONALIZATION_PHOTO_FILENAME_MAX_CHARS) {
        const filename = String(name || "");
        if (filename.length <= maxChars) return filename;
        const dot = filename.lastIndexOf(".");
        // Only treat a trailing dot group as an extension — a dotted stem
        // ("foto.2026.final") must not eat the whole visible part.
        const extension = dot > 0 && filename.length - dot <= 6 ? filename.slice(dot) : "";
        const stem = filename.slice(0, filename.length - extension.length);
        const room = maxChars - extension.length - 1;
        if (room < 4) return `${filename.slice(0, Math.max(maxChars - 1, 1))}…`;
        const head = Math.ceil(room / 2);
        const tail = room - head;
        return `${stem.slice(0, head)}…${tail > 0 ? stem.slice(-tail) : ""}${extension}`;
    }

    // options.reason: why the preparation of the customer's pick kept the file
    // as it is (preparePickedPhoto) - it picks the words of a refusal.
    applyPhotoFile(key, file, options = {}) {
        if (!key) return;
        // Hydration from IndexedDB passes persist:false — only a photo the
        // customer picked right now is a funnel event.
        const persist = options.persist !== false;
        const wrapper = this.getFieldWrapper(key);
        const errors = this.validatePhotoFile(file, wrapper?.querySelector("[data-photo-dropzone]"), options.reason);
        if (errors.length > 0) {
            if (persist) {
                this.reportRefusedPhoto(key, options.picked || file, options.reason);
                // At the photo the customer just picked, which the dropzone
                // showed while it was being prepared.
                this.refusePhoto(key, errors);
            } else {
                this.updateErrors(this.getActiveStepId(), errors);
            }
            return;
        }
        if (persist) {
            this.trackPersonalization("personalization_photo_selected", { photo_key: key });
            // A photo of a type the shop does not take, which the browser turned
            // into a JPEG: Clarity shows these next to the refusals' types.
            if (options.picked && !this.acceptedPhotoTypes().includes(options.picked.type)) {
                setPersonalizationClarityTag("pers_file_type", `${options.picked.type || "unknown"}→jpeg`);
            }
        }

        this.photoFiles.set(key, file);
        this.renderPhotoState(key);
        this.clearFieldError(key);
        this.clearErrors();

        if (persist) {
            const lineUuid = this.getOrCreateLineUuid();
            PersonalizationPhotoStore.set(this.getPhotoStoreKey(lineUuid, key), {
                blob: file,
                name: file.name,
                type: file.type,
                lastModified: file.lastModified,
            });
        }

        // Session flow: the photo is validated server-side the moment it is
        // selected — a rejection verdict renders right here at the dropzone.
        // A photo this session already verified (restored draft, re-picked
        // identical file) keeps its verdict without another AI check.
        const fingerprint = this.photoUploadFingerprint(file);
        const alreadyVerified =
            this.sessionUploadedPhotos.get(key) === fingerprint ||
            this.getPersistedUploadedPhotoFingerprints()[key] === fingerprint;
        if (alreadyVerified) {
            this.setPhotoCheckState(key, "verified");
        } else if (this.wizardOpened) {
            this.queuePhotoUpload(key, file);
        } else {
            // Hydrated before the wizard opened: no claim yet — the check
            // runs through the pre-preview sync.
            this.setPhotoCheckState(key, "none");
        }

        // A different photo invalidates any previously generated preview.
        this.invalidatePreviewIfStale();
    }

    // A photo the browser refused, counted with the picked file's type and size
    // range - never its name - and logged once, with why (`reason`, the
    // preparation's). why an Error: the Sentry mirror groups an Error by its
    // message, but writes plain objects into the message text - every type
    // and range would be an issue of its own.
    reportRefusedPhoto(key, picked, reason) {
        const refused = { file_type: picked.type || "unknown", file_size: personalizationPhotoSizeBucket(picked.size) };
        this.trackPersonalization("personalization_photo_rejected", { photo_key: key, reason: "client_validation", ...refused });
        console.error(
            "PersonalizationModal: photo refused in the browser",
            new Error(`PersonalizationModal: photo refused in the browser: ${reason}`),
            { ...refused, reason }
        );
    }

    clearPhoto(key) {
        // Nothing will use the removed photo's verdict: its upload is called off.
        this.abortPhotoUploads((upload) => upload.key === key);
        this.photoPreparations?.delete(key);
        this.photoFiles.delete(key);
        this.photoCheckStates.delete(key);
        this.renderPhotoState(key);
        this.clearFieldError(key);
        const lineUuid = this.getSavedState()?.["__line_uuid"];
        if (lineUuid) {
            PersonalizationPhotoStore.delete(this.getPhotoStoreKey(lineUuid, key));
            if (key === this.bookpipePhotoKey) {
                PersonalizationPhotoStore.delete(this.getLegacyPhotoStoreKey(lineUuid));
            }
        }
        this.invalidatePreviewIfStale();
    }

    renderPhotoState(key) {
        const wrapper = this.getFieldWrapper(key);
        if (!wrapper) return;
        const emptyEl = wrapper.querySelector("[data-photo-empty]");
        const filledEl = wrapper.querySelector("[data-photo-filled]");
        const previewImage = wrapper.querySelector("[data-photo-preview-image]");
        const filenameEl = wrapper.querySelector("[data-photo-filename]");
        if (!emptyEl || !filledEl) return;

        // A pick still being prepared shows as picked (preparePickedPhoto).
        const file = this.photoPreparations?.get(key)?.file || this.photoFiles.get(key);
        const previousUrl = this.photoObjectUrls.get(key);
        if (previousUrl) {
            URL.revokeObjectURL(previousUrl);
            this.photoObjectUrls.delete(key);
        }

        if (file) {
            emptyEl.setAttribute("hidden", "");
            filledEl.removeAttribute("hidden");
            if (previewImage) {
                const url = URL.createObjectURL(file);
                this.photoObjectUrls.set(key, url);
                previewImage.src = url;
            }
            if (filenameEl) {
                const filename = file.name || "";
                filenameEl.textContent = PersonalizationModal.shortenPhotoFilename(filename);
                if (filename) {
                    filenameEl.setAttribute("title", filename);
                } else {
                    filenameEl.removeAttribute("title");
                }
            }
        } else {
            filledEl.setAttribute("hidden", "");
            emptyEl.removeAttribute("hidden");
            if (previewImage) {
                previewImage.removeAttribute("src");
            }
            if (filenameEl) {
                filenameEl.textContent = "";
                filenameEl.removeAttribute("title");
            }
        }

        // A filled dropzone opens no picker - the photo is removed first: the
        // label goes with the empty state, and the input, which the keyboard
        // reaches all the same, is disabled.
        wrapper.querySelector("[data-photo-input]")?.toggleAttribute("disabled", Boolean(file));

        // Re-applied on every render: the dropzones are re-cloned from their
        // template whenever the field config re-renders step 1.
        this.setPhotoCheckState(key, this.photoCheckStates.get(key) || "none", this.photoCheckDetails?.get(key));
    }

    // Renders the photo's way to bookpipe at the dropzone: "uploading" with the
    // share of it sent so far (detail.share, 0–1), the "checking" spinner, the
    // "verified" badge, "failed" with its retry button - naming the bot gate
    // instead of the connection when that refused (detail.challenge) - or no
    // claim. The status container is a live region showing exactly one entry,
    // so swapping it is announced to screen readers as well.
    setPhotoCheckState(key, state, detail = {}) {
        this.photoCheckStates.set(key, state);
        // why: lazily — the node tests assemble these methods without running the constructor.
        this.photoCheckDetails ??= new Map();
        this.photoCheckDetails.set(key, detail);
        const wrapper = this.getFieldWrapper(key);
        if (!wrapper) return;
        const dropzone = wrapper.querySelector("[data-photo-dropzone]");
        if (dropzone) {
            dropzone.setAttribute("data-check-state", state);
            if (state === "uploading" || state === "checking") {
                dropzone.setAttribute("aria-busy", "true");
            } else {
                dropzone.removeAttribute("aria-busy");
            }
        }
        ["uploading", "checking", "verified", "failed"].forEach((entry) => {
            wrapper.querySelector(`[data-photo-status-${entry}]`)?.toggleAttribute("hidden", state !== entry);
        });
        const progress = wrapper.querySelector("[data-photo-upload-progress]");
        if (progress && state === "uploading") {
            // Never more than went out: the last bytes on their way are no 100 %.
            const percent = Math.floor((detail.share || 0) * 100);
            progress.textContent = this.formatMessage("photo-uploading", "Foto wird hochgeladen … {percent} %", { percent });
        }
        if (state === "failed") {
            wrapper.querySelector("[data-photo-failed-connection]")?.toggleAttribute("hidden", Boolean(detail.challenge));
            wrapper.querySelector("[data-photo-failed-challenge]")?.toggleAttribute("hidden", !detail.challenge);
        }
    }

    renderAllPhotoStates() {
        this.getUploadFields().forEach((field) => this.renderPhotoState(field.key));
    }

    // The photos are kept under the page's product like the draft itself - book, Hörbuch and set
    // are variants of it (plan hoerbuch-set-als-variante), so a format card switch keeps them.
    getPhotoStoreKey(lineUuid, key) {
        return `${this.productId}:${lineUuid || "draft"}:${key}`;
    }

    // Drafts saved before the multi-photo cutover stored their single photo
    // without an option-key suffix.
    getLegacyPhotoStoreKey(lineUuid) {
        return `${this.productId}:${lineUuid}`;
    }

    // Public entry point (also called by the openPersonalize prefill): waits
    // for the field config so the dropzones it hydrates into exist.
    async hydratePhotoFromStore(options = {}) {
        if (this.fieldConfigPromise) {
            try {
                await this.fieldConfigPromise;
            } catch (e) {
                // Fallback config still renders dropzones.
            }
        }
        return this.hydratePhotosForConfig(options);
    }

    async hydratePhotosForConfig(options = {}) {
        const lineUuid = options.lineUuid || this.getSavedState()?.["__line_uuid"];
        if (!lineUuid) return;
        for (const field of this.getUploadFields()) {
            if (this.photoFiles.has(field.key) && !options.force) continue;
            let record = await PersonalizationPhotoStore.get(this.getPhotoStoreKey(lineUuid, field.key));
            if ((!record || !record.blob) && field.key === this.bookpipePhotoKey) {
                record = await PersonalizationPhotoStore.get(this.getLegacyPhotoStoreKey(lineUuid));
            }
            if (!record || !record.blob) continue;
            // Drop the result if the wizard state moved on while we were reading.
            const currentUuid = options.lineUuid || this.getSavedState()?.["__line_uuid"];
            if (currentUuid !== lineUuid) return;
            if (options.force) {
                this.photoFiles.delete(field.key);
            }
            const file = new File([record.blob], record.name || "foto.jpg", {
                type: record.type || record.blob.type,
                lastModified: record.lastModified || Date.now(),
            });
            this.applyPhotoFile(field.key, file, { persist: false });
        }
    }

    // ------------------------------------------------------------------
    // Field configuration (bookpipe app proxy) + step-1 renderer
    // ------------------------------------------------------------------

    async resolveActiveSku() {
        const variantId = await this.getSelectedVariantId();
        const coverType = this.resolveCoverTypeForVariant(variantId);
        return this.resolveSkuForCoverType(coverType);
    }

    // Loads the personalization inputs configured in bookpipe for the selected
    // variant's product and renders step 1 from them. Falls back to the legacy
    // hardcoded field set when the config cannot be loaded, so the flagship
    // flow survives transient app-proxy failures.
    initFieldConfig(options = {}) {
        const promise = (async () => {
            let sku = "";
            try {
                sku = await this.resolveActiveSku();
            } catch (e) {
                sku = "";
            }

            if (this.fieldConfig && this.fieldConfigSku === sku) {
                return this.fieldConfig;
            }

            let config = null;
            if (sku) {
                try {
                    config = await this.loadFieldConfig(sku);
                } catch (error) {
                    console.error("PersonalizationModal: Failed to load the bookpipe field config, using the fallback field set.", error, error.details || {});
                }
            } else {
                console.error("PersonalizationModal: The selected variant has no SKU, using the fallback field set.");
            }

            this.fieldConfig = config || this.buildFallbackFieldConfig();
            this.fieldConfigSku = sku;
            this.renderStep1Fields();

            if (options.hydratePhotos !== false) {
                await this.hydratePhotosForConfig();
            }

            return this.fieldConfig;
        })();

        this.fieldConfigPromise = promise;
        return promise;
    }

    // why: per language - bookpipe answers the config (its labels above all) in the language the
    // request names, so after a language switch another language's entry would show its labels.
    productConfigCacheKey(sku) {
        return `bookpipe:product-config:${sku}:${this.storefrontLocale}`;
    }

    async loadFieldConfig(sku, signal = undefined) {
        const cacheKey = this.productConfigCacheKey(sku);
        try {
            const raw = window.sessionStorage.getItem(cacheKey);
            if (raw) {
                const cached = JSON.parse(raw);
                if (cached?.config && Date.now() - (cached.savedAt || 0) < PERSONALIZATION_CONFIG_CACHE_TTL_MS) {
                    return cached.config;
                }
            }
        } catch (e) {
            // Cache is best-effort only.
        }

        const fetchFn = window.originalFetch || window.fetch;
        // The sid keys the server's rate limit per customer session, like
        // the challenge request: behind the app proxy every customer shares
        // one IP, so without it the budget is shop-wide.
        const sid = encodeURIComponent(this.getOrCreateCustomerSessionId() || "");
        const response = await fetchFn(this.appProxyUrl(`${this.bookpipeProductConfigUrl}?shopify_sku=${encodeURIComponent(sku)}&sid=${sid}`), {
            method: "GET",
            headers: {
                Accept: "application/json",
                "X-Requested-With": "XMLHttpRequest",
            },
            signal,
        });

        if (!response.ok) {
            throw await this.buildRequestError("Bookpipe product config request failed", response);
        }

        const data = await this.parseJsonResponse(response);
        const config = this.normalizeFieldConfig(data);

        if (!config) {
            throw new Error("Bookpipe product config response is malformed");
        }

        try {
            window.sessionStorage.setItem(cacheKey, JSON.stringify({ savedAt: Date.now(), config }));
        } catch (e) {
            // noop
        }

        return config;
    }

    normalizeFieldConfig(data) {
        if (!data || !Array.isArray(data.personalization_fields) || !Array.isArray(data.personalization_upload_fields)) {
            return null;
        }

        const normalizeRules = (rules) => ({
            required: !!(rules && rules.required),
            min: rules && rules.min !== null && Number.isFinite(Number(rules.min)) ? Number(rules.min) : null,
            max: rules && rules.max !== null && Number.isFinite(Number(rules.max)) ? Number(rules.max) : null,
        });

        const textFields = data.personalization_fields
            .map((field) => ({
                key: String(field?.key || ""),
                label: String(field?.label || field?.key || ""),
                inputType: String(field?.input_type || "text"),
                // Example text for the empty field (additive server key):
                // absent, null or blank means none, so older backends keep working.
                placeholder: typeof field?.input_placeholder === "string" ? field.input_placeholder.trim() : "",
                ...normalizeRules(field?.validation_rules),
            }))
            .map((field) =>
                field.key === "main_character" && field.max === null
                    ? { ...field, max: PERSONALIZATION_MAIN_CHARACTER_DEFAULT_MAX }
                    : field
            )
            .filter((field) => field.key !== "");

        const uploadFields = data.personalization_upload_fields
            .map((field) => ({
                key: String(field?.key || ""),
                label: String(field?.label || field?.key || ""),
                inputType: "photo_upload",
                photoValidation: String(field?.photo_validation || "none"),
                ...normalizeRules(field?.validation_rules),
            }))
            .filter((field) => field.key !== "");

        const limits = data.limits || {};
        const maxUploadKb = Number(limits.max_image_upload_kb);
        const acceptedTypes = Array.isArray(limits.allowed_image_mime_types) && limits.allowed_image_mime_types.length > 0
            ? limits.allowed_image_mime_types.map((type) => String(type))
            : PERSONALIZATION_PHOTO_ACCEPTED_TYPES.slice();

        // Bot-challenge advertisement (additive server key): absent or
        // malformed means disabled, so older backends keep working.
        const challenge =
            data.challenge && typeof data.challenge === "object"
                ? { enabled: !!data.challenge.enabled, provider: String(data.challenge.provider || "altcha") }
                : { enabled: false, provider: "altcha" };

        return {
            source: "bookpipe",
            textFields,
            uploadFields,
            challenge,
            limits: {
                // The theme's 10 MB bound stays the UX ceiling even when the
                // server would accept larger uploads.
                maxFileBytes: Math.min(
                    PERSONALIZATION_PHOTO_MAX_BYTES,
                    Number.isFinite(maxUploadKb) && maxUploadKb > 0 ? maxUploadKb * 1024 : PERSONALIZATION_PHOTO_MAX_BYTES
                ),
                acceptedTypes,
            },
        };
    }

    isAudiobook() {
        return this.productKind === "audiobook";
    }

    // The printed book's offer of its set, which brings the Hörbuch along (plan
    // hoerbuch-set-als-variante): on offer where the product sells a set variant for every
    // cover the page sells - the Softcover always, the Hardcover where the cover step offers it -
    // as Liquid decides the Hörbuch step. Only to the printed book: the Hörbuch is no set of
    // itself, and the set format is the set already.
    setOffered() {
        if (this.format !== "book" || this.setSoftcoverVariantId === "") return false;
        return this.setHardcoverVariantId !== "" || !this.coverStepEnabled();
    }

    // The one reading of the customer's answer in the Hörbuch step, shared by the cart
    // payload and the name sample: the set format is the set, and in the book format only
    // a "Ja" to a set on offer counts - a step left unanswered (nothing is preselected) is no "Ja".
    setChosen(entries) {
        if (this.format === "set") return true;
        return this.setOffered() && String(entries["__add-audiobook"]) === "true";
    }

    // The pre-cutover hardcoded field set: main_character + photo + hash +
    // dedication. Only used when the config request fails. The audiobook has no
    // photo and no certificate — a required dropzone here would block the
    // purchase of a product bookpipe never advertises one for.
    buildFallbackFieldConfig() {
        const audiobook = this.isAudiobook();
        const nameField = { key: "main_character", label: this.getMessage("fallback-name-label", "Vorname des Kindes"), inputType: "text", required: true, min: null, max: PERSONALIZATION_MAIN_CHARACTER_DEFAULT_MAX };
        const hashField = { key: PERSONALIZATION_HASH_KEY, label: PERSONALIZATION_HASH_KEY, inputType: "text", required: false, min: null, max: null };
        const dedicationField = { key: PERSONALIZATION_DEDICATION_KEY, label: this.getMessage("fallback-dedication-label", "Widmung"), inputType: "textarea", required: false, min: null, max: null };
        const photoField = { key: this.bookpipePhotoKey, label: this.getMessage("fallback-photo-label", "Foto des Kindes"), inputType: "photo_upload", photoValidation: "none", required: true, min: null, max: null };

        return {
            source: "fallback",
            textFields: audiobook ? [nameField, dedicationField] : [nameField, hashField, dedicationField],
            uploadFields: audiobook ? [] : [photoField],
            // Unknown gate state (config request failed): don't mint
            // proactively — the challenge_required handlers heal on demand.
            challenge: { enabled: false, provider: "altcha" },
            limits: {
                maxFileBytes: PERSONALIZATION_PHOTO_MAX_BYTES,
                acceptedTypes: PERSONALIZATION_PHOTO_ACCEPTED_TYPES.slice(),
            },
        };
    }

    // Text/textarea inputs step 1 renders; reserved keys (hash, dedication)
    // and unsupported input types (audio) are excluded.
    getRenderableTextFields() {
        if (!this.fieldConfig) return [];
        return this.fieldConfig.textFields.filter(
            (field) =>
                field.key !== PERSONALIZATION_HASH_KEY &&
                field.key !== PERSONALIZATION_DEDICATION_KEY &&
                (field.inputType === "text" || field.inputType === "textarea")
        );
    }

    getUploadFields() {
        return this.fieldConfig ? this.fieldConfig.uploadFields : [];
    }

    supportsDedication() {
        return !!this.fieldConfig?.textFields.some((field) => field.key === PERSONALIZATION_DEDICATION_KEY);
    }

    fieldInputName(key) {
        // Leading underscores hide the property in the theme's cart display,
        // and bookpipe's fallback property import strips them back off, so the
        // property matches the option key without a channel mapping.
        return `__${key}`;
    }

    getEntryValue(entries, key) {
        const name = this.fieldInputName(key);
        if (entries[name] !== undefined && entries[name] !== null && String(entries[name]) !== "") {
            return entries[name];
        }
        const legacyName = PERSONALIZATION_LEGACY_ENTRY_ALIASES[key];
        if (legacyName && entries[legacyName] !== undefined && entries[legacyName] !== null && String(entries[legacyName]) !== "") {
            return entries[legacyName];
        }
        // Last line of defense: the step-1 inputs can transiently be missing
        // or blank in the DOM (config re-render while another step is
        // active). The saved draft is authoritative then. A stored "" is a
        // deliberate clear (handleInput persists clears) and stays cleared —
        // the legacy alias is only consulted when the primary key was never
        // stored at all (pre-cutover drafts, imported cart lines), so a
        // cleared field cannot resurrect from its alias.
        const state = this.getSavedState() || {};
        if (Object.prototype.hasOwnProperty.call(state, name)) {
            const stored = state[name];
            if (stored !== undefined && stored !== null && String(stored).trim() !== "") {
                return String(stored);
            }
            return "";
        }
        const legacySaved = legacyName ? state[legacyName] : undefined;
        if (legacySaved !== undefined && legacySaved !== null && String(legacySaved).trim() !== "") {
            return String(legacySaved);
        }
        return "";
    }

    getFieldWrapper(key) {
        const root = this.getModalRoot();
        const escaped = window.CSS && typeof CSS.escape === "function" ? CSS.escape(key) : key;
        return root.querySelector(`[data-field-wrapper][data-field-key="${escaped}"]`);
    }

    renderStep1Fields() {
        const root = this.getModalRoot();
        const container = root.querySelector("[data-dynamic-fields]");
        if (!container || !this.fieldConfig) return;

        container.querySelector("[data-fields-loading]")?.remove();
        container.querySelectorAll("[data-field-wrapper]").forEach((el) => el.remove());

        const textTemplate = root.querySelector("template[data-template-text-field]");
        const textareaTemplate = root.querySelector("template[data-template-textarea-field]");
        const photoTemplate = root.querySelector("template[data-template-photo-field]");
        const optionalSuffix = this.getMessage("field-optional", "(optional)");
        const labelText = (field) => (field.required ? field.label : `${field.label} ${optionalSuffix}`);

        this.getRenderableTextFields().forEach((field) => {
            const template = field.inputType === "textarea" ? textareaTemplate || textTemplate : textTemplate;
            if (!template) return;
            const wrapper = template.content.firstElementChild.cloneNode(true);
            wrapper.setAttribute("data-field-key", field.key);
            const label = wrapper.querySelector("[data-field-label]");
            const input = wrapper.querySelector("[data-field-input]");
            const errorEl = wrapper.querySelector("[data-field-error]");
            const inputId = `bp-field-${this.productId}-${field.key}`;
            if (label) {
                label.textContent = labelText(field);
                label.setAttribute("for", inputId);
            }
            if (input) {
                input.id = inputId;
                input.name = this.fieldInputName(field.key);
                if (field.required) input.setAttribute("required", "");
                if (field.max) input.setAttribute("maxlength", String(field.max));
                if (field.placeholder) input.setAttribute("placeholder", field.placeholder);
            }
            if (errorEl && input) {
                errorEl.id = `${inputId}-error`;
                input.setAttribute("aria-describedby", errorEl.id);
            }
            container.appendChild(wrapper);
        });

        const uploadFields = this.getUploadFields();
        uploadFields.forEach((field) => {
            if (!photoTemplate) return;
            const wrapper = photoTemplate.content.firstElementChild.cloneNode(true);
            wrapper.setAttribute("data-field-key", field.key);
            const label = wrapper.querySelector("[data-field-label]");
            if (label) label.textContent = labelText(field);
            const dropzone = wrapper.querySelector("[data-photo-dropzone]");
            const input = wrapper.querySelector("[data-photo-input]");
            const pickerLabel = wrapper.querySelector("[data-photo-select-label]");
            const errorEl = wrapper.querySelector("[data-field-error]");
            const inputId = `bp-photo-${this.productId}-${field.key}`;
            if (dropzone) {
                dropzone.setAttribute("data-photo-key", field.key);
                if (this.fieldConfig.limits?.maxFileBytes) {
                    dropzone.setAttribute("data-max-file-size", String(this.fieldConfig.limits.maxFileBytes));
                }
            }
            if (input) {
                // The empty dropzone is the input's label, so a tap on it opens
                // the browser's own picker: every clone needs an id of its own.
                input.id = inputId;
                input.setAttribute("accept", PERSONALIZATION_PHOTO_PICKER_ACCEPT);
                pickerLabel?.setAttribute("for", inputId);
            }
            if (errorEl) {
                errorEl.id = `bp-field-${this.productId}-${field.key}-error`;
                input?.setAttribute("aria-describedby", errorEl.id);
            }
            container.appendChild(wrapper);
        });

        // Photos selected under a previous config must not leak into requests
        // for a product that does not define their upload key.
        Array.from(this.photoFiles.keys()).forEach((key) => {
            if (!uploadFields.some((field) => field.key === key)) {
                this.photoFiles.delete(key);
                const url = this.photoObjectUrls.get(key);
                if (url) {
                    URL.revokeObjectURL(url);
                    this.photoObjectUrls.delete(key);
                }
            }
        });

        root.querySelector("[data-photo-guidelines]")?.toggleAttribute("hidden", uploadFields.length === 0);
        root.querySelector("[data-photo-privacy]")?.toggleAttribute("hidden", uploadFields.length === 0);

        // The dedication step only applies when the product configures the
        // dedication option; otherwise hide its choice UI (and never charge
        // the dedication fee).
        root.querySelectorAll("[data-dedication-ui]").forEach((el) => {
            el.toggleAttribute("hidden", !this.supportsDedication());
        });

        // (Re)bind input listeners and hydrate saved values for the active step.
        this.showFormForStep(this.getActiveStepId());
        // The re-render above re-cloned the step-1 wrappers, but
        // showFormForStep hydrates only the ACTIVE step: after a re-render
        // while the customer sits on step 2/3 the fresh step-1 inputs would
        // stay blank behind `hidden`, and the pre-preview full sync would
        // clear those values server-side — a "required" 422 on a field that
        // looks filled once the error handler re-hydrates it.
        this.hydrateAllForms();
        this.renderAllPhotoStates();
    }

    // ------------------------------------------------------------------
    // Audiobook name sample ("So klingt dein Hörbuch")
    // ------------------------------------------------------------------
    // bookpipe speaks the audiobook's greeting sentence with the child's name
    // and answers a signed, one-hour MP3 URL. This is a courtesy extra of the
    // Hörbuch — in its own view, in the set that brings it along and in the book
    // whose customer takes the set: it never blocks, never delays and never fails
    // the purchase. A sample that does not arrive just leaves the button
    // disabled — no wizard error, no entry in pendingPhotoUploads (which
    // ensurePreview awaits), nothing add-to-cart has to wait for.

    // Which Hörbuch a run brings along, in two halves that stay apart. WHICH one its
    // requests name follows from the render alone (nameSampleSku) and stays fixed
    // while the render stands; WHETHER the run wants a sample follows the customer
    // too (wantsNameSample) — the book only after a "Ja" to its set.
    // The Hörbuch's own session needs no name: it IS the Hörbuch. The set and the book
    // run on the book's session and name the Hörbuch to speak with by its SKU — the
    // book's digital variant, which the set brings along.
    nameSampleSku() {
        if (this.isAudiobook()) return "";
        return this.audiobookSku;
    }

    // Nothing to name, nothing to play: a book - or its set - that sells no Hörbuch asks
    // for no sample at all.
    wantsNameSample() {
        if (this.isAudiobook()) return true;
        if (this.nameSampleSku() === "") return false;
        return this.format === "set" || this.setChosen(this.collectEntries());
    }

    // Puts the preview on what the run wants. Where the customer's answer decides - the
    // book that offers its set - Liquid renders the block and the Hörbuch cover beside the
    // book hidden and marked, and they follow the answer from here; the Hörbuch and the set
    // carry no marker and keep theirs. A "Nein" asks nothing and cancels nothing: a poll under way
    // runs out on its own (bounded, see runNameSample), and its sample waits for a later "Ja".
    // Safe to repeat - on an answer, on entering the preview, after a fresh draft or a replaced
    // session: a name asked for once is only shown again (requestNameSample, applyNameSample).
    syncNameSample() {
        const root = this.getModalRoot();
        const wanted = this.wantsNameSample();
        root.querySelectorAll("[data-name-sample-on-answer]").forEach((element) => {
            element.toggleAttribute("hidden", !wanted);
        });
        if (wanted) {
            this.requestNameSample();
            return;
        }
        root.querySelector("[data-name-sample-audio]")?.pause();
    }

    requestNameSample({ prefetch = false } = {}) {
        // The prefetch off the character step (navigateToStep) asks ahead of the answer, too,
        // where the book offers its set and names a Hörbuch to speak it (Owner 01.10.: Prefetch
        // vor dem Ja): the render takes about as long as the way to the Hörbuch step, so a "Ja"
        // mostly finds the sample ready. What shows still follows the answer (syncNameSample).
        const ahead = prefetch && this.setOffered() && this.nameSampleSku() !== "";
        if (!ahead && !this.wantsNameSample()) return null;
        const name = String(this.getEntryValue(this.collectEntries(), "main_character") || "").trim();
        if (!name) return null;

        this.nameSampleActiveName = name;
        // Keyed by the trimmed name: a name the wizard already asked for is shown
        // again instead of re-posting (the endpoint is rate limited per session
        // and every render costs), while a corrected name gets its own sample.
        if (this.nameSampleRequests.has(name)) {
            this.showNameSampleResult(name);
            return this.nameSampleRequests.get(name);
        }

        this.nameSampleResults.set(name, "pending");
        const promise = this.runNameSample(name).catch(() => {
            // Cosmetic extra: the button state already says everything the
            // customer needs to know.
        });
        this.nameSampleRequests.set(name, promise);
        return promise;
    }

    async runNameSample(name) {
        // A run belongs to the session it started on: replaced meanwhile, the session took the run
        // with it (forgetNameSamplesOfReplacedSession). The name alone cannot tell - it may be asked
        // for again before the run's next check - the epoch can: such a run polls no more and records
        // nothing.
        const epoch = this.sessionEpoch;
        this.setNameSampleState("preparing");

        // ORDER MATTERS: the endpoint reads the name from the SESSION, the
        // per-field sync is debounced by 600 ms, and the full sync otherwise only
        // happens inside ensurePreview — posting first would ask bookpipe to
        // speak a name it has not been told yet. A failed sync means exactly
        // that, so give up quietly instead. The text fields are all the sample
        // needs: the photo stays with the preview's full sync.
        const sync = await this.syncTextFieldsToSession();
        if (sync && sync.ok === false) {
            this.settleNameSample(name, "failed");
            return;
        }

        let state = await this.nameSampleRequest("POST");
        if (state?.status === "unavailable" && state.reason === "no_name") {
            // Transient from the client's view: the sync above may still be
            // landing server-side. Exactly one retry, then give up.
            await this.nameSampleDelay(PERSONALIZATION_NAME_SAMPLE_RETRY_MS);
            state = await this.nameSampleRequest("POST");
        }

        // Poll while the render is queued. Bounded twice over: by the deadline
        // here and by the server's own 60/min limiter, whose 429 ends the loop
        // through the null response.
        const deadline = Date.now() + PERSONALIZATION_NAME_SAMPLE_POLL_MAX_MS;
        while (state?.status === "pending" && Date.now() < deadline) {
            await this.nameSampleDelay(PERSONALIZATION_NAME_SAMPLE_POLL_INTERVAL_MS);
            // The name was corrected meanwhile, or the session replaced: stop spending polls on it.
            if (this.nameSampleActiveName !== name || this.sessionEpoch !== epoch) break;
            state = await this.nameSampleRequest("GET");
        }

        if (this.sessionEpoch !== epoch) return;
        this.settleNameSample(name, state?.status === "ready" && state.url ? state.url : "failed");
    }

    // Records what a run ended with (a signed URL, or "failed") and — if this name
    // is still the one in the wizard — shows it. A name the customer corrected
    // meanwhile is forgotten instead of recorded: coming back to it asks again,
    // which bookpipe answers from its own unit cache.
    settleNameSample(name, result) {
        if (this.nameSampleActiveName !== name) {
            this.nameSampleRequests.delete(name);
            this.nameSampleResults.delete(name);
            return;
        }
        this.nameSampleResults.set(name, result);
        this.showNameSampleResult(name);
    }

    // A replaced session (resetSession: the other cover's SKU, a 409/404 replay, a fresh draft) takes
    // every run without a ready sample with it: its polls would ask the new session, which may not
    // know the name yet - bookpipe answers that "no_name", and the name would stay failed for the
    // draft. Forgotten, a run stops at its next check and records nothing (runNameSample); the next
    // "Ja", preview entry or prefetch asks again, which bookpipe answers from its cache. A ready
    // sample stays: its URL is signed for itself, not for the session. On the preview nothing is
    // coming to ask again, so it syncs at once, as entering it does.
    forgetNameSamplesOfReplacedSession() {
        this.nameSampleResults.forEach((result, name) => {
            if (result !== "pending" && result !== "failed") return;
            this.nameSampleRequests.delete(name);
            this.nameSampleResults.delete(name);
        });
        this.nameSampleActiveName = null;
        if (this.getActiveStepId() === this.previewStepId()) this.syncNameSample();
    }

    // Puts button and audio element on the recorded outcome of `name`. Anything
    // but a ready URL clears the element: it may still be holding the sample of
    // the name the customer just replaced.
    showNameSampleResult(name) {
        const result = this.nameSampleResults.get(name);
        if (result && result !== "pending" && result !== "failed") {
            this.applyNameSample(result);
            return;
        }
        const audio = this.getModalRoot().querySelector("[data-name-sample-audio]");
        if (audio) audio.removeAttribute("src");
        this.setNameSampleState(result === "failed" ? "failed" : "preparing");
    }

    nameSampleDelay(milliseconds) {
        return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
    }

    // The endpoint hangs off the personalization session, so it goes through
    // sessionRequest: a rotated or locked session (409/404) and the bot gate's
    // challenge_required 403 heal there exactly as they do for the preview.
    // Any non-2xx — the poll limiter's 429 above all — ends the attempt quietly.
    // The set and the book name the Hörbuch to speak with by its SKU (nameSampleSku):
    // in the query, which the app proxy signs (a body value would override it), and
    // so on the path every replay above re-sends. The SKU is the render's, never the
    // answer's: a "Nein" while the render is still being polled must not send the
    // next poll to the book's session without it — bookpipe would answer that
    // "unavailable", and the name would stay failed for this draft.
    async nameSampleRequest(method) {
        const audiobookSku = this.nameSampleSku();
        const audiobookQuery = audiobookSku !== "" ? `?audiobook_sku=${encodeURIComponent(audiobookSku)}` : "";
        try {
            const response = await this.sessionRequest(`/audio-sample${audiobookQuery}`, () => ({
                method,
                headers: this.sessionHeaders(),
                signal: this.timeoutSignal(PERSONALIZATION_NAME_SAMPLE_REQUEST_TIMEOUT_MS),
            }));
            if (!response || !response.ok) return null;
            return await this.parseJsonResponse(response);
        } catch (error) {
            return null;
        }
    }

    applyNameSample(url) {
        const audio = this.getModalRoot().querySelector("[data-name-sample-audio]");
        if (!audio || !url) {
            this.setNameSampleState("failed");
            return;
        }
        // A plain cross-origin <audio src> — deliberately without a crossorigin
        // attribute, which would make the browser demand CORS headers the signed
        // bookpipe URL does not send. Only for another sample: shown again (the preview
        // entered anew), the element may be playing this very one, and assigning it once
        // more would re-run the media load and stop it.
        if (audio.getAttribute("src") !== url) audio.src = url;
        // The button mirrors the media element (bindNameSampleControls): after a load it
        // stands paused, and a sample left playing keeps its pause label.
        this.setNameSampleState(audio.paused ? "ready" : "playing");
    }

    setNameSampleState(state) {
        const root = this.getModalRoot();
        const button = root.querySelector("[data-name-sample-button]");
        if (!button) return;
        const labelKey = state === "playing" ? "pause" : state === "ready" ? "ready" : state === "preparing" ? "preparing" : "failed";
        const label = button.querySelector("[data-name-sample-label]");
        const text = button.getAttribute(`data-label-${labelKey}`);
        if (label && text) label.textContent = text;
        button.setAttribute("aria-pressed", String(state === "playing"));
        button.disabled = state === "preparing" || state === "failed";
        // The state for the player's styles, on its block: its bars move only while the sample plays.
        root.querySelector("[data-name-sample]")?.setAttribute("data-name-sample-state", state);
        // The Hörbuch cover plays the sample too (bindNameSampleControls): named as the player's label
        // reads - what a click on it does now - and pressed while the sample plays. Never disabled itself,
        // so it stays in the tab order: where no sample will come, it tells assistive tech a click does
        // nothing.
        const trigger = root.querySelector("[data-name-sample-trigger]");
        if (!trigger) return;
        if (text) trigger.setAttribute("aria-label", text);
        trigger.setAttribute("aria-pressed", String(state === "playing"));
        trigger.setAttribute("aria-disabled", String(state === "failed"));
    }

    bindNameSampleControls() {
        const root = this.getModalRoot();
        const button = root.querySelector("[data-name-sample-button]");
        const audio = root.querySelector("[data-name-sample-audio]");
        if (!button || !audio) return;

        const toggle = () => {
            if (audio.paused) {
                audio.play().catch(() => this.setNameSampleState("ready"));
            } else {
                audio.pause();
            }
        };
        button.addEventListener("click", toggle);
        // The Hörbuch cover beside the book carries a play circle in its picture, which invites a click
        // (Markus 01.10.2026): it is one more button of this player, never a second one. Never disabled
        // itself - it stays in the tab order - it does what the player does, and so nothing while the
        // player has nothing to play: the sample still being prepared, or unavailable. The player's state
        // decides, not the audio element: a corrected name's sample is prepared while the old name's is
        // still loaded.
        root.querySelector("[data-name-sample-trigger]")?.addEventListener("click", () => {
            if (!button.disabled) toggle();
        });
        // The button mirrors the MEDIA element, not the click: a rejected play(),
        // an OS media key or the sample simply ending must all put the label back.
        audio.addEventListener("play", () => {
            this.setNameSampleState("playing");
            this.trackPersonalization("personalization_audio_sample_played", { trigger: "name_sample" });
        });
        // "ready" only while there is something to play: a reset clears the src,
        // and the pause this triggers must not re-enable an empty button.
        const reflectStopped = () => this.setNameSampleState(audio.getAttribute("src") ? "ready" : "preparing");
        audio.addEventListener("pause", reflectStopped);
        audio.addEventListener("ended", reflectStopped);
    }

    // The child's name on the Hörbuch cover, whose picture leaves it out: Liquid renders the
    // cover before the customer types the name, hidden, so the wizard writes in the name entered
    // by now - as text, never as markup - on every cover marked for it (the Hörbuch's first step's,
    // the Hörbuch step's, the preview's) and hides it while there is none. Run on every step the
    // wizard activates: cheap, and no cover shows without one - the fresh-draft reset, ending on its
    // first step, included - and on every input into the name field, under which the Hörbuch's
    // first step shows its cover.
    // The name also waits for the cover's picture: written over a picture still loading, it stood
    // alone on an empty square (Markus 06.10.2026). The picture's load runs this again - its failure
    // too, after which the name shows as it always did - through one shared handler, so the runs on
    // every keystroke add no further listeners.
    syncAudiobookCoverNames() {
        const name = String(this.getEntryValue(this.collectEntries(), "main_character") || "").trim();
        if (!this.resyncAudiobookCoverNames) {
            this.resyncAudiobookCoverNames = () => this.syncAudiobookCoverNames();
        }
        this.getModalRoot()
            .querySelectorAll("[data-audiobook-cover-name]")
            .forEach((element) => {
                const picture = element.parentNode?.querySelector("img");
                const pictureLoading = picture?.complete === false;
                if (pictureLoading) {
                    picture.addEventListener("load", this.resyncAudiobookCoverNames, { once: true });
                    picture.addEventListener("error", this.resyncAudiobookCoverNames, { once: true });
                }
                element.textContent = name;
                element.toggleAttribute("hidden", !name || pictureLoading);
            });
    }

    // Every picture in the wizard's steps is lazy, and a lazy picture in a hidden step starts to
    // load only once its step shows - where the CDN has not cut that size yet, a second or more in
    // which the step stands without it (Markus 06.10.2026: the Hörbuch cover). Whoever opens the
    // wizard walks into those steps: their pictures - a dozen small files - load now, while the
    // customer still types the name.
    warmWizardImages() {
        this.getModalRoot()
            .querySelectorAll('img[loading="lazy"]')
            .forEach((image) => image.setAttribute("loading", "eager"));
    }

    // ------------------------------------------------------------------
    // Per-field server errors (AI photo gate, strict input validation)
    // ------------------------------------------------------------------

    // A message of the root with its {tokens} filled in: the copy comes from the
    // locale files, the values from the script.
    formatMessage(messageKey, fallback, values = {}) {
        let message = this.getMessage(messageKey, fallback);
        Object.keys(values).forEach((token) => {
            message = message.split(`{${token}}`).join(String(values[token]));
        });
        return message;
    }

    formatFieldMessage(messageKey, field, fallback, replacements = {}) {
        return this.formatMessage(messageKey, fallback, { label: field.label, ...replacements });
    }

    // Maps a Laravel validation error bag to the wizard's fields:
    // images.<key> → photo dropzone, variables.<key> → text input (dedication
    // errors belong to the dedication step). Every field is stamped with the KEY of
    // the step that owns it — handlePreviewFieldErrors resolves that to the step id
    // this product renders it as. `general` holds what names no field at all
    // (generalErrorMessages); a field the customer cannot see - the hash the
    // wizard fills in itself, a photo or option this product does not ask for -
    // is left out, its message would name it ("Bitte fülle das Feld „Hash“ aus").
    // Returns null when nothing maps to a field, so the caller falls back to the
    // generic error handling.
    extractPreviewFieldErrors(data) {
        const errors = data?.errors;
        if (!errors || typeof errors !== "object") return null;

        const textKeys = new Set(this.getRenderableTextFields().map((field) => field.key));
        const uploadKeys = new Set(this.getUploadFields().map((field) => field.key));
        const fields = new Map();

        Object.keys(errors).forEach((errorKey) => {
            const rawMessages = Array.isArray(errors[errorKey]) ? errors[errorKey] : [errors[errorKey]];
            const messages = rawMessages.map((message) => String(message || "")).filter(Boolean);
            if (messages.length === 0) return;

            const imageMatch = errorKey.match(/^images\.(.+)$/);
            const variableMatch = errorKey.match(/^variables\.(.+)$/);

            if (imageMatch && uploadKeys.has(imageMatch[1])) {
                fields.set(imageMatch[1], { messages, step: PERSONALIZATION_STEP_KEYS.CHARACTER });
            } else if (variableMatch && textKeys.has(variableMatch[1])) {
                fields.set(variableMatch[1], { messages, step: PERSONALIZATION_STEP_KEYS.CHARACTER });
            } else if (variableMatch && variableMatch[1] === PERSONALIZATION_DEDICATION_KEY) {
                fields.set(variableMatch[1], { messages, step: PERSONALIZATION_STEP_KEYS.DEDICATION });
            }
        });

        if (fields.size === 0) return null;

        return { fields, general: this.generalErrorMessages(errors) };
    }

    // The messages of a Laravel validation error bag that name no field of the
    // session - neither images.<key> nor variables.<key> - but refuse the request
    // as such (an unavailable product, say): the customer reads them, whatever
    // else the refusal names.
    generalErrorMessages(errors) {
        return Object.keys(errors)
            .filter((errorKey) => !/^(images|variables)\./.test(errorKey))
            .flatMap((errorKey) => (Array.isArray(errors[errorKey]) ? errors[errorKey] : [errors[errorKey]]))
            .map((message) => String(message || ""))
            .filter(Boolean);
    }

    // The preview's error for inputs that were not taken - refused by bookpipe,
    // or a photo refused before the run went out: a later preview attempt must
    // resubmit even when the values look unchanged.
    showPreviewInputsRefused() {
        this.previewSignature = null;
        this.setPreviewState("error", {
            message: this.getMessage("preview-field-errors", "Einige Angaben wurden nicht akzeptiert. Bitte überprüfe die markierten Felder."),
            analyticsReason: "field_errors",
        });
    }

    handlePreviewFieldErrors(result, options = {}) {
        this.showPreviewInputsRefused();

        // A background (prefetch) rejection stays invisible: the customer is
        // busy on step 2 and must not be yanked to another step mid-keystroke.
        // The nulled signature above makes the step-3 entry resubmit, which
        // reproduces these errors through this same handler — then visibly.
        if (options.navigate === false) return;

        const allMessages = [];
        result.fields.forEach(({ messages }) => allMessages.push(...messages));
        allMessages.push(...result.general);

        // A rejected photo or name outranks a rejected dedication: it is the step the
        // customer has to fix first.
        const targetKey = Array.from(result.fields.values()).some((entry) => entry.step === PERSONALIZATION_STEP_KEYS.CHARACTER)
            ? PERSONALIZATION_STEP_KEYS.CHARACTER
            : PERSONALIZATION_STEP_KEYS.DEDICATION;
        const targetStep = this.stepIdForKey(targetKey);
        // Activate first: entering the step re-hydrates its inputs (which
        // dispatches input events that clear field errors), so the rejection
        // messages must be applied afterwards.
        this.activateStep(targetStep, { focusTitle: true });
        this.applyFieldErrors(result.fields);
        this.updateErrors(targetStep, allMessages);
    }

    // A sync the session refused ends the preview pass with the refusal: its
    // field errors at their fields, anything else as the preview's error - the
    // sync's message is either one of our locale texts or a localized bookpipe
    // app-proxy message.
    showSyncRefusal(syncResult, background) {
        const fieldErrors = syncResult.errorData ? this.extractPreviewFieldErrors(syncResult.errorData) : null;
        if (!fieldErrors) {
            throw new PersonalizationLocalizedError(
                syncResult.message || this.getMessage("preview-error", "Die Vorschau konnte nicht erstellt werden. Bitte versuche es erneut.")
            );
        }
        this.handlePreviewFieldErrors(fieldErrors, { navigate: !background });
    }

    // The customer at the photos the preview needs and lacks - refused (by
    // bookpipe or by the browser) or removed on her way to the preview step -
    // with why: the refusal's reason, kept in serverFieldErrors, or that a photo
    // is needed. She asked for the preview step herself, so this is no yank off
    // a step she is typing on (refusePhoto's notice). Entering step 1 re-hydrates
    // its inputs, so the messages follow the step change (handlePreviewFieldErrors).
    showMissingPhotos() {
        const fields = new Map(
            this.missingRequiredPhotos().map((field) => [
                field.key,
                { messages: this.serverFieldErrors.get(field.key) || [this.photoRequiredMessage(field)], step: PERSONALIZATION_STEP_KEYS.CHARACTER },
            ])
        );
        this.handlePreviewFieldErrors({ fields, general: [] });
    }

    // A photo the session never received - its upload gave up for good, and the
    // dropzone says so, with a retry of its own: a post would only be refused
    // for it. The customer reads the connection's message, with the preview's
    // retry. The upload reported its failure itself (sendPhotoToSession).
    showPhotosNotDelivered() {
        this.setPreviewState("error", {
            message: this.getMessage(
                "network-error",
                "Die Verbindung zum Server ist fehlgeschlagen. Bitte prüfe deine Internetverbindung und versuche es erneut."
            ),
            analyticsReason: "network",
        });
    }

    // The inputs a refused preview run (422) names - bookpipe's keys, and the
    // photos among them - when the wizard holds every one of them as it sends
    // them: a photo it has, a step-1 text whose value passes the wizard's own
    // rules, the hash it fills in itself. That is what the session lost on the
    // way, and sending it once more heals it. Null when the refusal names
    // anything else (a value the rules reject, a photo she removed, no field at
    // all): that refusal is the customer's to read.
    refusedInputsToResend(data) {
        const keys = Object.keys(data?.errors || {});
        const textFields = new Map(this.getRenderableTextFields().map((field) => [field.key, field]));
        const entries = this.collectEntries();
        const photoOf = (errorKey) => errorKey.match(/^images\.(.+)$/)?.[1];
        const held = (errorKey) => {
            const photoKey = photoOf(errorKey);
            if (photoKey) return this.photoFiles.has(photoKey);
            const variableKey = errorKey.match(/^variables\.(.+)$/)?.[1];
            if (variableKey === PERSONALIZATION_HASH_KEY) return true;
            const field = textFields.get(variableKey);
            return Boolean(field) && this.textFieldErrors(field, String(this.getEntryValue(entries, field.key) || "").trim()).length === 0;
        };
        if (keys.length === 0 || !keys.every(held)) return null;
        return { keys, photos: keys.map(photoOf).filter(Boolean) };
    }

    // Sends what a refused preview run named once more: the photos' uploads to
    // the session are forgotten, so the full sync sends them again - a sync that
    // starts after that. One of this session still under way started before and
    // may have gone past a photo while the session still counted as having it:
    // joined, it would end without that photo, and the post would meet a second
    // refusal. It is waited out instead.
    async resendRefusedInputs({ keys, photos }) {
        // Countable in Clarity and the browser log, by bookpipe's keys only:
        // what the customer entered never goes to a tracker or a log.
        setPersonalizationClarityTag("pers_preview_resynced", "true");
        console.warn("PersonalizationModal: preview refused for missing inputs, re-synced", { keys });
        photos.forEach((key) => this.forgetUploadedPhoto(key));
        if (this.pendingFullSync?.epoch === this.sessionEpoch) {
            await this.pendingFullSync.promise;
        }
        return this.sharedSyncAllToSession();
    }

    applyFieldErrors(fields) {
        this.clearAllFieldErrors();
        fields.forEach(({ messages }, key) => {
            this.serverFieldErrors.set(key, messages);
            const wrapper = this.getFieldWrapper(key);
            if (!wrapper) return;
            wrapper.classList.add("personalization-field--rejected");
            const errorEl = wrapper.querySelector("[data-field-error]");
            if (errorEl) {
                errorEl.textContent = messages.join(" ");
                errorEl.removeAttribute("hidden");
            }
            wrapper.querySelector("[data-photo-dropzone]")?.classList.add("has-error");
            wrapper.querySelector("[data-field-input]")?.classList.add("has-error");
        });
    }

    // A photo refused while the customer is on a later step: the reason shows
    // in that step's error box, with a button back to the photo (handleDocClick
    // follows its data-goto-step). Announced as an alert, but without taking
    // the focus, the scroll position or a shake: she may be typing on that step,
    // and a focus move closes a phone's keyboard mid-word.
    showPhotoRejectionNotice(stepId, messages) {
        this.updateErrors(stepId, messages, /*silentOnValid*/ true);
        const container = this.getModalRoot().querySelector(".personalization-steps-errors");
        if (!container) return;
        const button = document.createElement("button");
        button.setAttribute("type", "button");
        button.setAttribute("class", "personalization-photo-change");
        button.setAttribute("data-goto-step", this.characterStepId());
        button.textContent = this.getMessage("photo-change-after-rejection", "Foto ändern");
        container.append(button);
    }

    clearFieldError(key) {
        this.serverFieldErrors.delete(key);
        const wrapper = this.getFieldWrapper(key);
        if (!wrapper) return;
        wrapper.classList.remove("personalization-field--rejected");
        const errorEl = wrapper.querySelector("[data-field-error]");
        if (errorEl) {
            errorEl.textContent = "";
            errorEl.setAttribute("hidden", "");
        }
        wrapper.querySelector("[data-photo-dropzone]")?.classList.remove("has-error");
        wrapper.querySelector("[data-field-input]")?.classList.remove("has-error");
    }

    clearAllFieldErrors() {
        const keys = new Set(this.serverFieldErrors.keys());
        this.getModalRoot()
            .querySelectorAll("[data-field-wrapper][data-field-key]")
            .forEach((wrapper) => keys.add(wrapper.getAttribute("data-field-key")));
        keys.forEach((key) => this.clearFieldError(key));
        this.serverFieldErrors.clear();
    }

    // ------------------------------------------------------------------
    // Bookpipe preview (step 3)
    // ------------------------------------------------------------------

    bindPreviewControls() {
        const root = this.getModalRoot();
        this.previewRetryButton = root.querySelector("[data-preview-retry]");
        if (this.previewRetryButton) {
            this.previewRetryButton.addEventListener("click", this.boundHandlePreviewRetry);
        }
        this.previewPrevButton = root.querySelector("[data-preview-prev]");
        this.previewNextButton = root.querySelector("[data-preview-next]");
        this.previewTrack = root.querySelector("[data-preview-track]");
        if (this.previewPrevButton) {
            this.previewPrevButton.addEventListener("click", this.boundHandlePreviewPrev);
        }
        if (this.previewNextButton) {
            this.previewNextButton.addEventListener("click", this.boundHandlePreviewNext);
        }
        if (this.previewTrack) {
            this.previewTrack.addEventListener("scroll", this.boundHandlePreviewTrackScroll, { passive: true });
        }
    }

    unbindPreviewControls() {
        if (this.previewRetryButton) {
            this.previewRetryButton.removeEventListener("click", this.boundHandlePreviewRetry);
        }
        if (this.previewPrevButton) {
            this.previewPrevButton.removeEventListener("click", this.boundHandlePreviewPrev);
        }
        if (this.previewNextButton) {
            this.previewNextButton.removeEventListener("click", this.boundHandlePreviewNext);
        }
        if (this.previewTrack) {
            this.previewTrack.removeEventListener("scroll", this.boundHandlePreviewTrackScroll);
        }
    }

    handlePreviewRetry(event) {
        event.preventDefault();
        this.trackPersonalization("personalization_preview_retry");
        this.ensurePreview({ force: true });
    }

    handlePreviewPrev(event) {
        event.preventDefault();
        this.scrollPreviewToPage(this.activePreviewPage - 1);
    }

    handlePreviewNext(event) {
        event.preventDefault();
        this.scrollPreviewToPage(this.activePreviewPage + 1);
    }

    // ------------------------------------------------------------------
    // Preview page gallery (rendered preview-template pages from bookpipe)
    // ------------------------------------------------------------------

    renderPreviewGallery(personalization) {
        const root = this.getModalRoot();
        const gallery = root.querySelector("[data-preview-gallery]");
        const track = root.querySelector("[data-preview-track]");
        const dots = root.querySelector("[data-preview-dots]");
        const fallbackImage = root.querySelector("[data-preview-image]");
        const pages = (personalization?.preview_pages || []).filter((page) => page?.url);

        if (!gallery || !track || pages.length === 0) {
            // No rendered pages (feature off, or the product has no preview
            // template): fall back to the raw generated image as before.
            // Clear any previous gallery render so stale slides don't sit in
            // the hidden track (memory + previously-fetched resources).
            gallery?.setAttribute("hidden", "");
            gallery?.classList.remove("personalization-preview-gallery-single");
            if (track) track.innerHTML = "";
            if (dots) dots.innerHTML = "";
            this.previewPageCount = 0;
            this.activePreviewPage = 0;
            const previewImages = personalization?.preview_images || [];
            const firstImage = previewImages.find((img) => img?.url);
            if (fallbackImage && firstImage) {
                fallbackImage.src = firstImage.url;
                fallbackImage.removeAttribute("hidden");
            } else {
                fallbackImage?.setAttribute("hidden", "");
            }
            return;
        }

        fallbackImage?.setAttribute("hidden", "");

        const altTemplate = gallery.getAttribute("data-page-alt-template") || "";
        const dotTemplate = gallery.getAttribute("data-page-dot-template") || "";

        track.innerHTML = "";
        if (dots) dots.innerHTML = "";

        pages.forEach((page, index) => {
            const pageNumber = index + 1;
            const slide = document.createElement("div");
            slide.className = "personalization-preview-slide";

            const image = document.createElement("img");
            image.className = "personalization-preview-page";
            // loading/decoding must be set BEFORE src — assigning src first
            // starts the fetch immediately and the lazy hint is ignored.
            image.loading = index === 0 ? "eager" : "lazy";
            image.decoding = "async";
            image.alt = altTemplate.replace("@page@", String(pageNumber));
            image.src = page.url;
            slide.appendChild(image);
            track.appendChild(slide);

            if (dots && pages.length > 1) {
                const dot = document.createElement("button");
                dot.type = "button";
                dot.className = "personalization-preview-dot";
                dot.setAttribute("aria-label", dotTemplate.replace("@page@", String(pageNumber)));
                dot.addEventListener("click", () => this.scrollPreviewToPage(index));
                dots.appendChild(dot);
            }
        });

        this.previewPageCount = pages.length;
        this.activePreviewPage = 0;
        gallery.classList.toggle("personalization-preview-gallery-single", pages.length === 1);
        gallery.removeAttribute("hidden");
        track.scrollLeft = 0;
        this.updatePreviewGalleryState();
    }

    scrollPreviewToPage(index) {
        const track = this.previewTrack || this.getModalRoot().querySelector("[data-preview-track]");
        // A collapsed track (display:none ancestor) cannot scroll — bail
        // without touching the recorded page so state and view never split.
        if (!track || this.previewPageCount === 0 || track.clientWidth === 0) return;
        const target = Math.max(0, Math.min(this.previewPageCount - 1, index));
        // Instant scroll on purpose: with scroll-snap-type "mandatory",
        // browsers cancel programmatic SMOOTH scrolling and re-snap to the
        // current page (verified in Chrome) — the arrows/dots would simply
        // not move. Swiping stays native and smooth either way.
        track.scrollLeft = target * track.clientWidth;
        this.activePreviewPage = target;
        this.updatePreviewGalleryState();
    }

    handlePreviewTrackScroll() {
        const track = this.previewTrack;
        if (!track || track.clientWidth === 0 || this.previewPageCount === 0) return;
        const index = Math.round(track.scrollLeft / track.clientWidth);
        const clamped = Math.max(0, Math.min(this.previewPageCount - 1, index));
        if (clamped !== this.activePreviewPage) {
            this.activePreviewPage = clamped;
            this.updatePreviewGalleryState();
        }
    }

    updatePreviewGalleryState() {
        const root = this.getModalRoot();
        const prev = this.previewPrevButton || root.querySelector("[data-preview-prev]");
        const next = this.previewNextButton || root.querySelector("[data-preview-next]");
        if (prev) prev.disabled = this.activePreviewPage <= 0;
        if (next) next.disabled = this.activePreviewPage >= this.previewPageCount - 1;
        root.querySelectorAll("[data-preview-dots] .personalization-preview-dot").forEach((dot, index) => {
            dot.classList.toggle("is-active", index === this.activePreviewPage);
            dot.setAttribute("aria-current", index === this.activePreviewPage ? "true" : "false");
        });
    }

    collectEntries() {
        const root = this.getModalRoot();
        const forms = root.querySelectorAll(".personalization-step-form");
        const combinedEntries = {};
        forms.forEach((f) => {
            const fd = new FormData(f);
            for (let [key, value] of fd.entries()) {
                if (typeof value === "string") {
                    combinedEntries[key] = value;
                }
            }
        });
        return combinedEntries;
    }

    getPhotoFingerprint() {
        const parts = [];
        Array.from(this.photoFiles.keys())
            .sort()
            .forEach((key) => {
                const file = this.photoFiles.get(key);
                if (file) {
                    parts.push(`${key}=${file.name}:${file.size}:${file.lastModified}`);
                }
            });
        return parts.join("|");
    }

    // The ONE place the selected cover is derived, so every reader agrees no matter in
    // which order they run (trigger click, field config, drawer show, preview).
    async getSelectedVariantId() {
        if (this.editMode) {
            const editLineUuid = String(this.getSavedState()?.["__line_uuid"] || "");
            if (editLineUuid) {
                const cart = await this.getCart();
                const lineItem = cart?.items?.find((item) => item.properties?.["__line_uuid"] === editLineUuid);
                if (lineItem?.variant_id) {
                    return String(lineItem.variant_id);
                }
            }
        }

        if (this.coverStepEnabled()) {
            // The draft's own answer outranks the product form input — the wizard
            // WRITES that input (applyCoverVariant), so it can only ever confirm what
            // the wizard already decided.
            const selectedCoverType = this.selectedCoverType();
            if (selectedCoverType) {
                return String(this.variantIdForCoverType(selectedCoverType));
            }
            // No choice in the draft yet, but a session bound to one of the two SKUs:
            // that is a pre-cover-step draft, and its cover stays (no rotation —
            // ensureSessionForActiveVariant then compares the SKU against itself).
            const sessionCoverType = this.coverTypeForSessionSku();
            if (sessionCoverType) {
                return String(this.variantIdForCoverType(sessionCoverType));
            }
        }

        const productFormInput = document.querySelector('product-form form input[name="id"]');
        if (productFormInput?.value) {
            return String(productFormInput.value);
        }

        const urlVariant = new URLSearchParams(window.location.search || "").get("variant");
        if (urlVariant) {
            return String(urlVariant);
        }

        return String(this.variantId || this.softcoverVariantId || "");
    }

    // why: the cover of a set's variant as well - the set's line is edited in the book's wizard of
    // its own product (snippets/edit-book.liquid), where the line's own variant (getSelectedVariantId)
    // is one of the set's, and its Kombi-Hardcover has to bind the book's Hardcover SKU, not the
    // Softcover's.
    resolveCoverTypeForVariant(variantId) {
        if (this.hardcoverVariantId && String(variantId) === String(this.hardcoverVariantId)) {
            return "hardcover";
        }
        if (this.setHardcoverVariantId && String(variantId) === String(this.setHardcoverVariantId)) {
            return "hardcover";
        }
        return "softcover";
    }

    variantIdForCoverType(coverType) {
        return coverType === "hardcover" ? this.hardcoverVariantId : this.softcoverVariantId;
    }

    // The variant a line is bought as for the cover - the one place that picks it: the set's
    // variant of the cover when the line is the set, the page's own otherwise. The session stays
    // the book's either way (resolveSkuForCoverType).
    cartVariantIdForCoverType(coverType, wantsSet) {
        if (!wantsSet) return this.variantIdForCoverType(coverType);
        return coverType === "hardcover" ? this.setHardcoverVariantId : this.setSoftcoverVariantId;
    }

    // The cover the customer picked in the wizard, as stored in the draft. Only the two
    // values the cover step can produce count — anything else is a foreign/legacy key.
    selectedCoverType() {
        const coverType = String(this.getSavedState()?.["__cover_type"] || "");
        return coverType === "softcover" || coverType === "hardcover" ? coverType : null;
    }

    // The cover a draft from before the cover step was on: its session is bound to the
    // SKU of the variant the customer had selected on the product page. Reading it back
    // keeps that choice (and the paid session) alive across the theme switch instead of
    // rotating the draft to softcover. A SKU that belongs to neither cover returns null
    // and rotates exactly as it did before.
    coverTypeForSessionSku() {
        const sessionSku = String(this.getSavedState()?.["__bp_session_sku"] || "");
        if (!sessionSku) return null;
        if (this.hardcoverSku && sessionSku === this.hardcoverSku) return "hardcover";
        if (this.softcoverSku && sessionSku === this.softcoverSku) return "softcover";
        return null;
    }

    resolveSkuForCoverType(coverType) {
        // The Hörbuch is a product of its own in bookpipe, whichever variant of the
        // book the page sells it as: its config, session and name sample go by its SKU.
        if (this.isAudiobook() && this.audiobookSku) return this.audiobookSku;
        // Bookpipe resolves the product from this SKU alone, so never
        // substitute the other cover's SKU — a missing SKU must fail the
        // preview loudly instead of silently binding the wrong product.
        if (coverType === "hardcover") {
            return this.hardcoverSku || "";
        }

        return this.softcoverSku || "";
    }

    getOrCreateLineUuid() {
        const state = this.getSavedState() || {};
        let lineUuid = String(state["__line_uuid"] || "");
        if (!lineUuid) {
            lineUuid = window.crypto.randomUUID();
            this.saveState({ "__line_uuid": lineUuid });
        }
        return lineUuid;
    }

    // The browser-scoped customer session id sent as external_session_id:
    // unlike the per-product-page line uuid it spans EVERY product page, so
    // the backend pools the preview budgets per browser and reuses paid
    // generation outputs across cover/title switches. Stored under a global
    // localStorage key — deliberately outside the per-product draft state,
    // so resetSession() and draft sweeps never clear it. The line uuid
    // stays what it is: the cart-line identity (__line_uuid properties,
    // edit-from-cart matching, photo store keys) must remain unique per
    // product page.
    getOrCreateCustomerSessionId() {
        const key = "bookpipe:customer-session:v1";
        try {
            let id = String(window.localStorage.getItem(key) || "");
            if (!id) {
                id = window.crypto.randomUUID();
                window.localStorage.setItem(key, id);
            }
            return id;
        } catch (e) {
            // Storage unavailable (private mode, quota): a per-load id still
            // groups this page's sessions, it just cannot span pages.
            if (!window.__bookpipeCustomerSessionId) {
                window.__bookpipeCustomerSessionId = window.crypto.randomUUID();
            }
            return window.__bookpipeCustomerSessionId;
        }
    }

    getOrCreateCertificateHash() {
        const state = this.getSavedState() || {};
        let hash = String(state["__certificate_hash"] || "");
        if (!hash) {
            hash = window.crypto.randomUUID().replace(/-/g, "");
            this.saveState({ "__certificate_hash": hash });
        }
        return hash;
    }

    // Bookpipe validates variables strictly against the product's configured
    // personalization option keys and rejects unknown or non-scalar keys, so
    // only keys advertised by the product config are sent — empty optional
    // values are omitted (the server substitutes the option's Standardwert).
    buildBookpipeVariables(entries) {
        const variables = {};
        const config = this.fieldConfig || this.buildFallbackFieldConfig();

        config.textFields.forEach((field) => {
            if (field.key === PERSONALIZATION_HASH_KEY) {
                // Hidden option: random per-personalization hash, generated in
                // the background and kept stable across edits — bookpipe uses
                // it to build the unique superhero-certificate URL. The same
                // value goes on the cart line as __certificate_hash.
                variables[field.key] = this.getOrCreateCertificateHash();
                return;
            }

            if (field.key === PERSONALIZATION_DEDICATION_KEY) {
                const addDedication = String(entries["__add-dedication"]) === "true";
                const dedication = addDedication ? String(entries["__dedication"] || "").trim() : "";
                if (dedication !== "") {
                    variables[field.key] = dedication;
                }
                return;
            }

            if (field.inputType !== "text" && field.inputType !== "textarea") return;

            const value = String(this.getEntryValue(entries, field.key) || "").trim();
            if (value !== "") {
                variables[field.key] = value;
            }
        });

        return variables;
    }

    buildPersonalizationIdempotencyKey(lineUuid, variables, requestContext) {
        const source = JSON.stringify(variables) + "|" + (requestContext || "") + "|" + this.getPhotoFingerprint();
        let hash = 0;

        for (let i = 0; i < source.length; i += 1) {
            hash = (hash << 5) - hash + source.charCodeAt(i);
            hash |= 0;
        }

        return `shopify:${lineUuid}:${Math.abs(hash).toString(36)}`;
    }

    // Mirrors the server's preview relevance: the dedication is a book-only
    // field — its page is not part of the preview templates, so the server's
    // input hash ignores it. Keeping it out of the client signature (and the
    // idempotency key) means a dedication written on step 2 never invalidates
    // the run prefetched on the 1→2 move: step 3 adopts/replays that run
    // instead of re-triggering. The flip side: this signature no longer
    // forces a preview pass (and its full sync) after a dedication edit —
    // handleAddToCart reconciles the session explicitly instead.
    previewRelevantVariables(variables) {
        const relevant = { ...variables };
        delete relevant[PERSONALIZATION_DEDICATION_KEY];
        return relevant;
    }

    async buildPreviewSignature() {
        const entries = this.collectEntries();
        const variantId = await this.getSelectedVariantId();
        const variables = this.previewRelevantVariables(this.buildBookpipeVariables(entries));
        return JSON.stringify({ variables, variantId, photo: this.getPhotoFingerprint() });
    }

    invalidatePreviewIfStale() {
        // Called on input changes; the actual re-generation happens when the
        // customer enters step 3 again (ensurePreview compares signatures).
        if (this.previewState === "ready" || this.previewState === "error") {
            this.updateAddToCartAvailability();
        }
    }

    // options.background: the pass was started by the step-1→2 prefetch (or a
    // session rotation while the customer sits on step 2) — the run proceeds
    // exactly as usual (its whole UI lives inside the hidden step-3 form), but
    // nothing may pull the customer around: a 422 must not navigate them off
    // the step they are on. Step-3 entry then re-enters ensurePreview, which
    // adopts the run (or replays it deduped) when it is healthy, reveals a
    // stored failure as-is (never a silent second spend — see the sticky
    // backgroundPreviewErrorSignature), and resubmits only field-error (422)
    // rejections, whose messages need the full pass to be reproduced and
    // routed to the offending step.
    async ensurePreview(options = {}) {
        const force = options.force === true;
        const background = options.background === true;
        this.previewRunTrigger = background ? "prefetch" : force ? "retry" : "step3";
        // Token discipline makes this pass the only live one, so the flags
        // reliably describe whichever pass later settles a state.
        this.previewPassBackground = background;
        this.previewPassSignature = null;
        this.previewRequestToken += 1;
        const token = this.previewRequestToken;
        let timeoutId = null;

        try {
            // The variables contract derives from the field config; without it
            // the request would be built from an empty field set.
            if (!this.fieldConfig && this.fieldConfigPromise) {
                await this.fieldConfigPromise;
            }
            if (token !== this.previewRequestToken) return;

            // The preview step entered without a photo it needs: there is no run
            // to post, and the customer goes to the photo. A background pass
            // never takes her anywhere - a photo missing there ends it below,
            // like one refused while the pass waited for it.
            if (!background && this.missingRequiredPhotos().length > 0) {
                this.showMissingPhotos();
                return;
            }

            // "Weiter" while the photo is still being prepared: the signature,
            // the sync and the run are the prepared photo's.
            await this.whenPhotosPrepared();
            if (token !== this.previewRequestToken) return;

            const signature = await this.buildPreviewSignature();
            if (token !== this.previewRequestToken) return;

            if (!force && this.previewSignature === signature) {
                if (this.previewState === "ready") {
                    this.updateAddToCartAvailability();
                    return;
                }
                if (this.previewState === "error" && this.backgroundPreviewErrorSignature === signature) {
                    // A prefetched run already failed for exactly these
                    // inputs, invisibly. Re-triggering here would spend a
                    // second run on a byte-identical request before the
                    // customer saw the first failure (deterministic photo
                    // rejections would double every affected customer's
                    // cost). Entering step 3 reveals the stored error as-is;
                    // the retry button re-spends explicitly (force bypasses
                    // this branch and bumps the retry nonce).
                    this.updateAddToCartAvailability();
                    return;
                }
                if (this.previewState === "loading") {
                    this.updateAddToCartAvailability();
                    // The token bump above just invalidated the poll loop that
                    // was serving this run — adopt the run under the new token
                    // instead of leaving "loading" unserved forever. Without a
                    // known run id (the trigger POST hadn't returned yet) fall
                    // through to a full re-trigger; the server dedupes it.
                    if (this.activePreviewPoll && this.activePreviewPoll.signature === signature) {
                        // The adopting pass now serves this signature — a
                        // failure it settles must stick against it.
                        this.previewPassSignature = signature;
                        await this.resolvePreviewResult(
                            { personalization_id: this.activePreviewPoll.id, preview_status: "pending" },
                            token
                        );
                        return;
                    }
                }
            }

            // Retrying after a FAILED run needs a fresh idempotency key (a
            // replayed key returns the failed run as-is), while unchanged-
            // successful state keeps its key so the server replays/dedupes.
            if (signature !== this.previewSignature) {
                this.previewRetryNonce = 0;
            } else if (this.previewState === "error") {
                this.previewRetryNonce += 1;
            }

            this.previewSignature = signature;
            this.previewPassSignature = signature;

            if (this.previewAbortController) {
                this.previewAbortController.abort();
            }
            // The request watchdog times each post alone - the loop below arms
            // it per attempt. The waits before the post (the photo's upload,
            // the sync) may take longer than a post ever may, and a watchdog
            // running through them called the post off before it went out.
            this.previewAbortController = new AbortController();

            this.setPreviewState("loading");
            // Reaching this point means a new run is actually triggered (the
            // same-signature case returned above), so the wait starts over.
            this.restartPreviewProgress();

            const entries = this.collectEntries();
            const variantId = await this.getSelectedVariantId();
            const coverType = this.resolveCoverTypeForVariant(variantId);
            const lineUuid = this.getOrCreateLineUuid();
            const variables = this.buildBookpipeVariables(entries);
            // The bookpipe app proxy identifies the product by its Shopify
            // SKU alone (resolved through the store's SKU mappings), so every
            // previewable variant needs a SKU in the Shopify admin.
            const shopifySku = this.resolveSkuForCoverType(coverType);

            if (!shopifySku) {
                console.error(`Bookpipe preview blocked: the selected ${coverType} variant has no SKU configured in Shopify.`);
                throw new PersonalizationLocalizedError(
                    this.getMessage("preview-error", "Die Vorschau konnte nicht erstellt werden. Bitte versuche es erneut.")
                );
            }

            // Interactive session flow: the draft state already lives
            // server-side — reconcile any remainder, then actively trigger a
            // preview run. The server dedupes by the preview-relevant input
            // hash, so re-entering step 3 without a relevant change never
            // generates (and never spends) anything.
            // Resolved through the SKU check, not plain ensureSession: a
            // reopen can race the theme's async variant switch (the wizard
            // read input[name=id] before the section update landed) and
            // resume the OTHER cover's session — a preview must never be
            // generated against a session whose SKU is no longer the active
            // one. On rotation this call invalidates the run and re-enters
            // ensurePreview; the stale token below ends this pass.
            const sessionId = await this.ensureSessionForActiveVariant();
            if (token !== this.previewRequestToken) return;

            if (sessionId) {
                // In-flight photo uploads to this session first: their verdicts
                // decide the flow. One still going to a replaced session (cover
                // switch mid-upload) can never reach this one — the sync below
                // sends the photo here itself, so it must not hold the pass up.
                // The customer on the preview step waits for her photo now, not
                // for the run yet: the loading area says so until it is posted.
                const uploads = this.currentSessionPhotoUploads();
                const waitsForPhoto = !background && uploads.length > 0;
                if (waitsForPhoto) this.showPreviewWaitingForPhoto();
                await Promise.all(uploads.map((upload) => upload.promise));
                if (token !== this.previewRequestToken) return;

                // A photo it waited for was refused meanwhile - by bookpipe's
                // check or by the browser, once it prepared the photo - and the
                // customer reads why at the photo and on her step already
                // (refusePhoto). Posted without it, the run would only be
                // refused for the missing photo, whose message would take the
                // place of that reason.
                if (this.missingRequiredPhotos().length > 0) {
                    this.showPreviewInputsRefused();
                    return;
                }

                let syncResult = await this.sharedSyncAllToSession();
                if (token !== this.previewRequestToken) return;

                if (syncResult?.stale) {
                    // The session was replaced while that sync ran, without a new
                    // pass taking over (sessionRequest's 409/404 replay — the name
                    // sample's, too): the name may have reached the replaced session
                    // only. Sync the current one once more; a second replacement in a
                    // row is not chased — the generic error, whose retry starts afresh.
                    syncResult = await this.sharedSyncAllToSession();
                    if (token !== this.previewRequestToken) return;
                    if (syncResult?.stale) {
                        throw new PersonalizationLocalizedError(
                            this.getMessage("preview-error", "Die Vorschau konnte nicht erstellt werden. Bitte versuche es erneut.")
                        );
                    }
                }

                if (syncResult && syncResult.ok === false) {
                    this.showSyncRefusal(syncResult, background);
                    return;
                }

                // A photo the sync could not send even with the transport's retry:
                // bookpipe would refuse the run for it. A background pass keeps
                // the failure for the preview step, like any other.
                if (syncResult?.undeliveredPhotos) {
                    this.showPhotosNotDelivered();
                    return;
                }

                // The run goes out next: its progress starts here, not with the
                // wait for the photo.
                if (waitsForPhoto) this.restartPreviewProgress();

                if (this.deferredSiblingRun) {
                    const pending = this.deferredSiblingRun;
                    // No request watchdog may run during a wait that may
                    // legitimately last the full
                    // PERSONALIZATION_PREVIEW_INFLIGHT_WAIT_MAX_MS: it would abort
                    // previewAbortController mid-wait and the POST below would reject
                    // instantly. None is armed before the POST loop, which arms one
                    // per attempt - this keeps it that way.
                    window.clearTimeout(timeoutId);
                    // false (budget exhausted, polling dead) means "generate normally":
                    // a missed adoption costs one image, a stalled wizard costs the sale.
                    await this.waitOutInFlightPreviewRun(pending.sessionId, token);
                    // Superseded mid-wait (preview-step entry, another cover switch):
                    // the successor pass owns the preview — and it must not POST into
                    // the same generation window we were waiting out, so the marker
                    // stays for it.
                    if (token !== this.previewRequestToken) return;
                    // Settled, timed out or polling failed — this pass generates now
                    // either way, so the marker has served its purpose.
                    this.deferredSiblingRun = null;
                }

                const previewForm = new FormData();
                previewForm.append(
                    "idempotency_key",
                    this.buildPersonalizationIdempotencyKey(
                        lineUuid,
                        // Preview-relevant projection, like the signature: a
                        // dedication-only change must replay/dedupe onto the
                        // same run, not derive a fresh key.
                        this.previewRelevantVariables(variables),
                        `${shopifySku}:${variantId}:v2r${this.previewRetryNonce}`
                    )
                );

                let previewResponse;
                let previewData;
                let challengeRetried = false;
                let inFlightRunAwaited = false;
                let refusedInputsResent = false;

                while (true) {
                    // Arm the request watchdog for THIS attempt alone: the loop
                    // posts again after a challenge_required re-verify, a
                    // run_in_flight wait-out and a refusal whose inputs it sent
                    // once more, and every attempt clears its timer once its
                    // answer is in (below) — without this a POST would hang
                    // unbounded on a stalled backend.
                    window.clearTimeout(timeoutId);
                    timeoutId = window.setTimeout(() => this.previewAbortController?.abort(), PERSONALIZATION_PREVIEW_REQUEST_TIMEOUT_MS);

                    previewResponse = await (window.originalFetch || window.fetch)(
                        this.appProxyUrl(`${this.bookpipePersonalizationUrl}/${encodeURIComponent(this.sessionId || sessionId)}/previews`),
                        {
                            method: "POST",
                            headers: this.sessionHeaders(),
                            body: previewForm,
                            signal: this.previewAbortController.signal,
                        }
                    );

                    window.clearTimeout(timeoutId);

                    previewData = await this.parseJsonResponse(previewResponse);
                    if (token !== this.previewRequestToken) return;

                    // Bot gate enabled while this session was unverified:
                    // re-verify against the session verify endpoint and re-run
                    // the preview once (no run was created for the 403). The
                    // heal's own calls carry their own timeout — the watchdog
                    // above is cleared for the whole inter-attempt window, so
                    // an unbounded fetch here would park the loading bar at
                    // ~95% with no error and no retry button.
                    if (this.isChallengeRequired(previewResponse, previewData) && !challengeRetried) {
                        challengeRetried = true;
                        const verified = await this.healChallenge();
                        if (token !== this.previewRequestToken) return;
                        if (verified) continue;
                    }

                    // Another preview run for this session is still in
                    // flight (the customer went back, edited a field and
                    // re-entered step 3 mid-generation): the server enforces
                    // one run at a time, so wait that run out — every status
                    // poll also lets the server settle it if it is overdue —
                    // and re-trigger with the current inputs once it settled.
                    // Only once per pass: a second collision means someone
                    // else keeps triggering (another tab) and gets the
                    // localized 429 message instead of an endless wait.
                    if (
                        previewResponse.status === 429 &&
                        previewData?.code === "run_in_flight" &&
                        !inFlightRunAwaited
                    ) {
                        inFlightRunAwaited = true;
                        const settled = await this.waitOutInFlightPreviewRun(this.sessionId || sessionId, token);
                        if (token !== this.previewRequestToken) return;
                        if (settled) continue;
                    }

                    // bookpipe refused the run for inputs the session lost on
                    // the way although the wizard holds them: send them once
                    // more and post again. Once per pass, on top of the retries
                    // above - a second refusal is the customer's to read.
                    if (previewResponse.status === 422 && !refusedInputsResent) {
                        const resend = this.refusedInputsToResend(previewData);
                        if (resend) {
                            refusedInputsResent = true;
                            const resynced = await this.resendRefusedInputs(resend);
                            if (token !== this.previewRequestToken) return;
                            if (resynced && resynced.ok === false) {
                                this.showSyncRefusal(resynced, background);
                                return;
                            }
                            if (resynced?.undeliveredPhotos) {
                                this.showPhotosNotDelivered();
                                return;
                            }
                            continue;
                        }
                    }

                    break;
                }

                if (!previewResponse.ok) {
                    if (this.isChallengeRequired(previewResponse, previewData)) {
                        throw new PersonalizationLocalizedError(
                            this.getMessage(
                                "challenge-failed",
                                "Deine Anfrage konnte nicht als menschlich bestätigt werden. Bitte lade die Seite neu und versuche es erneut."
                            )
                        );
                    }
                    if (previewResponse.status === 429) {
                        // The app proxy localizes its throttle body (bookpipe
                        // renders ThrottleRequestsException itself for exactly
                        // this reason — the framework default is English).
                        throw new PersonalizationLocalizedError(
                            previewData?.message ||
                                this.getMessage("preview-rate-limit", "Zu viele Vorschau-Anfragen. Bitte warte einen Moment und versuche es dann erneut.")
                        );
                    }
                    if (previewResponse.status === 422) {
                        const fieldErrors = this.extractPreviewFieldErrors(previewData);
                        if (fieldErrors) {
                            this.handlePreviewFieldErrors(fieldErrors, { navigate: !background });
                            return;
                        }
                        // No field the customer sees is named, and Laravel's
                        // summary (message) leads with the first error - maybe
                        // one of a field she cannot see. She reads what names no
                        // field at all (an unavailable product), else the
                        // generic error.
                        const errors = previewData?.errors && typeof previewData.errors === "object" ? previewData.errors : {};
                        if (Object.keys(errors).length > 0) {
                            throw new PersonalizationLocalizedError(
                                this.generalErrorMessages(errors).join(" ") ||
                                    this.getMessage("preview-error", "Die Vorschau konnte nicht erstellt werden. Bitte versuche es erneut.")
                            );
                        }
                    }
                    if (previewResponse.status === 409 || previewResponse.status === 404) {
                        // Locked (already ordered) or purged session: replace
                        // it; the retry re-syncs the full state.
                        this.resetSession();
                        this.previewSignature = null;
                    }
                    throw new PersonalizationLocalizedError(
                        previewData?.message || this.getMessage("preview-error", "Die Vorschau konnte nicht erstellt werden. Bitte versuche es erneut.")
                    );
                }

                if (!previewData?.personalization_id) {
                    throw new PersonalizationLocalizedError(
                        this.getMessage("preview-error", "Die Vorschau konnte nicht erstellt werden. Bitte versuche es erneut.")
                    );
                }

                await this.resolvePreviewResult(previewData, token);
                return;
            }

            // Without a session there is nothing to trigger — surface the
            // friendly error; a retry probes the session again. When the bot
            // gate is what blocked the create, say so: reloading (which drops
            // the stale product-config cache) is the actual remedy, and the
            // generic message would send the customer into a retry loop.
            if (this.challengeMintFailed) {
                throw new PersonalizationLocalizedError(
                    this.getMessage(
                        "challenge-failed",
                        "Deine Anfrage konnte nicht als menschlich bestätigt werden. Bitte lade die Seite neu und versuche es erneut."
                    )
                );
            }
            throw new PersonalizationLocalizedError(
                this.getMessage("preview-error", "Die Vorschau konnte nicht erstellt werden. Bitte versuche es erneut.")
            );
        } catch (error) {
            if (timeoutId) window.clearTimeout(timeoutId);
            if (token !== this.previewRequestToken) return;
            // The request watchdog aborts with an AbortError, AbortSignal
            // .timeout() (poll requests, challenge round-trips) with a
            // TimeoutError — both are "took too long", and only the first was
            // caught here before, so a timed-out poll used to render the
            // engine's English "The operation timed out."
            if (isPersonalizationTimeoutError(error)) {
                this.setPreviewState("error", {
                    message: this.getMessage("preview-timeout", "Die Vorschau dauert gerade länger als erwartet. Bitte versuche es erneut."),
                    analyticsReason: "timeout",
                });
                return;
            }
            // Everything that is not one of our own translated errors gets
            // classified by customerMessage() — rendering a raw error.message
            // here is what put Safari's English "Load failed" in front of
            // customers whenever the network dropped mid-request.
            console.error("PersonalizationModal: Preview generation failed", error);
            this.setPreviewState("error", {
                message: this.customerMessage(error, "preview-error", "Die Vorschau konnte nicht erstellt werden. Bitte versuche es erneut."),
                analyticsReason: isPersonalizationNetworkError(error) ? "network" : "error",
            });
        }
    }

    async resolvePreviewResult(data, token) {
        let personalization = data;
        const pollingStartedAt = Date.now();
        let consecutivePollFailures = 0;
        let nextPollDelayMs = null;

        // Registered so a re-entrant ensurePreview with an unchanged
        // signature can adopt this run instead of re-triggering it.
        this.activePreviewPoll = { id: data.personalization_id, signature: this.previewSignature };

        while (
            token === this.previewRequestToken &&
            (personalization?.preview_status === "pending" || personalization?.preview_status === "generating")
        ) {
            const elapsedMs = Date.now() - pollingStartedAt;
            if (elapsedMs > PERSONALIZATION_PREVIEW_POLL_MAX_TOTAL_MS) {
                this.setPreviewState("error", {
                    message: this.getMessage("preview-timeout", "Die Vorschau dauert gerade länger als erwartet. Bitte versuche es erneut."),
                    analyticsReason: "timeout",
                });
                return;
            }
            const pollCadenceMs =
                elapsedMs < PERSONALIZATION_PREVIEW_POLL_FAST_WINDOW_MS
                    ? PERSONALIZATION_PREVIEW_POLL_INTERVAL_FAST_MS
                    : PERSONALIZATION_PREVIEW_POLL_INTERVAL_MS;
            await new Promise((resolve) => window.setTimeout(resolve, nextPollDelayMs ?? pollCadenceMs));
            nextPollDelayMs = null;

            let response;
            let polled;
            try {
                response = await (window.originalFetch || window.fetch)(
                    this.appProxyUrl(`${this.bookpipePersonalizationUrl}/${encodeURIComponent(personalization.personalization_id)}`),
                    {
                        method: "GET",
                        headers: {
                            Accept: "application/json",
                            "X-Requested-With": "XMLHttpRequest",
                        },
                        // Bound each poll request; otherwise one hung GET
                        // would suspend the loop past the total cap above.
                        signal:
                            typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function"
                                ? AbortSignal.timeout(PERSONALIZATION_PREVIEW_POLL_REQUEST_TIMEOUT_MS)
                                : undefined,
                    }
                );
                polled = await this.parseJsonResponse(response);
            } catch (pollError) {
                if (token !== this.previewRequestToken) return;
                // Transient network hiccup or per-request timeout: the run is
                // likely still healthy server-side, so keep polling (the
                // total cap bounds the loop).
                consecutivePollFailures += 1;
                if (consecutivePollFailures >= PERSONALIZATION_PREVIEW_POLL_MAX_CONSECUTIVE_FAILURES) {
                    throw pollError;
                }
                nextPollDelayMs = PERSONALIZATION_PREVIEW_POLL_INTERVAL_MS;
                continue;
            }
            if (token !== this.previewRequestToken) return;
            if (!response.ok) {
                // The status limiter's bucket can be shared behind the
                // Shopify proxy: a 429 never means the run failed, so back
                // off (honoring Retry-After) instead of surfacing an error.
                if (response.status === 429) {
                    const retryAfterSeconds = parseInt(response.headers.get("Retry-After") || "", 10);
                    nextPollDelayMs = Math.min(
                        Math.max(
                            Number.isFinite(retryAfterSeconds) ? retryAfterSeconds * 1000 : PERSONALIZATION_PREVIEW_POLL_INTERVAL_MS * 2,
                            PERSONALIZATION_PREVIEW_POLL_INTERVAL_MS
                        ),
                        30000
                    );
                    continue;
                }
                if (response.status >= 500 || response.status === 408) {
                    consecutivePollFailures += 1;
                    if (consecutivePollFailures < PERSONALIZATION_PREVIEW_POLL_MAX_CONSECUTIVE_FAILURES) {
                        nextPollDelayMs = PERSONALIZATION_PREVIEW_POLL_INTERVAL_MS;
                        continue;
                    }
                }
                this.setPreviewState("error", {
                    // polled.message comes from the app proxy, which localizes
                    // its storefront responses (including framework 404s and
                    // throttling) — never a browser-generated string.
                    message: polled?.message || this.getMessage("preview-error", "Die Vorschau konnte nicht geladen werden. Bitte versuche es erneut."),
                    analyticsReason: "poll_error",
                });
                return;
            }
            consecutivePollFailures = 0;
            personalization = polled;
        }

        if (token !== this.previewRequestToken) return;

        if (personalization?.preview_status === "failed" || personalization?.status === "failed") {
            this.setPreviewState("error", {
                message: this.getMessage("preview-failed", "Die Vorschau konnte leider nicht erstellt werden. Bitte überprüfe das Foto und versuche es erneut."),
                analyticsReason: "generation_failed",
            });
            return;
        }

        this.currentPersonalization = personalization;
        this.setPreviewState("ready", { personalization });
    }

    // Polls the session until the preview run that 429-blocked our trigger
    // (code=run_in_flight) reaches a terminal state. Returns true when it
    // settled (ready OR failed — either way the caller re-triggers: the
    // blocking run belongs to OLDER inputs, its result must never be
    // rendered for the current signature); false when the wait budget ran
    // out or polling failed persistently — the caller then surfaces the
    // localized 429 message. Each poll hits the show endpoint, which also
    // performs the server's 180s overdue settle, so even a run whose worker
    // died is guaranteed a verdict inside the wait budget.
    async waitOutInFlightPreviewRun(sessionId, token) {
        if (!sessionId) return false;
        const waitStartedAt = Date.now();
        let consecutivePollFailures = 0;
        let nextPollDelayMs = PERSONALIZATION_PREVIEW_POLL_INTERVAL_FAST_MS;

        while (Date.now() - waitStartedAt < PERSONALIZATION_PREVIEW_INFLIGHT_WAIT_MAX_MS) {
            await new Promise((resolve) => window.setTimeout(resolve, nextPollDelayMs));
            if (token !== this.previewRequestToken) return false;
            nextPollDelayMs = PERSONALIZATION_PREVIEW_POLL_INTERVAL_MS;

            let response;
            let polled;
            try {
                response = await (window.originalFetch || window.fetch)(
                    this.appProxyUrl(`${this.bookpipePersonalizationUrl}/${encodeURIComponent(sessionId)}`),
                    {
                        method: "GET",
                        headers: {
                            Accept: "application/json",
                            "X-Requested-With": "XMLHttpRequest",
                        },
                        signal:
                            typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function"
                                ? AbortSignal.timeout(PERSONALIZATION_PREVIEW_POLL_REQUEST_TIMEOUT_MS)
                                : undefined,
                    }
                );
                polled = await this.parseJsonResponse(response);
            } catch (pollError) {
                if (token !== this.previewRequestToken) return false;
                consecutivePollFailures += 1;
                if (consecutivePollFailures >= PERSONALIZATION_PREVIEW_POLL_MAX_CONSECUTIVE_FAILURES) {
                    return false;
                }
                continue;
            }
            if (token !== this.previewRequestToken) return false;

            if (!response.ok) {
                // Same shared-bucket caveat as the result poll: a 429 here
                // never means the run failed — back off honoring Retry-After.
                if (response.status === 429) {
                    const retryAfterSeconds = parseInt(response.headers.get("Retry-After") || "", 10);
                    nextPollDelayMs = Math.min(
                        Math.max(
                            Number.isFinite(retryAfterSeconds) ? retryAfterSeconds * 1000 : PERSONALIZATION_PREVIEW_POLL_INTERVAL_MS * 2,
                            PERSONALIZATION_PREVIEW_POLL_INTERVAL_MS
                        ),
                        30000
                    );
                    continue;
                }
                consecutivePollFailures += 1;
                if (consecutivePollFailures >= PERSONALIZATION_PREVIEW_POLL_MAX_CONSECUTIVE_FAILURES) {
                    return false;
                }
                continue;
            }

            consecutivePollFailures = 0;
            if (polled?.preview_status !== "pending" && polled?.preview_status !== "generating") {
                return true;
            }
        }

        return false;
    }

    setPreviewState(state, data = {}) {
        const previousState = this.previewState;
        this.previewState = state;
        // Every state transition invalidates the adopt target: terminal
        // states end the run, and a fresh "loading" must not let a re-entrant
        // ensurePreview adopt the PREVIOUS run's (possibly failed) poll —
        // resolvePreviewResult re-registers the live one.
        this.activePreviewPoll = null;

        // Only a failure the customer has NOT seen (settled by a background
        // pass) becomes sticky — see the constructor note. Every other
        // transition clears it. The pass-signature match keeps two cases
        // out: handlePreviewFieldErrors nulls previewSignature first (422s
        // never stick — their step-3 resubmit is what reproduces and
        // displays the field messages), and a pass that died before
        // claiming its signature must not stick to the previous pass's.
        this.backgroundPreviewErrorSignature =
            state === "error" &&
            this.previewPassBackground &&
            this.previewPassSignature !== null &&
            this.previewSignature === this.previewPassSignature
                ? this.previewSignature
                : null;

        // Funnel: one requested event per LOGICAL run. A same-signature
        // fall-through re-trigger (step-3 entry racing the prefetch POST —
        // the server replays it as the same run) re-enters "loading" without
        // being a new run: keep the original trigger and clock so
        // requested/ready/failed pair up 1:1 and stay attributed to what
        // actually started the run. duration_ms spans trigger → terminal
        // state, including the in-flight-collision wait, the wait a rotated
        // session sits out for the cover it switched away from, and the
        // automatic re-POST (one logical run for the customer).
        if (state === "loading") {
            if (previousState !== "loading" || this.previewSignature !== this.analyticsRequestedSignature) {
                this.analyticsPreviewStartedAt = Date.now();
                this.analyticsPreviewTrigger = this.previewRunTrigger || "step3";
                this.analyticsRequestedSignature = this.previewSignature;
                this.trackPersonalization("personalization_preview_requested", {
                    trigger: this.analyticsPreviewTrigger,
                });
            }
        } else if (state === "ready" && previousState === "loading") {
            const pages = data.personalization?.preview_pages || data.personalization?.preview_images || [];
            this.trackPersonalization("personalization_preview_ready", {
                duration_ms: this.analyticsPreviewStartedAt ? Date.now() - this.analyticsPreviewStartedAt : null,
                page_count: Array.isArray(pages) ? pages.length : null,
                trigger: this.analyticsPreviewTrigger,
            });
        } else if (state === "error" && previousState === "loading") {
            this.trackPersonalization("personalization_preview_failed", {
                duration_ms: this.analyticsPreviewStartedAt ? Date.now() - this.analyticsPreviewStartedAt : null,
                reason: data.analyticsReason || "error",
                trigger: this.analyticsPreviewTrigger,
            });
        }
        const root = this.getModalRoot();
        const loadingEl = root.querySelector("[data-preview-loading]");
        const readyEl = root.querySelector("[data-preview-ready]");
        const errorEl = root.querySelector("[data-preview-error]");
        const previewRoot = root.querySelector("[data-preview-root]");

        [loadingEl, readyEl, errorEl].forEach((el) => el?.setAttribute("hidden", ""));

        if (state === "loading") {
            loadingEl?.removeAttribute("hidden");
            this.startPreviewProgress();
        } else {
            this.stopPreviewProgress({ complete: state === "ready" });
        }

        if (state === "ready") {
            readyEl?.removeAttribute("hidden");
            this.renderPreviewGallery(data.personalization);
        }

        if (state === "error") {
            errorEl?.removeAttribute("hidden");
            const messageEl = root.querySelector("[data-preview-error-message]");
            if (messageEl && data.message) {
                messageEl.textContent = data.message;
            }
        }

        this.updatePreviewHeadline(state, previewRoot);
        this.updateAddToCartAvailability();
    }

    startPreviewProgress() {
        // An already-running wait keeps its clock (e.g. re-entering step 3
        // while the same run is still generating); only a genuinely new run
        // resets it via restartPreviewProgress().
        if (this.previewProgressTimer) return;

        this.previewProgressStartedAt = Date.now();
        this.previewMessageIndex = -1;
        this.renderPreviewProgress(0);
        // Empty the long-wait announcer so a fresh run can announce again
        // (the announcer only fires on a content change).
        const announcer = this.getModalRoot().querySelector("[data-preview-longwait-announcer]");
        if (announcer) announcer.textContent = "";
        this.rotatePreviewProgressMessage();

        this.previewProgressTimer = window.setInterval(() => {
            this.renderPreviewProgress(this.computePreviewProgress(Date.now() - this.previewProgressStartedAt));
        }, PERSONALIZATION_PREVIEW_PROGRESS_TICK_MS);
        this.previewMessageTimer = window.setInterval(
            () => this.rotatePreviewProgressMessage(),
            PERSONALIZATION_PREVIEW_MESSAGE_INTERVAL_MS
        );
    }

    restartPreviewProgress() {
        this.stopPreviewProgress();
        this.startPreviewProgress();
    }

    // The loading area while the pass still waits for the photo's upload: it
    // says so, its bar at 0 - the clock and the rotation stand still until the
    // run is posted (restartPreviewProgress), or nothing generated yet would
    // pass for progress. Told once to screen readers, through the long wait's
    // announcer: the rotating message is aria-hidden.
    showPreviewWaitingForPhoto() {
        const loadingEl = this.getModalRoot().querySelector("[data-preview-loading]");
        const message = (loadingEl?.getAttribute("data-progress-message-uploading") || "").trim();
        if (!message) return;
        this.stopPreviewProgress();
        const messageEl = loadingEl.querySelector("[data-preview-progress-message]");
        if (messageEl) messageEl.textContent = message;
        const announcer = loadingEl.querySelector("[data-preview-longwait-announcer]");
        if (announcer) announcer.textContent = message;
    }

    stopPreviewProgress(options = {}) {
        if (this.previewProgressTimer) {
            window.clearInterval(this.previewProgressTimer);
            this.previewProgressTimer = null;
        }
        if (this.previewMessageTimer) {
            window.clearInterval(this.previewMessageTimer);
            this.previewMessageTimer = null;
        }
        if (this.previewMessageFadeTimer) {
            // A mid-fade swap must not write the old run's message into a
            // freshly started one.
            window.clearTimeout(this.previewMessageFadeTimer);
            this.previewMessageFadeTimer = null;
        }
        this.previewMessageIndex = -1;
        // silent: the element is being torn down — don't touch the drawer
        // DOM, another live instance may own the modal id by now.
        if (options.silent === true) return;
        this.getModalRoot().querySelector("[data-preview-progress-message]")?.classList.remove("is-fading");
        // The ready state replaces the loading UI, so 100% is only ever seen
        // if the loading state briefly re-renders — still better than a bar
        // snapping back to 0 next to a finished preview.
        this.renderPreviewProgress(options.complete === true ? 100 : 0);
    }

    computePreviewProgress(elapsedMs) {
        const frames = PERSONALIZATION_PREVIEW_PROGRESS_KEYFRAMES;
        if (!(elapsedMs > 0)) return 0;
        for (let i = 1; i < frames.length; i += 1) {
            if (elapsedMs < frames[i][0]) {
                const [prevTime, prevValue] = frames[i - 1];
                const ratio = (elapsedMs - prevTime) / (frames[i][0] - prevTime);
                return prevValue + (frames[i][1] - prevValue) * ratio;
            }
        }
        return frames[frames.length - 1][1];
    }

    renderPreviewProgress(percent) {
        const root = this.getModalRoot();
        const track = root.querySelector("[data-preview-progress]");
        const bar = root.querySelector("[data-preview-progress-bar]");
        const clamped = Math.max(0, Math.min(100, Math.round(percent)));
        if (bar) bar.style.width = `${clamped}%`;
        track?.setAttribute("aria-valuenow", String(clamped));
    }

    rotatePreviewProgressMessage() {
        const root = this.getModalRoot();
        const messageEl = root.querySelector("[data-preview-progress-message]");
        const loadingEl = root.querySelector("[data-preview-loading]");
        if (!messageEl || !loadingEl) return;

        const messages = (loadingEl.getAttribute("data-progress-messages") || "")
            .split("||")
            .map((message) => message.trim())
            .filter(Boolean);
        const longWaitMessage = (loadingEl.getAttribute("data-progress-message-long") || "").trim();
        const elapsed = Date.now() - this.previewProgressStartedAt;

        let nextMessage = "";
        if (longWaitMessage && elapsed >= PERSONALIZATION_PREVIEW_LONG_WAIT_MS) {
            // Clearly above the typical wait: reassure instead of cycling.
            nextMessage = longWaitMessage;
            // The rotating element is aria-hidden (rotation would be noisy),
            // but this one transition matters to screen-reader users too —
            // the static note promised ~30s. Announce it once via the
            // visually-hidden child of the role="status" region.
            const announcer = loadingEl.querySelector("[data-preview-longwait-announcer]");
            if (announcer && announcer.textContent !== longWaitMessage) {
                announcer.textContent = longWaitMessage;
            }
        } else if (messages.length) {
            // Advance through the activity messages and hold on the last one.
            this.previewMessageIndex = Math.min(this.previewMessageIndex + 1, messages.length - 1);
            nextMessage = messages[this.previewMessageIndex];
        }

        if (!nextMessage || messageEl.textContent === nextMessage) return;

        messageEl.classList.add("is-fading");
        window.clearTimeout(this.previewMessageFadeTimer);
        this.previewMessageFadeTimer = window.setTimeout(() => {
            this.previewMessageFadeTimer = null;
            messageEl.textContent = nextMessage;
            messageEl.classList.remove("is-fading");
        }, 250);
    }

    updatePreviewHeadline(state, previewRoot) {
        if (!previewRoot) return;
        if (this.getActiveStepId() !== this.previewStepId()) return;

        const root = this.getModalRoot();
        const titleEl = root.querySelector("[data-step-dynamic-title]");
        const descriptionEl = root.querySelector("[data-step-dynamic-description]");
        // The book ready with its Hörbuch along - the set, or the book after a "Ja" - says so (Figma
        // 1098:2369); the Hörbuch's own view keeps the copy of its own set.
        const withAudiobook = !this.isAudiobook() && this.wantsNameSample();
        const suffix = state === "ready" ? (withAudiobook ? "ready-set" : "ready") : state === "error" ? "error" : "loading";
        const title = previewRoot.getAttribute(`data-title-${suffix}`);
        let description = previewRoot.getAttribute(`data-description-${suffix}`) || "";

        const nameInput = root.querySelector('input[name="__main_character"], input[name="__main-character-name"]');
        const savedState = this.getSavedState() || {};
        const name =
            String(nameInput?.value || "").trim() ||
            String(savedState["__main_character"] || "").trim() ||
            String(savedState["__main-character-name"] || "").trim();

        if (titleEl && title) titleEl.textContent = title;
        if (descriptionEl) {
            if (description.indexOf("{name}") !== -1) {
                const escapeHtml = (value) =>
                    String(value)
                        .replace(/&/g, "&amp;")
                        .replace(/</g, "&lt;")
                        .replace(/>/g, "&gt;")
                        .replace(/"/g, "&quot;");
                descriptionEl.innerHTML = escapeHtml(description).replace(
                    "{name}",
                    `<span class="personalization-preview-name">${escapeHtml(name)}</span>`
                );
            } else {
                descriptionEl.textContent = description;
            }
        }
    }

    updateAddToCartAvailability() {
        const root = this.getModalRoot();
        const button = root.querySelector("[data-add-personalized-to-cart]");
        if (!button) return;
        const ready = this.previewState === "ready" && !!this.currentPersonalization?.personalization_id;
        button.disabled = !ready;
    }

    // Cover image of the preview the customer approved — snapshotted onto the
    // cart line as _bookpipe_preview_url so Liquid can render the real
    // preview on the cart/order pages without a backend call. The
    // personalization id stays the backend's source of truth; this URL is
    // presentation only. Batch URLs are immutable: a regeneration after
    // add-to-cart never mutates what the cart shows — only saving the edit
    // rewrites the snapshot (and the theme falls back to the cover mockup if
    // the URL stops resolving, e.g. after the draft retention window).
    currentPreviewImageUrl() {
        const pages = this.currentPersonalization?.preview_pages;
        if (Array.isArray(pages) && pages[0]?.url) {
            return String(pages[0].url);
        }
        const images = this.currentPersonalization?.preview_images;
        if (Array.isArray(images) && images[0]?.url) {
            return String(images[0].url);
        }
        return "";
    }

    // ------------------------------------------------------------------
    // Cart submission
    // ------------------------------------------------------------------

    async handleAddToCart(event) {
        event.preventDefault();
        if (this.isSubmittingCart) return;
        if (this.cartRedirectPending) {
            // The previous add/edit already succeeded; only the navigation to
            // /cart failed. Retry the navigation — never re-add (a second add
            // would mint a duplicate cart line).
            this.beginCartRedirect(event.currentTarget || event.target);
            return;
        }
        this.isSubmittingCart = true;
        const button = event.currentTarget || event.target;
        this.setLoadingState(button, true);
        let redirecting = false;

        try {
            const cart = await this.getCart();
            const root = this.getModalRoot();
            const forms = root.querySelectorAll(".personalization-step-form");

            if (!forms || forms.length === 0) {
                return;
            }

            // A photo still being prepared: the steps' check, the preview's
            // signature and the session's last sync below are the prepared
            // photo's.
            await this.whenPhotosPrepared();

            // Validate every input step in tab order; if invalid, navigate to that step
            for (const sid of this.inputStepIds()) {
                const { valid, errors } = this.validateStep(sid);
                if (!valid) {
                    this.trackPersonalization("personalization_add_to_cart_blocked", {
                        step: sid,
                        reason: "step_validation",
                    });
                    this.activateStep(sid);
                    this.updateErrors(sid, errors);
                    return;
                }
            }

            // The session must be bound to the ACTIVE cover's SKU. The
            // signature check below only guards the inputs (incl. variant
            // id); the generated assets hang off the SESSION, and a reopen
            // that raced the theme's async variant switch can have resumed
            // the other cover's session — selling that personalization puts
            // a wrong-product session id on the cart line, which the order
            // import rejects. Rotate and regenerate instead.
            const activeSku = await this.resolveActiveSku();
            const boundSku = String(this.getSavedState()?.["__bp_session_sku"] || "");
            if (!this.editMode && activeSku && boundSku && activeSku !== boundSku) {
                this.trackPersonalization("personalization_add_to_cart_blocked", { reason: "sku_rotation" });
                await this.ensureSessionForActiveVariant();
                this.activateStep(this.previewStepId());
                return;
            }

            // The preview must be generated and current before the book can be purchased.
            const signature = await this.buildPreviewSignature();
            if (
                this.previewState !== "ready" ||
                !this.currentPersonalization?.personalization_id ||
                this.previewSignature !== signature
            ) {
                this.trackPersonalization("personalization_add_to_cart_blocked", { reason: "preview_not_ready" });
                this.activateStep(this.previewStepId());
                return;
            }

            // One form snapshot for the session sync AND the cart payload:
            // both writes must describe the same state — an edit landing
            // while the awaits below run reaches neither, never just one.
            const combinedEntries = this.collectEntries();
            // The cover radio is a wizard-internal key: collectEntries sweeps every step
            // form, but the cart line carries the cover in its VARIANT, and the order
            // import binds the session by _bookpipe_personalization_id + product.
            delete combinedEntries["__cover_type"];

            // The signature above is preview-relevant only — a dedication
            // edited after the last preview pass passes it without ever
            // having been synced. The ORDER prints from the server-side
            // session, so reconcile it here, always and fail-closed: a stale
            // dedication in the session would end up in the printed book,
            // and this is the last write before the cart line exists.
            const sessionSync = await this.syncAllToSession({ entries: combinedEntries, failClosed: true });
            if (!sessionSync || sessionSync.ok === false) {
                const sessionSyncFieldErrors = sessionSync?.errorData ? this.extractPreviewFieldErrors(sessionSync.errorData) : null;
                if (sessionSyncFieldErrors) {
                    this.trackPersonalization("personalization_add_to_cart_blocked", { reason: "field_errors" });
                    this.handlePreviewFieldErrors(sessionSyncFieldErrors);
                    return;
                }
                throw new PersonalizationLocalizedError(
                    sessionSync?.message || this.getMessage("cart-error", "Personalisierung konnte nicht gespeichert werden.")
                );
            }
            const selectedVariantId = await this.getSelectedVariantId();
            const certificateHash = this.getOrCreateCertificateHash();
            combinedEntries["__certificate_hash"] = certificateHash;
            const mainCharacterName = String(this.getEntryValue(combinedEntries, "main_character") || "").trim();
            if (mainCharacterName) {
                // Cart snippets (title [Name] replacement, cover SVG overlay,
                // edit link) and mail templates still read the pre-cutover
                // property name — keep it in sync with the dynamic field.
                combinedEntries[PERSONALIZATION_LEGACY_ENTRY_ALIASES.main_character] = mainCharacterName;
            }

            // The fee only applies when the product supports a dedication and
            // there is actual dedication text; keep the stored flag consistent
            // so the cart-repair statics never re-add a fee for an empty
            // dedication.
            const dedicationText = String(combinedEntries["__dedication"] || "").trim();
            const addDedication = this.supportsDedication() && String(combinedEntries["__add-dedication"]) === "true" && dedicationText !== "";
            combinedEntries["__add-dedication"] = addDedication ? "true" : "false";
            const dedicationFeeVariantId = this.getAttribute("data-dedication-variant-id");

            // The Hörbuch comes as the set, two more variants of the book (plan
            // hoerbuch-set-als-variante): a "Ja" in the book's Hörbuch step buys the set's variant of
            // the chosen cover instead of the book's, on the book's session and with its
            // personalization, and the set format always does - bookpipe makes the set from the book
            // and its Hörbuch, nothing hangs under it but the fee. Read the way the name sample reads
            // it (setChosen), off this snapshot.
            const wantsSet = this.setChosen(combinedEntries);
            // The answer goes on the line: the cart row ("inkl. Hörbuch") and the book's edit prefill
            // read it. Written here, in the payload, and never as a form field: the draft is one for
            // every format of the product, and hydration would write the book's "Nein" into the
            // set's. It only ever says what the cart holds, so a render without a set on offer - a
            // book without one, the Hörbuch, which IS the Hörbuch - carries no answer at all,
            // whatever a form holds.
            if (wantsSet) {
                combinedEntries["__add-audiobook"] = "true";
            } else if (this.setOffered()) {
                combinedEntries["__add-audiobook"] = "false";
            } else {
                delete combinedEntries["__add-audiobook"];
            }
            // The line remembers what it was bought as: the cart's edit link reopens the set and the
            // Hörbuch where they are sold (snippets/edit-book.liquid). bookpipe has no option under
            // that name.
            combinedEntries["__format"] = wantsSet ? "set" : this.format;
            // What the line is bought as: the set's variant of the chosen cover for the set, else
            // the variant the page selected (the Hörbuch has no cover to pick by).
            const cartVariantId = wantsSet ? this.cartVariantIdForCoverType(this.resolveCoverTypeForVariant(selectedVariantId), true) : selectedVariantId;

            if (!cartVariantId) {
                throw new PersonalizationLocalizedError(
                    this.getMessage("variant-unavailable", "Bitte wähle zuerst eine Ausführung des Buches aus.")
                );
            }

            const editLineUuid = String(this.getSavedState()?.["__line_uuid"] || "");
            // cart kann null sein (Fetch fehlgeschlagen/Nicht-JSON): dann ohne Cart-Kenntnis
            // über den Neu-Add-Pfad weiterkaufen statt den Kauf abzubrechen
            const lineItem = cart?.items?.find((item) => item.properties?.["__line_uuid"] === editLineUuid);

            if (lineItem && this.editMode) {
                const quantityValue = lineItem.quantity || 1;
                // Merge properties: preserve original outside edit range, override from combinedEntries
                const mergedProperties = { ...(lineItem.properties || {}) };
                if (lineItem.properties?.__line_uuid) {
                    mergedProperties["__line_uuid"] = String(lineItem.properties.__line_uuid);
                }
                if (!mergedProperties["__line_uuid"]) {
                    mergedProperties["__line_uuid"] = window.crypto.randomUUID();
                }
                Object.keys(combinedEntries).forEach((k) => {
                    mergedProperties[k] = combinedEntries[k];
                });
                // why: a Hörbuch line from before its dedication was charged names no fee, and
                // the cart repairs below only hang the fee a line names. A root without the
                // fee's variant keeps whatever the line has.
                if (dedicationFeeVariantId) {
                    mergedProperties["__dedication_variant_id"] = dedicationFeeVariantId;
                }
                // Legacy in-wizard hardcover upsell properties are no longer written.
                delete mergedProperties["__add-diary"];
                delete mergedProperties["__diary_variant_id"];
                if (mainCharacterName) {
                    PersonalizationModal.nameRecipient(mergedProperties, mainCharacterName);
                }
                mergedProperties["_bookpipe_personalization_id"] = this.currentPersonalization.personalization_id;
                // Saving the edit re-snapshots the preview cover; a stale
                // snapshot from the pre-edit save must never survive.
                const editPreviewImageUrl = this.currentPreviewImageUrl();
                if (editPreviewImageUrl) {
                    mergedProperties["_bookpipe_preview_url"] = editPreviewImageUrl;
                } else {
                    delete mergedProperties["_bookpipe_preview_url"];
                }

                const currentVariantId = lineItem.variant_id || lineItem.id || cartVariantId;
                // What the line is after the edit: the answer in the Hörbuch step moves it between
                // the book's variant and the set's of the same cover (the cover step is locked in edit
                // mode) - in the book format that offers the set; everywhere else the line stays what
                // it is.
                const targetVariantId = this.setOffered()
                    ? String(this.cartVariantIdForCoverType(this.resolveCoverTypeForVariant(currentVariantId), wantsSet))
                    : String(currentVariantId);

                // why: Shopify's cart can neither change a line's variant nor add one line and remove
                // another in one step. So the line of this id is first there as its target variant -
                // the one already in the cart (no move, or a save cut off before), else a new one with
                // the line's quantity - and only then does every other line of this id go. A save cut
                // off in between leaves both, but keeps the draft and its __line_uuid (clearStorage
                // runs last), and the next save converges on one line - whichever of the two the cart
                // lists first.
                const parentLineUuid = mergedProperties["__line_uuid"];
                const parentLines = cart.items.filter((item) => item.properties?.["__line_uuid"] === parentLineUuid);
                const targetLine = parentLines.find((item) => String(item.variant_id) === targetVariantId);
                if (targetLine) {
                    await this.changeCartLine(targetLine.key, targetLine.quantity || quantityValue, mergedProperties);
                } else {
                    await this.addToCart({ items: [{ id: targetVariantId, quantity: quantityValue, properties: mergedProperties }] });
                }
                // why: Shopify gives a line a new key whenever its discount allocations change - an
                // automatic discount over the number of books does exactly that while the swap holds
                // two of them, and again when one goes - so each line that goes is looked up in the
                // cart as the write before it left it, never by a key read before that write. A cart
                // that cannot be read stops the save like a failed write: the draft stays for the retry.
                for (let removed = 0; removed < parentLines.length; removed += 1) {
                    const currentCart = await this.getCart();
                    if (!currentCart) {
                        throw new Error("Shopify cart API failed to read the cart before removing a replaced line");
                    }
                    const idLines = currentCart.items.filter((item) => item.properties?.["__line_uuid"] === parentLineUuid);
                    const keptLine = idLines.find((item) => String(item.variant_id) === targetVariantId);
                    const leavingLine = idLines.find((item) => item !== keptLine);
                    if (!leavingLine) {
                        break;
                    }
                    await this.changeCartLine(leavingLine.key, 0, leavingLine.properties);
                }

                // The lines under it (they name the id as their __parent_line_uuid, so none went
                // above) follow its name and the variant they now hang under.
                if (mainCharacterName) {
                    const updatedCart = await this.getCart();
                    // null-Cart: Kind-Zeilen-Update überspringen (nur Cart-Anzeige betroffen,
                    // der Druck kommt aus der Server-Session) — Edit-Save + Redirect laufen weiter
                    const childItems =
                        updatedCart?.items?.filter(
                            (item) => item.properties["__parent_line_uuid"] === parentLineUuid
                        ) ?? [];

                    for (const childItem of childItems) {
                        const childProperties = { ...childItem.properties };
                        PersonalizationModal.nameRecipient(childProperties, mainCharacterName);
                        childProperties["__parent_variant_id"] = targetVariantId;
                        await this.changeCartLine(childItem.key, childItem.quantity, childProperties);
                    }
                }

                await this.adjustCart();
                await this.checkForMissing();
                // A saved edit is a cart write too — the line (re-)entered
                // the cart with this session's id.
                this.reportCarted();
                // Only clear the saved edit state once the whole sequence
                // succeeded — a mid-flow failure must keep __line_uuid so a
                // retry edits the same line instead of adding a duplicate.
                this.clearStorage();
                // No wizard reset here: it would visibly blank the inputs of
                // the still-open modal while /cart loads. handlePageShow
                // resets the wizard if bfcache ever restores this page.
                redirecting = true;
                this.beginCartRedirect(button);
                return;
            }

            const parentProperties = { ...combinedEntries };
            if (mainCharacterName) {
                PersonalizationModal.nameRecipient(parentProperties, mainCharacterName);
            }

            parentProperties["__dedication_variant_id"] = dedicationFeeVariantId;
            parentProperties["__line_uuid"] = this.getOrCreateLineUuid();
            parentProperties["_bookpipe_personalization_id"] = this.currentPersonalization.personalization_id;
            const previewImageUrl = this.currentPreviewImageUrl();
            if (previewImageUrl) {
                parentProperties["_bookpipe_preview_url"] = previewImageUrl;
            }

            const items = [{ id: cartVariantId, quantity: 1, properties: parentProperties }];

            if (addDedication && dedicationFeeVariantId) {
                const dedicationProperties = {
                    __parent_line_uuid: String(parentProperties["__line_uuid"]),
                    __parent_variant_id: String(cartVariantId),
                    __variant_id: String(dedicationFeeVariantId),
                };
                if (mainCharacterName) {
                    PersonalizationModal.nameRecipient(dedicationProperties, mainCharacterName);
                }
                items.push({
                    id: dedicationFeeVariantId,
                    quantity: 1,
                    properties: dedicationProperties,
                });
            }
            const response = await this.addToCart({ items, id: cartVariantId });

            if (response) {
                // Funnel completion — before the redirect starts; keepalive
                // is GTM's problem, dataLayer pushes are synchronous. It names
                // what went into the cart: after a "Ja" the set's variant, where
                // every other event of the run names the book's.
                this.analyticsCartCompleted = true;
                this.trackPersonalization("personalization_add_to_cart", {
                    with_dedication: this.dedicationSelectedForAnalytics(),
                    variant_id: cartVariantId,
                });
                this.reportCarted();
                this.clearStorage();
                this.dispatchCartEvents({ id: cartVariantId, quantity: 1, properties: parentProperties });
                // The modal stays open under the nav cover — hide() would
                // fade the wizard out and (on touch) scroll-jump the PDP
                // before /cart paints. The wizard reset moved to
                // handlePageShow for the bfcache-restore case.
                redirecting = true;
                this.beginCartRedirect(button);
            }
        } catch (error) {
            console.error("PersonalizationModal: Failed to add personalized product", error);
            this.trackPersonalization("personalization_add_to_cart_blocked", { reason: "cart_error" });
            this.showErrorMessage(button, this.customerMessage(error, "cart-error", "Personalisierung konnte nicht gespeichert werden."));
        } finally {
            this.isSubmittingCart = false;
            if (!redirecting) {
                this.setLoadingState(button, false);
            }
        }
    }

    // Validation/status texts come from the locale files via data-msg-*
    // attributes; the literals below are only fallbacks.
    getMessage(key, fallback) {
        return this.getAttribute(`data-msg-${key}`) || fallback;
    }

    // The single gate between a caught exception and the customer. Only
    // PersonalizationLocalizedError carries text that was translated (locale
    // file or bookpipe's localized app-proxy response); every other error is
    // classified and answered with a translated message of our own, so a raw
    // engine string like Safari's "Load failed" can never reach the UI.
    customerMessage(error, fallbackKey, fallbackText) {
        if (error instanceof PersonalizationLocalizedError) {
            return error.message;
        }
        if (isPersonalizationNetworkError(error)) {
            return this.getMessage(
                "network-error",
                "Die Verbindung zum Server ist fehlgeschlagen. Bitte prüfe deine Internetverbindung und versuche es erneut."
            );
        }
        return this.getMessage(fallbackKey, fallbackText);
    }

    /**
     * Instance method wrapper - delegates to static method
     */
    async adjustCart() {
        return PersonalizationModal.adjustCart();
    }

    /**
     * Instance method wrapper - delegates to static method
     */
    async checkForMissing() {
        return PersonalizationModal.checkForMissing();
    }

    // Utility to get the current modal root (supports append-to-body)
    getModalRoot() {
        const drawer = document.getElementById(`personalize-modal-${this.productId}`);
        return drawer || this;
    }

    // ------------------------------------------------------------------
    // Step handling
    // ------------------------------------------------------------------

    // Step NUMBERS are Liquid's doing: a product with both cover variants gets a Cover
    // step after the Widmung, a book that also sells its Hörbuch a Hörbuch step, and each
    // shifts the Vorschau by one. Everything below therefore asks the markup for a step's
    // id by its key (data-step-key) instead of assuming a number, so the same code drives
    // the 3-, 4- and 5-step flow.
    stepIdForKey(key) {
        if (!key) return null;
        const step = this.getModalRoot()?.querySelector(`.personalization-step[data-step-key="${key}"]`);
        return step ? step.getAttribute("data-step-id") : null;
    }

    stepKeyForId(stepId) {
        if (stepId === null || stepId === undefined || stepId === "") return null;
        const step = this.getModalRoot()?.querySelector(`.personalization-step[data-step-id="${stepId}"]`);
        return step ? step.getAttribute("data-step-key") : null;
    }

    characterStepId() {
        return this.stepIdForKey(PERSONALIZATION_STEP_KEYS.CHARACTER);
    }

    // null when the product has no cover step (one cover variant only).
    coverStepId() {
        return this.stepIdForKey(PERSONALIZATION_STEP_KEYS.COVER);
    }

    // The wizard owns the cover choice only when Liquid rendered the step. Without it
    // the cover comes from the product page as before — every cover path below is
    // behind this guard so single-cover books behave exactly as they did.
    coverStepEnabled() {
        return Boolean(this.coverStepId());
    }

    // null when the product offers no Hörbuch step (a book without a set, the set, the Hörbuch).
    audiobookStepId() {
        return this.stepIdForKey(PERSONALIZATION_STEP_KEYS.AUDIOBOOK);
    }

    dedicationStepId() {
        return this.stepIdForKey(PERSONALIZATION_STEP_KEYS.DEDICATION);
    }

    previewStepId() {
        return this.stepIdForKey(PERSONALIZATION_STEP_KEYS.PREVIEW);
    }

    // Every step id in tab order.
    stepIds() {
        return Array.from(this.getModalRoot()?.querySelectorAll(".personalization-step") || [])
            .map((step) => step.getAttribute("data-step-id"))
            .filter(Boolean);
    }

    // The steps the customer fills in: the preview step only shows the result of the
    // others, it has no inputs to validate.
    inputStepIds() {
        const previewStepId = this.previewStepId();
        return this.stepIds().filter((stepId) => stepId !== previewStepId);
    }

    // The step that follows `stepId` in tab order; null for the last one.
    nextStepId(stepId) {
        const ids = this.stepIds();
        const index = ids.indexOf(String(stepId));
        return index > -1 ? ids[index + 1] || null : null;
    }

    // ------------------------------------------------------------------
    // Cover step (only rendered for books sold in both editions)
    // ------------------------------------------------------------------

    // Settles the cover step's UI when the wizard opens. Nothing is preselected — the
    // customer picks (owner 28.09.2026): only the customer's own pick is ticked again,
    // and an edited cart line keeps its cover. Memoized while it runs — hydrateAllForms()
    // and the drawer's show handler both call it, and in edit mode the derivation fetches
    // cart.js, so without the shared promise the two would race each other.
    async ensureCoverSelection() {
        if (!this.coverStepEnabled()) return;
        if (this.coverSelectionPromise) return this.coverSelectionPromise;

        this.coverSelectionPromise = (async () => {
            try {
                let coverType = null;
                if (this.editMode) {
                    // The edited line's cover, derived from its variant in the cart (its
                    // session belongs to exactly that variant): ticked and locked below.
                    coverType = this.resolveCoverTypeForVariant(await this.getSelectedVariantId());
                    if (this.selectedCoverType() !== coverType) {
                        // Persist what was derived, so every later read resolves the
                        // choice directly instead of deriving it again.
                        this.saveState({ __cover_type: coverType });
                    }
                } else if (this.getSavedState()?.["__cover_picked"] === "true") {
                    // The customer's own pick (handleInput marks it).
                    coverType = this.selectedCoverType();
                } else {
                    // An answer without the mark was derived by an older theme, not picked.
                    // Nulled rather than skipped: showFormForStep() re-fills a step's radios
                    // from the draft on every activation and would tick it again.
                    this.saveState({ __cover_type: null });
                }
                this.getModalRoot()
                    .querySelectorAll('input[type="radio"][name="__cover_type"]')
                    .forEach((radio) => {
                        // Deliberately no input event: this is a restore, not a
                        // customer decision — handleInput would rotate the session for
                        // a cover nobody changed.
                        radio.checked = radio.value === coverType;
                    });
                // Without a pick nothing is mirrored: the product form keeps the page's
                // variant, and the session stays on getSelectedVariantId's fallback (an
                // older draft's session, else that variant).
                if (!coverType) return;
                this.applyCoverVariant(this.variantIdForCoverType(coverType));
                this.applyEditModeCoverLock(coverType);
            } catch (e) {
                // The next open derives again; leaving the radios untouched is the
                // safe answer (the customer picks, which settles everything).
            }
        })().finally(() => {
            this.coverSelectionPromise = null;
        });

        return this.coverSelectionPromise;
    }

    // Editing a book that is already in the cart keeps its cover: the line's variant is
    // pinned through the cart and its session belongs to exactly that variant. The other
    // card stops reacting and the hint below the picker says why.
    applyEditModeCoverLock(coverType) {
        if (!this.editMode) return;
        const root = this.getModalRoot();
        root.querySelectorAll("[data-cover-option]").forEach((option) => {
            const isSelected = option.getAttribute("data-cover-option") === coverType;
            option.classList.toggle("is-locked", !isSelected);
            const radio = option.querySelector('input[type="radio"][name="__cover_type"]');
            if (radio) radio.disabled = !isSelected;
        });
        const hint = root.querySelector("[data-cover-locked-hint]");
        if (!hint) return;
        hint.removeAttribute("hidden");
        const selectedRadio = root.querySelector(`[data-cover-option="${coverType}"] input[name="__cover_type"]`);
        if (selectedRadio && hint.id) {
            selectedRadio.setAttribute("aria-describedby", hint.id);
        }
    }

    // Mirrors the chosen cover into everything that reads a variant: the element itself
    // (resolveActiveSku, analytics) and the product form's hidden input, which is what
    // the Shopify add-to-cart actually posts. No variant:change is dispatched — the PDP
    // renders no picker for a wizard-owned cover, and the wizard runs its own rotation.
    applyCoverVariant(variantId) {
        if (!variantId) return;
        this.variantId = String(variantId);
        const productFormInput = document.querySelector('product-form form input[name="id"]');
        if (productFormInput) {
            productFormInput.value = String(variantId);
        }
    }

    // The customer picked the other cover: mirror it, then rotate the session exactly
    // like a variant switch on the product page did. The rotation restarts the
    // background preview run (invalidatePreviewForNewSession), and the server adopts
    // the sibling session's uploads, so the new cover's preview is usually ready by the
    // time the customer reaches it.
    async handleCoverSelectionChange(coverType) {
        this.applyCoverVariant(this.variantIdForCoverType(coverType));
        this.syncSetSurcharge();
        // Report the click before the rotation is awaited: the funnel must see the
        // choice even when the session call is slow or fails.
        this.trackPersonalization("personalization_cover_selected", { cover_type: coverType });
        // handleInput returns before its generic live-validation tail, so the "please
        // choose a cover" message is cleared here.
        const coverStepId = this.coverStepId();
        if (coverStepId && coverStepId === this.getActiveStepId()) {
            const validation = this.validateStep(coverStepId);
            this.updateErrors(coverStepId, validation.errors, /*silentOnValid*/ true);
        }
        await this.ensureSessionForActiveVariant();
    }

    // The Hörbuch step prices the set as what it adds to the book, for the cover chosen one step
    // before: Liquid renders one price per cover (personalization-step-audiobook-form.liquid), and
    // this shows the one that applies - on entering the step and whenever the cover changes.
    // Without a choice (a book with one cover) it is the Softcover's, as Liquid renders it.
    syncSetSurcharge() {
        const coverType = this.selectedCoverType() || "softcover";
        this.getModalRoot()
            .querySelectorAll("[data-set-surcharge]")
            .forEach((price) => {
                price.toggleAttribute("hidden", price.getAttribute("data-set-surcharge") !== coverType);
            });
    }

    handleStepClick(event) {
        const step = event.currentTarget;
        const targetStepId = step.getAttribute("data-step-id");
        this.navigateToStep(targetStepId);
    }

    handleDocClick(event) {
        const root = this.getModalRoot();
        if (!root || !(event.target instanceof Element)) return;
        if (!root.contains(event.target)) return;
        const stepBtn = event.target.closest("[data-next-step], [data-prev-step], [data-goto-step]");
        if (stepBtn) {
            event.preventDefault();
            const stepId =
                stepBtn.getAttribute("data-next-step") ||
                stepBtn.getAttribute("data-prev-step") ||
                stepBtn.getAttribute("data-goto-step");
            if (!stepId) return;
            this.navigateToStep(stepId);
        }
    }

    navigateToStep(stepId) {
        if (!stepId) return;
        const currentStepId = this.getActiveStepId();
        if (stepId === currentStepId) return;
        // Forwards/backwards is a question of tab ORDER, not of the id's numeric
        // value — the ids are whatever Liquid numbered the rendered steps with.
        const stepIds = this.stepIds();
        const currentIndex = stepIds.indexOf(String(currentStepId));
        const targetIndex = stepIds.indexOf(String(stepId));
        const goingForward = currentIndex > -1 && targetIndex > currentIndex;
        if (goingForward) {
            // Every step the customer leaves behind, from the current one up to
            // (excluding) the target.
            const stepsLeftBehind = stepIds.slice(currentIndex, targetIndex);
            for (const sid of stepsLeftBehind) {
                const validation = this.validateStep(sid);
                if (!validation.valid) {
                    this.trackPersonalization("personalization_validation_error", {
                        step: sid,
                        reason: "step_validation",
                    });
                    this.activateStep(sid);
                    this.updateErrors(sid, validation.errors);
                    return;
                }
            }
            // Every step between current and target validated: the customer
            // finished them (multi-step jumps via the header count each one).
            const dedicationStepId = this.dedicationStepId();
            for (const sid of stepsLeftBehind) {
                this.trackPersonalization("personalization_step_completed", {
                    step: sid,
                    with_dedication: sid === dedicationStepId ? this.dedicationSelectedForAnalytics() : null,
                });
            }
        }
        this.clearErrors();
        this.activateStep(stepId, { focusTitle: true });

        // Prefetch: everything the generation consumes is fixed once the character
        // step validated — the dedication written afterwards never reaches the
        // preview templates (it does not dirty the server-side preview hash), and a
        // cover switch rotates the session, which restarts the run on its own
        // (invalidatePreviewForNewSession). Kicking the run off here means the
        // customer usually finds it done on the preview step. Deliberately AFTER
        // activateStep: entering the next step seeds the dedication template into
        // the form, and the signature must be computed against that settled state.
        // Only the move from the character step to the one right after it
        // prefetches — a header jump further ahead already triggers the visible run
        // via activateStep, and a back-navigation must not restart anything. Coming
        // back and leaving again without changes is free (same-signature adoption);
        // with changes the server serializes the runs (run_in_flight wait-out).
        if (goingForward && currentStepId === this.characterStepId() && String(stepId) === this.nextStepId(currentStepId)) {
            this.ensurePreview({ background: true });
            // Same moment, same reason (the name is fixed from here on) — but a
            // separate, unawaited flow: the sample is cosmetic and must never
            // hold up the preview or the purchase. As a prefetch it reaches the
            // book that offers its set as well, ahead of the Hörbuch step's answer.
            this.requestNameSample({ prefetch: true });
        }
    }

    handleTriggerOpen() {
        this.wizardOpened = true;
        this.warmWizardImages();
        // Don't clear storage when editing an existing item (openPersonalize mode)
        const params = new URLSearchParams(window.location.search || "");
        if (params.has("openPersonalize")) {
            return;
        }
        // State imported by an abandoned edit-from-cart visit belongs to a
        // cart line, not to a draft: resuming it would mutate the in-cart
        // book's live session and mint a duplicate __line_uuid on
        // add-to-cart. Plain drafts never carry a personalization id — it is
        // only written into cart-line properties and imported back from them.
        if (!this.editMode && this.getSavedState()?.["_bookpipe_personalization_id"]) {
            this.clearStorage();
            this.resetWizardToFreshDraft();
        }
        // Reopening resumes the draft where the customer left off: text
        // values re-hydrate from localStorage, photos from IndexedDB and the
        // server-side session from its stored id. Only a completed
        // add-to-cart clears the draft (handleAddToCart → clearStorage).
        // The flow itself restarts at step 1: the drawer DOM keeps the last
        // active step across close/open, which would drop returning
        // customers into the middle of the wizard.
        this.clearErrors();
        this.activateStep("1");
        // The cover variant may have changed since the last open: a changed
        // SKU refreshes the field config and rotates the session (sessions
        // are bound to the SKU they were created with).
        this.ensureSessionForActiveVariant();
        this.initFieldConfig({ hydratePhotos: true });
    }

    clearStorage() {
        try {
            localStorage.removeItem(this.getStorageKey());
        } catch (e) {
            // noop
        }
    }

    // Full reset to a fresh draft. Used when a bfcache restore resurrects
    // the page after a completed add-to-cart (handlePageShow — the restored
    // page must not resume the purchased draft) and when discarding leaked
    // non-draft state such as an abandoned edit-from-cart import.
    resetWizardToFreshDraft() {
        this.photoPreparations?.clear();
        this.photoFiles.clear();
        this.photoObjectUrls.forEach((url) => URL.revokeObjectURL(url));
        this.photoObjectUrls.clear();
        this.photoCheckStates.clear();
        this.clearAllFieldErrors();
        this.renderAllPhotoStates();
        this.getModalRoot()
            .querySelectorAll(".personalization-step-form input[type='text'], .personalization-step-form textarea")
            .forEach((input) => {
                input.value = "";
            });
        this.getModalRoot()
            .querySelectorAll(".personalization-step-form input[type='radio']")
            .forEach((input) => {
                input.checked = false;
            });
        if (this.coverStepEnabled()) {
            // The cover radios were unchecked with the rest: the mirror goes back to
            // the variant the PAGE selected, not to the cover of the discarded draft.
            this.applyCoverVariant(this.initialVariantId || this.softcoverVariantId);
        }
        this.previewSignature = null;
        this.currentPersonalization = null;
        this.previewRetryNonce = 0;
        // Kill any leftover run: its poll loop must not resurrect "ready"
        // state (with the purchased book's data) into the fresh wizard.
        this.previewRequestToken += 1;
        this.previewAbortController?.abort();
        this.activePreviewPoll = null;
        // Nothing of the discarded draft is worth waiting for either: a marker kept
        // here would make the fresh draft's first run sit out a purchased session.
        this.deferredSiblingRun = null;
        // The sold draft's name sample: its signed URL belongs to that purchase, and a
        // settled entry under the same name would keep the fresh draft from asking again.
        this.nameSampleRequests.clear();
        this.nameSampleResults.clear();
        this.nameSampleActiveName = null;
        const nameSampleAudio = this.getModalRoot().querySelector("[data-name-sample-audio]");
        if (nameSampleAudio) nameSampleAudio.removeAttribute("src");
        this.setNameSampleState("preparing");
        this.resetSession();
        this.setPreviewState("idle");
        this.activateStep("1");
        // The radios were unticked above without an input event: the book's block and cover go
        // back behind the Hörbuch step's answer. Last, so that anything it could ask for would
        // belong to the fresh session, never to the sold one.
        this.syncNameSample();
    }

    activateStep(stepId, options = {}) {
        if (!stepId) return;

        const root = this.getModalRoot();
        const headers = root.querySelectorAll(".personalization-step");
        headers.forEach((s) => {
            if (s.getAttribute("data-step-id") == stepId) {
                s.setAttribute("aria-current", "step");
                s.classList.add("selected");
            } else {
                s.removeAttribute("aria-current");
                s.classList.remove("selected");
            }
        });
        const active = Array.from(headers).find((s) => s.classList.contains("selected"));
        if (active) {
            const title = active.getAttribute("data-title") || "";
            const description = active.getAttribute("data-description") || "";
            const dynTitle = root.querySelector("[data-step-dynamic-title]");
            const dynDesc = root.querySelector("[data-step-dynamic-description]");
            if (dynTitle) dynTitle.textContent = title;
            if (dynDesc) dynDesc.textContent = description;
        }
        this.showFormForStep(stepId);
        if (String(stepId) === this.audiobookStepId()) {
            this.syncSetSurcharge();
        }
        // Funnel step view — gated on the drawer being visible: activateStep
        // also runs while it is closed (connectedCallback, trigger prep).
        this.trackStepView(stepId);

        // Hiding the outgoing form drops keyboard focus to <body>; move it to
        // the step title so keyboard/AT users keep their place and hear the
        // new step (the title has tabindex="-1").
        if (options.focusTitle) {
            const dynTitle = root.querySelector("[data-step-dynamic-title]");
            if (dynTitle) {
                dynTitle.focus({ preventScroll: false });
            }
        }

        if (String(stepId) === this.previewStepId()) {
            this.ensurePreview();
            // The name sample catches up with whatever changed unseen before: a Hörbuch-step
            // answer that a restored draft or an edited cart line ticked without an input event,
            // or a request the prefetch never made because a header jump skipped the move off the
            // character step. Only here - activateStep also runs for the first step while the
            // drawer is still closed (connectedCallback) and on every open (handleTriggerOpen), and
            // a returning customer's draft would turn a sync there into a paid render nobody asked
            // to hear.
            this.syncNameSample();
            // Refresh headline in case the preview is already resolved.
            this.updatePreviewHeadline(this.previewState, root.querySelector("[data-preview-root]"));
            // Re-sync the gallery: while the step-3 form was display:none the
            // track's scroll box was destroyed, and some browsers reset its
            // offset — realign the visible page with the recorded state so
            // dots/arrows never point at the wrong page.
            if (this.previewState === "ready" && this.previewPageCount > 0) {
                this.scrollPreviewToPage(this.activePreviewPage);
            }
        }
        this.syncAudiobookCoverNames();
    }

    // Restores one input from the saved draft. Returns whether a saved value
    // existed; no-op when nothing (non-empty) is saved — hydration must never
    // wipe a value the customer can see.
    hydrateInputFromSavedState(input) {
        const saved = this.getSavedFieldValue(input.name);
        if (saved === null || saved === undefined || saved === "") return false;
        if (input.type === "radio" || input.type === "checkbox") {
            if (input.value === saved) {
                input.checked = true;
            }
            return true;
        }
        input.value = saved;
        // Dispatch input so dependent UI updates (dedication template,
        // preview staleness). isHydrating keeps the synthetic event from
        // queueing a session PATCH; step 2's dedication opt-in flip is
        // isTrusted-guarded against synthetic events already.
        this.isHydrating = true;
        try {
            input.dispatchEvent(new Event("input", { bubbles: true }));
        } finally {
            this.isHydrating = false;
        }
        return true;
    }

    // Re-fills EVERY step form from the saved draft — including forms hidden
    // behind an inactive step, which showFormForStep deliberately skips.
    // Freshly cloned step-1 inputs get their listeners on activation, exactly
    // as before; this only restores values.
    hydrateAllForms() {
        this.getModalRoot()
            .querySelectorAll(".personalization-step-form input, .personalization-step-form textarea, .personalization-step-form select")
            .forEach((input) => {
                if (input.type === "file" || !input.name) return;
                this.hydrateInputFromSavedState(input);
            });
        // The cover radios are settled last: the restore above ticks whatever answer the
        // draft holds, but only the customer's own pick — or an edited line's cover — may
        // stay ticked (nothing is preselected).
        this.ensureCoverSelection();
    }

    showFormForStep(stepId) {
        const root = this.getModalRoot();
        const forms = root.querySelectorAll(".personalization-step-form");

        if (!forms || forms.length === 0) return;
        forms.forEach((form) => {
            const isActive = form.getAttribute("data-step-id") === String(stepId);
            form.classList.toggle("hidden", !isActive);
            if (isActive) {
                form.removeAttribute("hidden");
                form.querySelectorAll("input, textarea, select").forEach((input) => {
                    if (input.type === "file") return;
                    input.removeEventListener("input", this.boundHandleInput);
                    input.removeEventListener("focus", this.boundHandleFocus);
                    input.removeEventListener("pointerdown", this.boundHandlePointerDown);
                    input.removeEventListener("mousedown", this.boundHandlePointerDown);
                    input.removeEventListener("touchstart", this.boundHandlePointerDown);
                    input.addEventListener("input", this.boundHandleInput);
                    input.addEventListener("focus", this.boundHandleFocus);
                    input.addEventListener("pointerdown", this.boundHandlePointerDown);
                    input.addEventListener("mousedown", this.boundHandlePointerDown);
                    input.addEventListener("touchstart", this.boundHandlePointerDown, { passive: true });
                    const hydrated = this.hydrateInputFromSavedState(input);
                    if (!hydrated && input.tagName === "TEXTAREA" && input.hasAttribute("data-dedication-template") && input.placeholder && !input.value) {
                        input.value = input.placeholder;
                        this.saveState({ ...(this.getSavedState() || {}), [input.name]: input.value });
                    }
                });
            } else {
                form.setAttribute("hidden", "");
            }
        });
    }

    handleFocus(event) {
        const target = event.target;
        if (!target || target.tagName !== "TEXTAREA") return;

        if (this.lastPointerDownTarget === target) {
            this.lastPointerDownTarget = null;
            return;
        }

        const placeCaretAtEnd = () => {
            try {
                const length = target.value ? target.value.length : 0;
                target.selectionStart = length;
                target.selectionEnd = length;
            } catch (e) {
                // noop
            }
        };
        setTimeout(placeCaretAtEnd, 0);
    }

    handlePointerDown(event) {
        this.lastPointerDownTarget = event?.target || null;
    }

    // Persistence helpers
    getStorageKey() {
        return personalizationStorageKey(this.productId);
    }

    getSavedState() {
        try {
            const raw = localStorage.getItem(this.getStorageKey());
            return raw ? JSON.parse(raw) : {};
        } catch (e) {
            return {};
        }
    }

    saveState(next) {
        try {
            const current = this.getSavedState();
            localStorage.setItem(this.getStorageKey(), JSON.stringify({ ...current, ...next }));
        } catch (e) {
            // noop
        }
    }

    handleInput(event) {
        const target = event.target;
        if (!target?.name) return;
        if (target.matches?.("[data-bp-dynamic-input]")) {
            window.PersonalizationFieldSanitizer?.sanitizeFieldValue?.(target);
        }
        this.saveState({ ...(this.getSavedState() || {}), [target.name]: target.value });
        this.invalidatePreviewIfStale();

        // A corrected value clears its server-side rejection message.
        const fieldWrapper = target.closest("[data-field-wrapper][data-field-key]");
        if (fieldWrapper) {
            this.clearFieldError(fieldWrapper.getAttribute("data-field-key"));
        }

        // The Hörbuch shows its cover under the name field: the name goes onto it as it is typed.
        if (fieldWrapper?.getAttribute("data-field-key") === "main_character") {
            this.syncAudiobookCoverNames();
        }

        // Session flow: sync the edited field to the server-side draft
        // (debounced) so the draft state stays the authoritative source.
        // Hydration events are excluded: programmatic restores must not
        // PATCH — the pre-preview full sync reconciles the session instead.
        if (!this.isHydrating && fieldWrapper && target.matches?.("[data-bp-dynamic-input]")) {
            this.queueFieldSync(fieldWrapper.getAttribute("data-field-key"), target.value);
        }

        const form = target.closest(".personalization-step-form");

        if (target.name === "__cover_type") {
            if (this.editMode) {
                // The locked radio fires no input event of its own; anything that gets
                // here anyway must not rotate the edited line's live session — restore
                // the line's cover (which also rewrites the draft's __cover_type).
                this.ensureCoverSelection();
                return;
            }
            // The customer's own pick: marked, so the next open ticks it again
            // (ensureCoverSelection drops an answer without the mark).
            this.saveState({ __cover_picked: "true" });
            this.handleCoverSelectionChange(target.value);
            return;
        }

        if (target.name === "__add-dedication") {
            const stepId = form?.getAttribute("data-step-id");
            const activeId = this.getActiveStepId();
            if (stepId && stepId === activeId && target.value === "false") {
                const validation = this.validateStep(stepId);
                this.updateErrors(stepId, validation.errors, /*silentOnValid*/ true);
            }
            return;
        }

        // Don't nag with live validation while the customer is still typing
        // into a text field.
        if (
            target.matches?.("[data-bp-dynamic-input]") ||
            target.name === "__main-character-name" ||
            target.name === "__dedication"
        ) {
            return;
        }
        // Live-validate current step and update its error container only
        if (form) {
            const stepId = form.getAttribute("data-step-id");
            const activeId = this.getActiveStepId();
            if (stepId && stepId === activeId) {
                const validation = this.validateStep(stepId);
                this.updateErrors(stepId, validation.errors, /*silentOnValid*/ true);
            }
        }

        // The Hörbuch step's answer decides whether the book's preview plays the name sample.
        if (target.name === "__add-audiobook") {
            this.syncNameSample();
        }
    }

    getSavedFieldValue(name) {
        const state = this.getSavedState();
        if (!state) return null;
        const value = state[name];
        if (value !== undefined && value !== null && value !== "") {
            return value;
        }
        // Lines saved by the pre-cutover wizard used theme-specific names.
        if (name === "__main_character") {
            return state[PERSONALIZATION_LEGACY_ENTRY_ALIASES.main_character] ?? value;
        }
        return value;
    }

    // Shopify's cart AJAX API answers with a LOCALIZED `description` ("Du
    // kannst nicht mehr … hinzufügen.") next to an untranslated generic
    // `message` ("Cart Error"). Only the description may be shown; anything
    // else becomes a technical error the catch translates for the customer.
    buildCartError(errorData, status, operation) {
        const description = String(errorData?.description || "").trim();
        if (description !== "") {
            return new PersonalizationLocalizedError(description);
        }
        return new Error(`Shopify cart API failed to ${operation} (HTTP ${status}): ${errorData?.message || "unknown error"}`);
    }

    // Error for a failed bookpipe request. The message keeps the shape the
    // existing Sentry issues group on; the details tell a backend throttle
    // (JSON `code`, Retry-After) apart from an edge or proxy rejection
    // (empty body, `platform` header) once a caller logs them.
    async buildRequestError(prefix, response) {
        const error = new Error(`${prefix} (${response.status})`);
        const details = {
            status: response.status,
            retry_after: response.headers.get("retry-after"),
            platform: response.headers.get("platform"),
            body_length: null,
            code: null,
        };
        try {
            const text = await response.text();
            details.body_length = text.length;
            if (text) {
                details.code = JSON.parse(text)?.code ?? null;
            }
        } catch (e) {
            // Unreadable or non-JSON body: the length alone is telling.
        }
        error.details = details;
        return error;
    }

    async parseJsonResponse(response) {
        const text = await response.text();

        if (!text) {
            return {};
        }

        try {
            return JSON.parse(text);
        } catch (error) {
            // Non-JSON body: typically Shopify's app proxy replacing a
            // backend 5xx/timeout with its themed HTML error page. Never
            // surface raw markup as a customer-facing message — log it for
            // debugging and let the callers fall back to their friendly
            // default texts.
            console.error(
                `PersonalizationModal: non-JSON response (HTTP ${response.status}) from ${response.url}`,
                text.slice(0, 500)
            );
            return {};
        }
    }

    // Fire-and-forget funnel beacon: reports a successful Shopify cart-add
    // to bookpipe, which stamps added_to_cart_at on the session. Deliberately
    // NOT sessionRequest(): no rotation/re-verify retries — a 4xx on a stale
    // session is expected noise here. keepalive lets the request ride out
    // the immediate /cart navigation; no failure may ever surface into the
    // add-to-cart flow.
    reportCarted() {
        try {
            const personalizationId = String(this.currentPersonalization?.personalization_id || "");
            if (!personalizationId) return;
            (window.originalFetch || window.fetch)(
                this.appProxyUrl(`${this.bookpipePersonalizationUrl}/${encodeURIComponent(personalizationId)}/carted`),
                { method: "POST", keepalive: true }
            ).catch(() => {});
        } catch (error) {
            // Telemetry must never break the cart flow.
        }
    }

    async addToCart(cartData) {
        const hasMultiple = Array.isArray(cartData?.items);
        const payload = hasMultiple
            ? { items: cartData.items, sections: ["cart-bubble", "variant-added", "main-cart"] }
            : { ...cartData, sections: ["cart-bubble", "variant-added", "main-cart"] };
        const requestBody = JSON.stringify(payload);

        const response = await (window.originalFetch || window.fetch)(window.routes.cart_add_url, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "X-Requested-With": "XMLHttpRequest",
            },
            body: requestBody,
        });

        if (!response.ok) {
            throw this.buildCartError(await this.parseJsonResponse(response), response.status, "add to cart");
        }

        const result = await response.json();

        if (result.sections && result.sections["cart-bubble"]) {
            this.updateCartBubble(result.sections["cart-bubble"]);
        }

        return result;
    }

    /**
     * Instance method wrapper - delegates to static method
     */
    async getCart() {
        return PersonalizationModal.getCart();
    }

    async changeCartLine(lineKey, quantity, properties) {
        const changeUrl = window.routes?.cart_change_url || `${window.Shopify?.routes?.root || "/"}cart/change.js`;
        const body = JSON.stringify({
            id: lineKey,
            quantity: quantity,
            properties: properties || {},
            sections: ["cart-bubble", "variant-added", "main-cart"],
        });

        const response = await (window.originalFetch || window.fetch)(changeUrl, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "X-Requested-With": "XMLHttpRequest",
            },
            body,
        });

        if (!response.ok) {
            throw this.buildCartError(await this.parseJsonResponse(response), response.status, "update cart");
        }

        const result = await response.json();
        if (result.sections && result.sections["cart-bubble"]) {
            this.updateCartBubble(result.sections["cart-bubble"]);
        }
        return result;
    }

    getActiveStepId() {
        const root = this.getModalRoot();
        const active = root.querySelector(".personalization-step.selected");
        return active ? active.getAttribute("data-step-id") : "1";
    }

    validateStep(stepId) {
        const root = this.getModalRoot();
        const form = root.querySelector(`.personalization-step-form[data-step-id="${stepId}"]`);
        const errors = [];
        if (!form) return { valid: true, errors };
        // The form carries its key; the preview step has nothing to validate and
        // falls through to valid.
        const stepKey = form.getAttribute("data-step-key");

        if (stepKey === PERSONALIZATION_STEP_KEYS.CHARACTER) {
            if (!this.fieldConfig) {
                errors.push(this.getMessage("fields-unavailable", "Die Personalisierungsfelder werden noch geladen. Bitte versuche es gleich erneut."));
                return { valid: false, errors };
            }

            this.getRenderableTextFields().forEach((field) => {
                const input = form.querySelector(`[name="${this.fieldInputName(field.key)}"]`);
                errors.push(...this.textFieldErrors(field, input ? String(input.value || "").trim() : ""));
            });

            this.missingRequiredPhotos().forEach((field) => errors.push(this.photoRequiredMessage(field)));
        }

        if (stepKey === PERSONALIZATION_STEP_KEYS.COVER) {
            // Nothing is preselected (owner 28.09.2026): every fresh draft stays here until
            // the customer picks one of the two — navigateToStep validates each step left
            // behind and handleAddToCart every input step, so no book is bought without one.
            const radios = form.querySelectorAll('input[type="radio"][name="__cover_type"]');
            if (!Array.from(radios).some((radio) => radio.checked)) {
                errors.push(this.getMessage("cover-required", "Bitte wähle ein Cover aus."));
            }
        }

        if (stepKey === PERSONALIZATION_STEP_KEYS.DEDICATION) {
            if (!this.supportsDedication()) {
                return { valid: true, errors };
            }
            const radios = form.querySelectorAll('input[type="radio"][name="__add-dedication"]');
            const selected = Array.from(radios).find((r) => r.checked);
            if (!selected) {
                errors.push(this.getMessage("dedication-required", "Bitte wähle, ob du eine Widmung hinzufügen möchtest."));
            } else if (selected && selected.value === "true") {
                const dedication = form.querySelector('textarea[name="__dedication"]');
                const value = dedication ? String(dedication.value || "").trim() : "";
                if (value && PERSONALIZATION_DEDICATION_SIGNATURE_REGEX.test(value)) {
                    errors.push(this.getMessage("dedication-xxx", 'Bitte passe den Text der persönlichen Widmung deinen Wünschen entsprechend an oder tausche zumindest "XXX" gegen deinen Namen aus.'));
                }
            }
        }

        if (stepKey === PERSONALIZATION_STEP_KEYS.AUDIOBOOK) {
            // Neither card is preselected (the customer decides, owner 25.09.): the step
            // only lets the customer on with an answer, "Nein" as much as "Ja".
            const radios = form.querySelectorAll('input[type="radio"][name="__add-audiobook"]');
            if (!Array.from(radios).some((radio) => radio.checked)) {
                errors.push(this.getMessage("audiobook-required", "Bitte wähle aus, ob du das Hörbuch dazu möchtest."));
            }
        }

        return { valid: errors.length === 0, errors };
    }

    // The step-1 rules of a text field for its value (trimmed): the message of
    // every rule the value breaks - none when it passes.
    textFieldErrors(field, value) {
        const errors = [];
        const isMainCharacter = field.key === "main_character";

        if (!value && field.required) {
            errors.push(
                isMainCharacter
                    ? this.getMessage("name-required", "Bitte gib den Vornamen des Kindes ein.")
                    : this.formatFieldMessage("field-required", field, 'Bitte fülle das Feld „{label}“ aus.')
            );
        }
        if (value && field.max && value.length > field.max) {
            errors.push(this.formatFieldMessage("field-maxlength", field, 'Das Feld „{label}“ darf maximal {max} Zeichen lang sein.', { max: field.max }));
        }
        if (value && field.min && value.length < field.min) {
            errors.push(this.formatFieldMessage("field-minlength", field, 'Das Feld „{label}“ muss mindestens {min} Zeichen enthalten.', { min: field.min }));
        }
        if (isMainCharacter && value && /[\p{L}]/u.test(value) && PERSONALIZATION_ALL_UPPERCASE_REGEX.test(value)) {
            errors.push(this.getMessage("name-uppercase", "Bitte schreibe den Namen der Hauptfigur nicht komplett in Großbuchstaben."));
        }
        return errors;
    }

    updateErrors(stepId, errors, silentOnValid = false) {
        const root = this.getModalRoot();
        const container = root.querySelector(".personalization-steps-errors");
        if (!container) return;
        const errorSignature = JSON.stringify(errors || []);
        if (!errors || errors.length === 0) {
            if (!silentOnValid) container.innerHTML = "";
            container.classList.add("hidden");
            container.setAttribute("hidden", "");
            container.dataset.errorSignature = "";
            return;
        }
        const shouldAnimate =
            !silentOnValid &&
            !container.classList.contains("hidden") &&
            container.dataset.errorSignature === errorSignature;
        // Alert-Rolle liegt auf dem Container; erst einblenden, dann schreiben,
        // damit die eingefügten Fehler von Screenreadern angesagt werden.
        container.setAttribute("role", "alert");
        container.classList.remove("hidden");
        container.removeAttribute("hidden");
        const list = errors.map((e) => `<li>${e}</li>`).join("");
        container.innerHTML = `<ul>${list}</ul>`;
        container.dataset.errorSignature = errorSignature;

        if (!silentOnValid) {
            // The triggering button sits in the fixed footer; make sure the
            // errors are actually seen and focus follows for AT users.
            container.scrollIntoView({ block: "center", behavior: "smooth" });
            container.focus({ preventScroll: true });
        }

        if (shouldAnimate) {
            container.classList.remove("personalization-steps-errors--shake");
            void container.offsetWidth;
            container.classList.add("personalization-steps-errors--shake");
        } else {
            container.classList.remove("personalization-steps-errors--shake");
        }
    }

    clearErrors() {
        const root = this.getModalRoot();
        const container = root.querySelector(".personalization-steps-errors");
        if (container) {
            container.innerHTML = "";
            container.classList.add("hidden");
            container.setAttribute("hidden", "");
        }
    }

    updateCartBubble(cartBubbleHtml) {
        PersonalizationModal.applyCartBubbleHtml(cartBubbleHtml);
    }

    static applyCartBubbleHtml(cartBubbleHtml) {
        const cartBubble = document.getElementById("CartBubble");
        if (!cartBubble) return;

        const parsedHtml = new DOMParser().parseFromString(cartBubbleHtml, "text/html");
        const renderedBubble = parsedHtml.querySelector("#CartBubble");
        const renderedCount = renderedBubble
            ? renderedBubble.textContent.trim()
            : parsedHtml.body.textContent.trim();

        if (renderedCount) {
            cartBubble.classList.remove("hidden");
            cartBubble.textContent = renderedCount;
        } else {
            cartBubble.classList.add("hidden");
            cartBubble.textContent = "";
        }
    }

    showErrorMessage(button, message) {
        let errorEl = button.parentNode.querySelector(".personalization-error");
        if (!errorEl) {
            errorEl = document.createElement("div");
            errorEl.className = "personalization-error text-danger text-sm mt-2";
            button.parentNode.appendChild(errorEl);
        }

        errorEl.textContent = message;
        errorEl.style.display = "block";

        setTimeout(() => {
            errorEl.style.display = "none";
        }, 5000);
    }

    setLoadingState(button, loading) {
        if (loading) {
            button.disabled = true;
            button.setAttribute("aria-loading", "true");
            button.style.position = "relative";

            if (!button.dataset.originalContent) {
                button.dataset.originalContent = button.innerHTML;
            }

            button.innerHTML = `
            <div style="display: flex; align-items: center; justify-content: center;">
              <svg
                aria-hidden="true"
                focusable="false"
                class="spinner"
                viewBox="0 0 66 66"
                xmlns="http://www.w3.org/2000/svg"
                style="width: 16px; height: 16px; animation: spin 1s linear infinite;"
              >
                <circle class="path" fill="none" stroke="currentColor" stroke-width="6" cx="33" cy="33" r="30"></circle>
              </svg>
            </div>
            `;

            if (!document.getElementById("personalization-spinner-styles")) {
                const styles = document.createElement("style");
                styles.id = "personalization-spinner-styles";
                styles.textContent = `
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `;
                document.head.appendChild(styles);
            }
        } else {
            button.disabled = false;
            button.removeAttribute("aria-loading");

            if (button.dataset.originalContent) {
                button.innerHTML = button.dataset.originalContent;
            }
        }
    }

    // ------------------------------------------------------------------
    // Cart-redirect navigation cover
    // ------------------------------------------------------------------

    // The cover itself lives in personalization-nav-cover.liquid (inline in
    // <head> so it can also paint before the wizard auto-opens); the class
    // fallback covers the theoretical case of that snippet missing.
    showNavCover() {
        if (window.__bpNavCover) window.__bpNavCover.show(10000);
        else document.documentElement.classList.add("bp-nav-cover");
    }

    hideNavCover() {
        if (window.__bpNavCover) window.__bpNavCover.hide();
        else document.documentElement.classList.remove("bp-nav-cover");
    }

    // Covers the open wizard and navigates to the cart. The modal is
    // deliberately NOT hidden: hide() would fade the wizard out (revealing
    // the PDP) and its "hide" listener unpins the touch scroll lock with a
    // scroll jump — all while /cart is still loading. Everything the open
    // modal holds dies with the navigation; a bfcache restore of this page
    // is rolled back in handlePageShow.
    beginCartRedirect(button) {
        this.cartRedirectPending = true;
        this.showNavCover();
        window.location.href = window.routes.cart_page_url;
        // A hung navigation (dead connection) never fires any event, and an
        // Escape/Stop during the load aborts it while the page stays alive.
        // If this page still exists after 10s, roll back to a clean PDP —
        // the add DID succeed, so the wizard must not stay interactive and
        // impersonate an unsaved draft (edits made there would be lost).
        window.clearTimeout(this.navFallbackTimer);
        this.navFallbackTimer = window.setTimeout(() => this.rollbackCartRedirect(), 10000);
    }

    // Rolls the page back to a clean state when a cart redirect never left
    // this page (bfcache restore, or a navigation that hung / was stopped by
    // the user). The items ARE in the cart and the draft was already
    // cleared, so the wizard closes and resets to a fresh draft; the cart
    // bubble already shows the added book.
    rollbackCartRedirect() {
        this.cartRedirectPending = false;
        this.isSubmittingCart = false;
        window.clearTimeout(this.navFallbackTimer);
        this.hideNavCover();
        // Button first: resetWizardToFreshDraft ends in the idle preview
        // state, whose updateAddToCartAvailability disables the buy button —
        // restoring the button content afterwards would re-enable it.
        const button = this.getModalRoot().querySelector("[data-add-personalized-to-cart]");
        if (button) this.setLoadingState(button, false);
        const drawer = document.getElementById(`personalize-modal-${this.productId}`);
        if (drawer?.open && typeof drawer.hide === "function") {
            drawer.hide({ anim: false });
        }
        this.resetWizardToFreshDraft();
    }

    // Capture-phase so it beats modal-drawer's own Escape handler: while a
    // cart redirect is in flight the wizard must stay open under the cover.
    handleRedirectKeydown(event) {
        if (this.cartRedirectPending && event.key === "Escape") {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    handlePageShow(event) {
        if (!event.persisted) return;
        // bfcache restored a snapshot: lift a cover it may have been taken
        // under (the inline snippet's own pageshow listener does this too —
        // idempotent). A normal back-navigation with a live draft is left
        // alone beyond that.
        this.hideNavCover();
        window.clearTimeout(this.navFallbackTimer);
        if (!this.cartRedirectPending) return;
        // The snapshot was taken mid-redirect: the wizard is still open with
        // the purchased draft while localStorage was already cleared.
        this.rollbackCartRedirect();
    }

    dispatchCartEvents(cartData) {
        document.dispatchEvent(
            new CustomEvent("product:added-to-cart", {
                detail: {
                    id: cartData.id,
                    quantity: cartData.quantity,
                    properties: cartData.properties,
                },
            })
        );
    }

    /**
     * Static method to set up the cart's remove buttons
     * One capture listener on the document, bound once: it outlives every re-render of the cart
     * section (whoever renders it) and runs before cart-form's own listener on the button, which
     * would remove the book's line alone and leave its fee behind - hidden, still charged.
     */
    static setupGlobalRemoveButtonListeners() {
        if (PersonalizationModal._removeListenersInitialized) {
            return;
        }

        PersonalizationModal._removeListenersInitialized = true;

        document.addEventListener("click", async function (event) {
            const button = event.target instanceof Element ? event.target.closest("[data-button-remove]") : null;
            if (!button) {
                return;
            }

            event.preventDefault();
            event.stopImmediatePropagation();

            // A second click while a removal runs would read the line by an index the cart no
            // longer has.
            if (PersonalizationModal._removeInFlight) {
                return;
            }
            PersonalizationModal._removeInFlight = true;

            try {
                button.style.pointerEvents = 'none';
                button.style.opacity = '0.5';

                const removed = await PersonalizationModal.removeCartLineWithLinked(parseInt(button.dataset.index, 10));
                const sectionElement = button.closest('.shopify-section');
                if (!removed || !(await PersonalizationModal.rerenderCartSection(sectionElement))) {
                    window.location.reload();
                }
            } catch (error) {
                console.error("Cart update error:", error);
                window.location.reload();
            } finally {
                PersonalizationModal._removeInFlight = false;
            }
        }, true);
    }

    /**
     * Removes the cart's line at `lineIndex` (1-based) with every line linked to it, then every
     * line whose parent is gone. Re-reads the cart until nothing of the line is left: the
     * automatic package discount re-keys the remaining lines as books leave, and a line missed by
     * a stale key would stay in the cart unseen. Returns false when the line could not be found.
     */
    static async removeCartLineWithLinked(lineIndex) {
        const cart = await PersonalizationModal.getCart();
        const itemToRemove = cart?.items?.[lineIndex - 1];
        if (!itemToRemove) {
            return false;
        }

        const uuid = itemToRemove.properties?.["__line_uuid"];
        const isLinked = (item) =>
            uuid ? item.properties?.["__line_uuid"] === uuid || item.properties?.["__parent_line_uuid"] === uuid : item.key === itemToRemove.key;

        let current = cart;
        for (let attempt = 0; attempt < 3; attempt++) {
            const updates = Object.fromEntries((current?.items || []).filter(isLinked).map((item) => [item.key, 0]));
            if (Object.keys(updates).length === 0) {
                break;
            }

            const response = await (window.originalFetch || window.fetch)(`${window.Shopify?.routes?.root || "/"}cart/update.js`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ updates }),
            });
            if (!response.ok) {
                return false;
            }
            current = await PersonalizationModal.getCart();
        }

        await PersonalizationModal.adjustCart();
        return true;
    }

    /**
     * Renders the cart section `sectionElement` and the cart bubble afresh from the cart as it is
     * now. Returns false when it could not.
     */
    static async rerenderCartSection(sectionElement) {
        if (!sectionElement) {
            return false;
        }

        const sectionId = sectionElement.id.replace('shopify-section-', '');
        const url = new URL(window.location.pathname, window.location.origin);
        url.searchParams.set("sections", ["cart-bubble", sectionId].join(","));
        const response = await (window.originalFetch || window.fetch)(url.toString());
        if (!response.ok) {
            return false;
        }

        const sections = await response.json();
        if (sections["cart-bubble"]) {
            PersonalizationModal.applyCartBubbleHtml(sections["cart-bubble"]);
        }
        if (!sections[sectionId]) {
            return false;
        }

        const parsedHtml = new DOMParser().parseFromString(sections[sectionId], "text/html");
        const newSection = parsedHtml.querySelector(`#${sectionElement.id}`) ||
                           parsedHtml.querySelector('.shopify-section') ||
                           parsedHtml.body;
        sectionElement.innerHTML = newSection.innerHTML;
        return true;
    }

    /**
     * On the cart page: removes the lines whose parent is gone and renders the cart again if any
     * were. The cart never shows such a line, so a cart left with one showed nothing but its price.
     */
    static async removeOrphansOnCartPage() {
        // Every page carries the cart modal's cart-form too; the page's own is the one outside it.
        const sectionElement = [...document.querySelectorAll("cart-form")]
            .find((form) => !form.closest("cart-modal"))
            ?.closest(".shopify-section");
        if (!sectionElement) {
            return;
        }

        if (await PersonalizationModal.adjustCart()) {
            if (!(await PersonalizationModal.rerenderCartSection(sectionElement))) {
                window.location.reload();
            }
        }
    }

    /**
     * The name of the line property that names the child a book is for: the storefront's word.
     */
    static recipientProperty() {
        return window._t?.sections?.personalization?.line_item_for || PERSONALIZATION_LEGACY_RECIPIENT_PROPERTY;
    }

    /**
     * The child a cart line names - under the storefront's word, or under the German one on a line
     * from before.
     */
    static recipientOf(properties) {
        return properties[PersonalizationModal.recipientProperty()] || properties[PERSONALIZATION_LEGACY_RECIPIENT_PROPERTY] || "";
    }

    /**
     * Names the child `name` on a line's `properties` under the storefront's word. why: a line from
     * before carries the name under the German word, and the checkout lists every property a line
     * carries - so that one goes.
     */
    static nameRecipient(properties, name) {
        delete properties[PERSONALIZATION_LEGACY_RECIPIENT_PROPERTY];
        properties[PersonalizationModal.recipientProperty()] = name;
    }

    /**
     * Static helper to get the current cart
     */
    static async getCart() {
        const response = await (window.originalFetch || window.fetch)(`${window.Shopify?.routes?.root || "/"}cart.js`, {
            method: "GET",
            headers: { Accept: "application/json" },
        });
        if (!response.ok) return null;
        try {
            return await response.json();
        } catch (error) {
            // console.warn, nicht console.error: error würde via Sentry-Wrapper ein neues Event erzeugen
            console.warn("PersonalizationModal: cart.js returned a non-JSON response", error);
            return null;
        }
    }

    /**
     * Static method to adjust cart - removes orphaned items
     * Returns true when it removed any.
     */
    static async adjustCart() {
        const cart = await PersonalizationModal.getCart();
        if (!cart) return;

        const parentLines = cart.items.filter((item) => !!item.properties["__line_uuid"]);

        const itemsToRemove = [];

        parentLines.forEach((parentItem) => {
            const parentLineUuid = parentItem.properties["__line_uuid"];
            const addDiary = parentItem.properties["__add-diary"];
            const addDedication = parentItem.properties["__add-dedication"];
            const diaryVariantId = parentItem.properties["__diary_variant_id"];
            const dedicationVariantId = parentItem.properties["__dedication_variant_id"];

            if (addDiary == "false" && diaryVariantId) {
                const diaryItem = cart.items.find(
                    (item) => item.properties["__parent_line_uuid"] == parentLineUuid && item.variant_id == diaryVariantId
                );

                if (diaryItem) {
                    itemsToRemove.push({
                        key: diaryItem.key,
                        quantity: 0,
                        title: diaryItem.product_title,
                        reason: "diary not needed",
                    });
                }
            }

            if (addDedication == "false" && dedicationVariantId) {
                const dedicationItem = cart.items.find(
                    (item) => item.properties["__parent_line_uuid"] === parentLineUuid && item.variant_id == dedicationVariantId
                );

                if (dedicationItem) {
                    itemsToRemove.push({
                        key: dedicationItem.key,
                        quantity: 0,
                        title: dedicationItem.product_title,
                        reason: "dedication not needed",
                    });
                }
            }

            cart.items.forEach((linkedItem) => {
                if (linkedItem.properties["__parent_line_uuid"] !== parentLineUuid) {
                    return;
                }

                const linkedVariantId = String(linkedItem.variant_id || "");
                const isDedicationItem = dedicationVariantId && linkedVariantId === String(dedicationVariantId);
                const isLegacyDiaryItem = diaryVariantId && linkedVariantId === String(diaryVariantId);

                if (isDedicationItem || (isLegacyDiaryItem && addDiary == "true")) {
                    return;
                }

                if (itemsToRemove.some((item) => item.key === linkedItem.key)) {
                    return;
                }

                itemsToRemove.push({
                    key: linkedItem.key,
                    quantity: 0,
                    title: linkedItem.product_title,
                    reason: "legacy linked add-on not needed",
                });
            });
        });

        // Check for orphan items which have __parent_line_uuid that doesn't match any current parent items
        const validParentUuids = new Set(parentLines.map((item) => item.properties["__line_uuid"]));
        cart.items.forEach((item) => {
            const parentLineUuid = item.properties["__parent_line_uuid"];
            if (item && parentLineUuid && !validParentUuids.has(parentLineUuid)) {
                itemsToRemove.push({
                    key: item.key,
                    quantity: 0,
                    title: item.product_title,
                    reason: `parent UUID ${parentLineUuid} not found in cart`,
                });
            }
        });

        if (itemsToRemove.length > 0) {
            const updates = (itemsToRemove || []).reduce((acc, item) => {
                const identifier = item?.key;
                if (identifier != null) {
                    acc[identifier] = 0;
                }
                return acc;
            }, {});
            const removeResponse = await (window.originalFetch || window.fetch)(`${window.Shopify?.routes?.root || "/"}cart/update.js`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ updates }),
            });

            if (!removeResponse.ok) {
                console.error(`Failed to remove orphaned items:`, await removeResponse.text());
                return false;
            }
            return true;
        }
        return false;
    }

    /**
     * Static method to check for missing items and add them
     */
    static async checkForMissing() {
        const cart = await PersonalizationModal.getCart();
        if (!cart) return;

        const parentLines = cart.items.filter((item) => !!item.properties["__line_uuid"]);

        const itemsToAdd = [];

        parentLines.forEach((parent) => {
            const parentLineUuid = parent.properties["__line_uuid"];
            const recipient = PersonalizationModal.recipientOf(parent.properties);
            const addDiary = parent.properties["__add-diary"];
            const addDedication = parent.properties["__add-dedication"];
            const diaryVariantId = parent.properties["__diary_variant_id"];
            const dedicationVariantId = parent.properties["__dedication_variant_id"];

            if (addDiary == "true" && diaryVariantId) {
                const diaryExists = cart.items.some(
                    (item) => item.properties["__parent_line_uuid"] == parentLineUuid && item.variant_id == diaryVariantId
                );

                if (!diaryExists) {
                    const diaryProperties = {
                        __parent_line_uuid: parentLineUuid,
                        __parent_variant_id: String(parent.variant_id),
                        __variant_id: String(diaryVariantId),
                    };
                    if (recipient) {
                        PersonalizationModal.nameRecipient(diaryProperties, recipient);
                    }
                    itemsToAdd.push({
                        id: diaryVariantId,
                        quantity: 1,
                        properties: diaryProperties,
                    });
                }
            }

            const dedicationText = String(parent.properties["__dedication"] || "").trim();

            if (addDedication == "true" && dedicationVariantId && dedicationText !== "") {
                const dedicationExists = cart.items.some(
                    (item) => item.properties["__parent_line_uuid"] === parentLineUuid && item.variant_id == dedicationVariantId
                );

                if (!dedicationExists) {
                    const dedicationProperties = {
                        __parent_line_uuid: parentLineUuid,
                        __parent_variant_id: String(parent.variant_id),
                        __variant_id: String(dedicationVariantId),
                    };
                    if (recipient) {
                        PersonalizationModal.nameRecipient(dedicationProperties, recipient);
                    }
                    itemsToAdd.push({
                        id: dedicationVariantId,
                        quantity: 1,
                        properties: dedicationProperties,
                    });
                }
            }
        });

        if (itemsToAdd.length > 0) {
            const addResponse = await (window.originalFetch || window.fetch)(`${window.Shopify?.routes?.root || "/"}cart/add.js`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ items: itemsToAdd }),
            });

            if (!addResponse.ok) {
                console.error("Failed to add missing items");
            }
        }
    }
}


// Auto-open and prefill from query params. The pre-paint nav cover
// (personalization-nav-cover.liquid, inline in <head>) hides the PDP from
// first paint while this handler prepares and opens the wizard — every exit
// path below must lift it, hence the try/finally.
document.addEventListener("DOMContentLoaded", async function () {
    const params = new URLSearchParams(window.location.search || "");
    if (!params.has("openPersonalize")) return;
    try {
        const modalEl = document.querySelector("personalization-modal");
        if (!modalEl) return;
        const productId = modalEl.getAttribute("data-product-id");
        if (!productId) return;

        const lineUuID = params.get("line_uuid");

        if (!lineUuID) {
            // Malformed link: fall back to the plain PDP instead of dying
            // with an uncaught rejection under a stuck cover.
            console.warn("PersonalizationModal: openPersonalize without line_uuid");
            return;
        }

        const cart = await fetch(`${window.Shopify?.routes?.root || "/"}cart.js`, {
            method: "GET",
            headers: { Accept: "application/json" },
        });
        if (!cart.ok) return;
        const cartData = await cart.json();

        const lineItem = cartData.items.find((item) => item.properties?.__line_uuid === lineUuID);
        if (!lineItem) return;

        const payload = {
            ...lineItem.properties,
        };

        const storageKey = personalizationStorageKey(productId);
        try {
            window.localStorage.setItem(storageKey, JSON.stringify(payload));
        } catch (error) {
            // why abort, not continue: the edit flow reads the line's uuid
            // back from this draft — without it a "save" would ADD a second
            // line instead of editing this one. A full or blocked storage
            // leaves the customer on the plain PDP (the finally lifts the
            // cover); the cart line stays as it is.
            console.warn("PersonalizationModal: could not store the edited line's draft", error);
            return;
        }

        // The edited line's uuid is now in localStorage, so the SKU (and with it
        // the field config) can resolve against the edited line's variant.
        if (typeof modalEl.initFieldConfig === "function") {
            modalEl.initFieldConfig({ hydratePhotos: false });
        }
        // Restore the photos (if still in this browser's IndexedDB) for the
        // edited line explicitly — this hydration is authoritative in edit mode.
        if (typeof modalEl.hydratePhotoFromStore === "function") {
            modalEl.hydratePhotoFromStore({ force: true, lineUuid: lineUuID });
        }
        // Opened without its trigger, so without handleTriggerOpen: the pictures load here.
        if (typeof modalEl.warmWizardImages === "function") {
            modalEl.warmWizardImages();
        }

        const drawer = document.getElementById(`personalize-modal-${productId}`);
        if (drawer && typeof drawer.show === "function") {
            // The cover hides the whole viewport anyway; the 400ms panel
            // animation would only delay the reveal. Open instantly so the
            // wizard is fully there when the cover lifts.
            await drawer.show({ anim: false });
        } else {
            const trigger = document.querySelector(`modal-trigger[target="#personalize-modal-${productId}"]`);
            if (trigger) trigger.click();
        }
        const modal = document.getElementById(`personalize-modal-${productId}`);
        if (modal) {
            Object.keys(payload).forEach((key) => {
                const textarea = modal.querySelector(`textarea[name="${key}"]`);
                if (textarea) textarea.value = payload[key];

                const select = modal.querySelector(`select[name="${key}"]`);
                if (select) select.value = payload[key];

                const input = modal.querySelector(`input[type="text"][name="${key}"]`);
                if (input) {
                    input.value = payload[key];
                    input.dispatchEvent(new Event("input", { bubbles: true }));
                }

                if (key === "__add-dedication" || key === "__add-audiobook") {
                    const radio = modal.querySelector(`input[type="radio"][name="${key}"][value="${payload[key]}"]`);
                    if (radio) radio.click();
                }
            });
        }

        // Let the opened wizard paint under the cover before lifting it.
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    } finally {
        window.__bpNavCover?.hide();
    }
});

/**
 * Override global fetch to validate cart changes
 * Intercepts /cart/change requests and runs validation steps
 */
document.addEventListener("DOMContentLoaded", function () {
    // Store the original fetch function
    const originalFetch = window.fetch;
    window.originalFetch = originalFetch;

    // Override the global fetch method
    window.fetch = async function (...args) {
        const url = typeof args[0] === "string" ? args[0] : args[0]?.url || "";
        const isCartChangeRequest = url.includes("/cart/change");

        if (isCartChangeRequest) {
            try {
                const response = await originalFetch.apply(this, args);

                if (response.ok) {
                    Promise.resolve()
                        .then(async () => {
                            try {
                                await PersonalizationModal.checkForMissing();
                                await PersonalizationModal.adjustCart();
                            } catch (error) {
                                console.error("Error during cart validation:", error);
                            }
                        })
                        .catch((error) => {
                            console.error("Error in validation promise chain:", error);
                        });

                    return response;
                }

                return response;
            } catch (error) {
                console.error("Error in fetch override for /cart/change:", error);
                throw error;
            }
        }

        return originalFetch.apply(this, args);
    };
});


// Register the custom element after the fetch override is set up
if (!customElements.get("personalization-modal")) {
    customElements.define("personalization-modal", PersonalizationModal);
}

/**
 * Initialize global cart remove button listeners
 * This runs independently of the personalization modal component
 */
document.addEventListener("DOMContentLoaded", function () {
    PersonalizationModal.setupGlobalRemoveButtonListeners();
    PersonalizationModal.removeOrphansOnCartPage().catch((error) => {
        console.error("Failed to remove orphaned cart lines:", error);
    });
});
